import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintIcon from '@mui/icons-material/Print';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WifiIcon from '@mui/icons-material/Wifi';
import './Configuracoes.css';
import { useAuth } from '../auth/AuthContext';
import PageMenuButton from '../components/PageMenuButton';
import FiscalSettingsPanel from '../components/FiscalSettingsPanel';
import BeeDeliverySettingsPanel from '../components/BeeDeliverySettingsPanel';
import NinetyNineFoodSettingsPanel from '../components/NinetyNineFoodSettingsPanel';
import StoreIdentityPanel from '../components/StoreIdentityPanel';
import CompanyPixPanel from '../components/CompanyPixPanel';
import OrderAmendmentAuditPanel from '../components/OrderAmendmentAuditPanel';
import {
  GENERAL_SETTINGS_KEY,
  PRINTER_SETTINGS_KEY,
  applyCibdStationBindings,
  getTenantStorageKey,
  isCibdStationConnectionCurrent,
  readTenantLocalJson,
  resolveCibdStationConnection,
  updateCibdStationDiscovery,
  writeCibdStationProfile,
  writeTenantLocalJson,
  writeTenantPrinterSettings,
} from '../utils/tenantSettingsStorage';
import DeliveryPricingPanel from '../components/DeliveryPricingPanel';
import { getApiUrl, getDefaultBridgeUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import { canAccessBackoffice, canManageOtherUsers, canAccessIntegrationConfig, isAdminLike, isProgrammer, USER_ROLES } from '../utils/auth';
import {
  DELETE_ALL_PRODUCTS_CONFIRMATION,
  isDeleteAllProductsConfirmationValid,
  normalizeDeleteAllProductsPreview,
} from '../utils/catalogDeleteAll';
import { invalidateOperationalCatalogLoader } from '../utils/operationalCatalogLoader';
import { invalidatePedidosSnapshots } from '../utils/pedidosDataLoader';
import {
  createPrintPointId,
  MAX_PRINT_POINTS,
  mergeServerPrintPoints,
  normalizePrintPoints,
  toPrintPointRegistryPayload,
} from '../utils/printPoints';

const API_URL = getApiUrl();
const generalStorageKey = GENERAL_SETTINGS_KEY;
const printerStorageKey = PRINTER_SETTINGS_KEY;
const CIBD_URL = getDefaultBridgeUrl();
const SYSTEM_PRINT_BASE_URL = CIBD_URL;

const emptyDeleteAllProductsDialog = () => ({
  open: false,
  applying: false,
  preview: null,
  confirmation: '',
  error: '',
});

const isFiscalSummaryActive = (summary) => Boolean(
  summary?.featureFlags?.fiscalEngineEnabled ?? summary?.featureFlags?.engineEnabled
);

const normalizeCibdPrinterList = (list) => {
  const printers = Array.isArray(list) ? list : [];
  return printers
    .map((printer) => {
      if (!printer) return null;
      if (typeof printer === 'string') {
        return { id: printer, name: printer, status: 'unknown', source: 'cibd' };
      }
      const id = printer.id || printer.name || printer.printerId;
      return { ...printer, id, name: printer.name || id, status: printer.status || 'unknown', source: printer.source || 'cibd' };
    })
    .filter((printer) => printer && printer.id)
    .sort((a, b) => (a.id || '').localeCompare(b.id || ''));
};

const normalizeBearerToken = (token = '') => {
  const trimmed = String(token || '').trim();
  if (!trimmed) return '';
  if (/^bearer\s+/i.test(trimmed)) return trimmed;
  return `Bearer ${trimmed}`;
};

const buildCibdHeaders = (settings = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  if (settings.cibdToken) {
    headers.Authorization = normalizeBearerToken(settings.cibdToken);
  }
  return headers;
};

const defaultGeneralSettings = {
  notificacoes: true,
  somAlerta: true,
  somNovoPedido: true,
  aceiteAutomatico: false,
  modoSilencioso: false,
  prepTime: 18,
  atrasoMinutos: 8,
  exibirKds: true,
  vibrarNovosPedidos: true,
};

const defaultPrinterSettings = {
  cibdToken: '',
  printerId: '',
  printPoints: [{ id: 'caixa', name: 'Caixa', printerId: '', enabled: true, isDefault: true }],
  comandaTitle: 'COMANDA',
  comandaSubtitle: 'Pedido',
  comandaShowOrderNumber: true,
  comandaOrderNumberBanner: false,
  comandaShowCustomer: true,
  comandaShowCustomerDocument: false,
  comandaShowCustomerPhone: true,
  comandaShowContact: true,
  comandaShowAddress: true,
  comandaShowItems: true,
  comandaShowNotes: true,
  comandaShowTotals: true,
  comandaShowDiscounts: false,
  comandaShowAdditionalFees: false,
  comandaShowPayment: true,
  comandaShowPaymentDetails: false,
  comandaShowDeliveryTime: true,
  comandaShowSalesChannel: true,
  comandaShowCreatedAt: true,
  comandaShowMerchant: false,
  comandaShowDeliveryMode: true,
  comandaShowPickupCode: false,
  comandaBoldTitle: true,
  comandaBoldOrderNumber: true,
  comandaBoldCustomer: false,
  comandaBoldAddress: false,
  comandaBoldPhone: false,
  comandaBoldOptions: false,
  comandaBoldTotal: true,
  comandaStrongPrint: false,
  comandaFontFamily: 'courier',
  comandaSizeTitle: 18,
  comandaSizeOrderNumberBanner: 16,
  comandaSizeOrderNumber: 14,
  comandaSizeItem: 12,
  comandaSizeTotal: 14,
};

const defaultCompanyContractSettings = {
  legalName: '',
  tradeName: '',
  cnpj: '',
  email: '',
  phone: '',
  ownerName: '',
  ownerPhone: '',
  legalRepresentativeName: '',
  legalRepresentativeCpf: '',
  address: {
    cep: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
  },
};

const defaultContractTemplateSettings = {
  id: null,
  name: 'Modelo base CLT',
  body: '',
  defaultScale: '5x2',
  version: 1,
};

const merchantWeekDays = [
  { value: 'MONDAY', label: 'Segunda' },
  { value: 'TUESDAY', label: 'Terça' },
  { value: 'WEDNESDAY', label: 'Quarta' },
  { value: 'THURSDAY', label: 'Quinta' },
  { value: 'FRIDAY', label: 'Sexta' },
  { value: 'SATURDAY', label: 'Sábado' },
  { value: 'SUNDAY', label: 'Domingo' },
];

const interruptionDurationOptions = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hora' },
  { value: 180, label: '3 horas' },
  { value: 360, label: '6 horas' },
  { value: 720, label: '12 horas' },
  { value: 1440, label: '24 horas' },
];

const interruptionReasonOptions = [
  'Falta de entregador',
  'Problema na cozinha',
  'Muitos pedidos',
  'Outro',
];

const getDayOrder = (dayOfWeek) => merchantWeekDays.findIndex((item) => item.value === dayOfWeek);
const padTime = (value) => String(value || '').padStart(2, '0');
const timeToMinutes = (value) => {
  const match = String(value || '').match(/^(\d{2}):(\d{2})/);
  if (!match) return NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
  return (hours * 60) + minutes;
};
const minutesToTime = (value) => {
  const safe = Math.max(0, Math.min(1439, Number(value) || 0));
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${padTime(hours)}:${padTime(minutes)}`;
};

const createEmptyShift = () => ({
  id: `draft-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
  dayOfWeek: 'MONDAY',
  start: '09:00',
  end: '18:00',
});

const normalizeMerchantName = (merchant = {}) => (
  merchant.name ||
  merchant.tradingName ||
  merchant.displayName ||
  merchant.corporateName ||
  merchant.shortName ||
  merchant.id ||
  'Loja iFood'
);

const normalizeMerchantList = (value) => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((merchant) => {
      if (!merchant || typeof merchant !== 'object') return null;
      const id = String(merchant.id || merchant.merchantId || '').trim();
      if (!id) return null;
      return {
        ...merchant,
        id,
        displayName: normalizeMerchantName(merchant),
        authorizationStatus: merchant.authorizationStatus || 'unknown',
        authorizationCheckedAt: merchant.authorizationCheckedAt || null,
        authorizationError: merchant.authorizationError || '',
        authorizationListComplete: Boolean(merchant.authorizationListComplete),
        accessError: merchant.accessError || '',
        accessStatus: merchant.accessStatus || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));
};

const IFOOD_MERCHANT_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const normalizeIfoodMerchantId = (value = '') => String(value || '').trim().toLowerCase();

const extractMerchantListPayload = (payload) => (
  Array.isArray(payload) ? payload : payload?.merchants
);

const extractMerchantStatusPayload = (payload) => (
  Array.isArray(payload) || (payload && typeof payload === 'object' && Array.isArray(payload.operations))
    ? payload
    : payload?.merchantStatus || null
);

const extractMerchantDetailsPayload = (payload) => (
  payload && typeof payload === 'object' && !Array.isArray(payload)
    ? (payload.merchant || payload)
    : null
);

const extractOpeningHoursPayload = (payload) => (
  Array.isArray(payload) ? payload : payload?.openingHours
);

const extractInterruptionsPayload = (payload) => (
  Array.isArray(payload) ? payload : payload?.interruptions
);

const BRASILIA_TIME_ZONE = 'America/Sao_Paulo';

const normalizeInterruptions = (value) => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const id = String(item.id || '').trim();
      const description = String(item.description || '').trim();
      const start = String(item.start || '').trim();
      const end = String(item.end || '').trim();
      if (!id || !start || !end) return null;
      return { ...item, id, description, start, end };
    })
    .filter(Boolean)
    .sort((left, right) => String(left.start || '').localeCompare(String(right.start || '')));
};

const mergeInterruption = (interruptions = [], interruption = {}) => {
  const [normalizedInterruption] = normalizeInterruptions([interruption]);
  if (!normalizedInterruption) return interruptions;

  return normalizeInterruptions([
    ...interruptions.filter((item) => item.id !== normalizedInterruption.id),
    normalizedInterruption,
  ]);
};

const parseInterruptionDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const normalized = /([zZ]|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isInterruptionActive = (interruption = {}, referenceDate = new Date()) => {
  const start = parseInterruptionDate(interruption?.start);
  const end = parseInterruptionDate(interruption?.end);
  const now = referenceDate.getTime();
  const startTime = start && !Number.isNaN(start.getTime()) ? start.getTime() : NaN;
  const endTime = end && !Number.isNaN(end.getTime()) ? end.getTime() : NaN;
  return Number.isFinite(startTime) && Number.isFinite(endTime) && startTime <= now && endTime >= now;
};

const toDatetimeLocalInput = (value) => {
  if (!value) return '';
  const date = parseInterruptionDate(value) || new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
};

const formatInterruptionDisplayDate = (value) => {
  const raw = String(value || '').trim();
  const wallClockParts = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (wallClockParts) {
    const [, year, month, day, hours, minutes] = wallClockParts;
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  const date = parseInterruptionDate(value) || new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRASILIA_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date).reduce((accumulator, part) => {
    accumulator[part.type] = part.value;
    return accumulator;
  }, {});

  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
};

const toIsoStringFromLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0');
  const offsetRemainderMinutes = String(absoluteOffsetMinutes % 60).padStart(2, '0');
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetRemainderMinutes}`;
};

const buildInterruptionWindow = (durationMinutes) => {
  const start = new Date();
  start.setSeconds(0, 0);
  const end = new Date(start.getTime() + (Number(durationMinutes || 0) * 60 * 1000));
  return {
    start: toDatetimeLocalInput(start.toISOString()),
    end: toDatetimeLocalInput(end.toISOString()),
  };
};

const normalizeOpeningHours = (payload = []) => {
  const source = Array.isArray(payload) ? payload : [payload];
  const shifts = source.flatMap((entry) => {
    if (Array.isArray(entry?.shifts)) return entry.shifts;
    if (entry?.dayOfWeek && entry?.start) return [entry];
    return [];
  });

  return shifts
    .map((shift, index) => ({
      id: String(shift.id || `shift-${index}`),
      dayOfWeek: String(shift.dayOfWeek || 'MONDAY').toUpperCase(),
      start: String(shift.start || '09:00:00').slice(0, 5),
      end: minutesToTime(timeToMinutes(String(shift.start || '09:00:00').slice(0, 5)) + Number(shift.duration || 0)),
    }))
    .filter((shift) => timeToMinutes(shift.end) > timeToMinutes(shift.start))
    .sort((a, b) => {
      const dayOrder = getDayOrder(a.dayOfWeek) - getDayOrder(b.dayOfWeek);
      if (dayOrder !== 0) return dayOrder;
      return a.start.localeCompare(b.start);
    });
};

const groupOpeningHoursByDay = (openingHours = []) => merchantWeekDays.map((day) => ({
  ...day,
  shifts: openingHours.filter((shift) => shift.dayOfWeek === day.value),
}));

const sortOpeningHours = (openingHours = []) => [...openingHours].sort((a, b) => {
  const dayOrder = getDayOrder(a.dayOfWeek) - getDayOrder(b.dayOfWeek);
  if (dayOrder !== 0) return dayOrder;
  return String(a.start || '').localeCompare(String(b.start || ''));
});

const extractMerchantStreetName = (address = {}) => {
  const explicitStreetName = String(address.streetName || '').trim();
  if (explicitStreetName) return explicitStreetName;

  const rawStreet = String(address.street || '').trim();
  if (!rawStreet) return '';

  const parts = rawStreet.split(',').map((part) => String(part || '').trim()).filter(Boolean);
  return parts[0] || rawStreet;
};

const formatMerchantStatusMessage = (message) => {
  if (!message) return '';
  if (typeof message === 'string') return message;
  if (typeof message !== 'object') return '';

  return [
    message.title,
    message.subtitle,
    message.description,
  ].map((item) => String(item || '').trim()).filter(Boolean).join(' • ');
};

const USER_TAB_OPTIONS = [
  { value: USER_ROLES.ADMINISTRADOR, label: 'Administrador' },
  { value: USER_ROLES.GERENTE, label: 'Gerente' },
  { value: USER_ROLES.COLABORADOR, label: 'Colaborador' },
  { value: USER_ROLES.GARCOM, label: 'Garçom' },
  { value: USER_ROLES.CAIXA, label: 'Caixa' },
];

const PLAN_LABELS = {
  FREE: 'Free',
  MASTER: 'Master',
  ENTERPRISE: 'Enterprise',
};

const SUPPORT_TIER_LABELS = {
  SELF_HELP: 'Tutoriais de autoajuda',
  EMAIL: 'E-mail',
  WHATSAPP_24H: 'WhatsApp 24h',
};

const PLAN_UPGRADE_OPTIONS = [
  {
    code: 'MASTER',
    price: 'R$97/mês',
    title: 'Master',
    highlights: ['Suporte por e-mail', '1 análise gratuita da loja no iFood', 'Fiscal liberável conforme configuração técnica'],
  },
  {
    code: 'ENTERPRISE',
    price: 'R$297/mês',
    title: 'Enterprise',
    highlights: ['WhatsApp 24h', 'Integração total', 'Onboarding com reunião e instalação gratuita'],
  },
];

const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
}).format(Number(value || 0));

const getUserRoleLabel = (role) => {
  if (role === USER_ROLES.ADMINISTRADOR) return 'Administrador';
  if (role === USER_ROLES.PROGRAMADOR) return 'Programador';
  if (role === USER_ROLES.GERENTE) return 'Gerente';
  if (role === USER_ROLES.GARCOM) return 'Garçom';
  if (role === USER_ROLES.CAIXA) return 'Caixa';
  return 'Colaborador';
};

const TenantConfiguracoes = ({ auth }) => {
  const [tenantSession] = useState(() => ({ token: auth.token, user: auth.user }));
  const settingsMounted = useRef(true);
  const isSettingsSessionCurrent = () => settingsMounted.current && Boolean(getTenantStorageKey(generalStorageKey, tenantSession));
  useEffect(() => {
    settingsMounted.current = true;
    return () => { settingsMounted.current = false; };
  }, []);
  const [hasTenantPrinterSettings] = useState(() => readTenantLocalJson(printerStorageKey, null, tenantSession) !== null);
  const currentUser = auth.user;
  const canSeeOperationalSettings = isAdminLike(currentUser);
  const canSeeIntegrationConfig = canAccessIntegrationConfig(currentUser);
  const canManageBeeDelivery = isAdminLike(currentUser);
  const canSeeFiscalIntegration = isProgrammer(currentUser);
  const canManageIfoodShippingConfig = canSeeIntegrationConfig;
  const showProgrammerCatalogTools = canSeeIntegrationConfig;
  const canSeeAdvancedConfig = canSeeIntegrationConfig;
  const canAdministerUsers = canManageOtherUsers(currentUser);
  const canUseContractSettings = canAccessBackoffice(currentUser);
  const canManageContractSettings = isAdminLike(currentUser);
  const [tab, setTab] = useState(() => (
    canSeeOperationalSettings && window.location.hash === '#entrega-preparo' ? 'entrega-preparo' : 'usuarios'
  ));
  const [integrationTab, setIntegrationTab] = useState(() => canSeeIntegrationConfig ? 'ifood' : 'bee');
  const [fiscalFeature, setFiscalFeature] = useState({ loading: false, available: false, summary: null });
  const [integrationManagement, setIntegrationManagement] = useState({});
  const [generalSettings, setGeneralSettings] = useState(() => {
    try {
      const saved = readTenantLocalJson(generalStorageKey, {}, tenantSession);
      return Object.fromEntries(Object.entries(defaultGeneralSettings).map(([key, value]) => [key, saved[key] ?? value]));
    } catch {
      return defaultGeneralSettings;
    }
  });
  const [printerSettings, setPrinterSettings] = useState(() => {
    try {
      const saved = readTenantLocalJson(printerStorageKey, {}, tenantSession);
      const station = resolveCibdStationConnection({ baseUrl: SYSTEM_PRINT_BASE_URL, session: tenantSession, tenantSettings: saved });
      let merged = { ...defaultPrinterSettings, ...saved };
      merged.cibdToken = station ? station.token : saved.cibdToken || saved.bridgeToken || '';
      if (!merged.printerId && saved.printerName) {
        merged.printerId = saved.printerName;
      }
      merged.printPoints = normalizePrintPoints(merged);
      merged.printerId = merged.printPoints[0]?.printerId || merged.printerId;
      merged = applyCibdStationBindings(merged, station);
      return merged;
    } catch {
      return defaultPrinterSettings;
    }
  });
  const printerSettingsRef = useRef(printerSettings);
  useEffect(() => {
    printerSettingsRef.current = printerSettings;
  }, [printerSettings]);
  const [ifoodSettings, setIfoodSettings] = useState({
    integrationEnabled: true,
    clientId: '',
    clientSecret: '',
    widgetId: '',
    merchantIds: [],
    scheduleMerchantId: '',
    detectedMerchantIds: [],
    pollingEnabled: true,
    webhookEnabled: false,
    autoAcceptEnabled: true,
    shippingEnabled: true,
    shippingMerchantIds: [],
    lastTransportFailure: null,
    credentialsConfigured: false,
    credentials: {
      hasClientId: false,
      clientIdPreview: '',
      hasClientSecret: false,
      source: 'environment',
      locked: true,
      testTenant: false,
    },
  });
  const [ifoodStatus, setIfoodStatus] = useState({ loading: true, error: '', updatedAt: null });
  const [ifoodSaving, setIfoodSaving] = useState(false);
  const [ifoodMerchantIdDraft, setIfoodMerchantIdDraft] = useState('');
  const [merchantPanel, setMerchantPanel] = useState({
    loading: true,
    refreshing: false,
    error: '',
    merchants: [],
    selectedMerchantId: '',
    merchantDetails: null,
    merchantStatus: null,
    openingHours: [],
    interruptions: [],
  });
  const merchantIdentityById = useMemo(() => {
    const merchantsById = new Map();
    const addMerchant = (merchant) => {
      if (!merchant || typeof merchant !== 'object') return;
      const id = normalizeIfoodMerchantId(merchant.id || merchant.merchantId);
      if (!id) return;
      const displayName = normalizeMerchantName(merchant);
      merchantsById.set(id, {
        id,
        displayName: displayName && displayName !== id && displayName !== 'Loja iFood'
          ? displayName
          : '',
      });
    };

    (merchantPanel.merchants || []).forEach(addMerchant);
    addMerchant(merchantPanel.merchantDetails);
    return merchantsById;
  }, [merchantPanel.merchantDetails, merchantPanel.merchants]);
  const getIfoodMerchantIdentity = (merchantId) => {
    const id = normalizeIfoodMerchantId(merchantId);
    return merchantIdentityById.get(id) || { id, displayName: '' };
  };
  const shippingMerchantOptions = useMemo(() => {
    const merchantsById = new Map();
    (merchantPanel.merchants || []).forEach((merchant) => {
      const id = String(merchant?.id || merchant?.merchantId || '').trim();
      if (!id) return;
      merchantsById.set(id, {
        id,
        name: merchant.name || merchant.corporateName || merchant.tradingName || 'Loja iFood',
      });
    });

    [
      ...(ifoodSettings.merchantIds || []),
      ...(ifoodSettings.detectedMerchantIds || []),
      ...(ifoodSettings.shippingMerchantIds || []),
    ].forEach((id) => {
      const normalizedId = String(id || '').trim();
      if (normalizedId && !merchantsById.has(normalizedId)) {
        merchantsById.set(normalizedId, { id: normalizedId, name: 'Loja iFood' });
      }
    });

    return Array.from(merchantsById.values());
  }, [
    ifoodSettings.detectedMerchantIds,
    ifoodSettings.merchantIds,
    ifoodSettings.shippingMerchantIds,
    merchantPanel.merchants,
  ]);
  const [merchantOpeningHoursSaving, setMerchantOpeningHoursSaving] = useState(false);
  const [merchantInterruptionForm, setMerchantInterruptionForm] = useState({
    description: '',
    start: '',
    end: '',
    selectedDuration: 15,
    selectedReason: 'Falta de entregador',
    customReason: '',
  });
  const [merchantInterruptionSaving, setMerchantInterruptionSaving] = useState(false);
  const [merchantInterruptionDeletingId, setMerchantInterruptionDeletingId] = useState('');
  const [siteWebhook, setSiteWebhook] = useState({
    siteEnabled: true,
    webhookUrl: '',
    brutosWebhookUrl: '',
    tokenBearer: '',
    webhookSecret: '',
  });
  const [siteWebhookStatus, setSiteWebhookStatus] = useState({ loading: true, error: '', updatedAt: null });
  const [siteWebhookSaving, setSiteWebhookSaving] = useState(false);
  const [showSiteToken, setShowSiteToken] = useState(false);
  const [showSiteSecret, setShowSiteSecret] = useState(false);
  const [brutosZap, setBrutosZap] = useState({
    enabled: true,
    webhookUrl: '',
    pingUrl: '',
    tokenBearer: '',
    webhookSecret: '',
    tokenPreview: '',
    secretPreview: '',
    hasToken: false,
    hasSecret: false,
    statusCallbackUrl: '',
    lastOrderReceivedAt: null,
    lastStatusCallbackAt: null,
    lastStatusCallbackError: '',
    updatedAt: null,
  });
  const [brutosZapStatus, setBrutosZapStatus] = useState({ loading: true, error: '', updatedAt: null });
  const [brutosZapSaving, setBrutosZapSaving] = useState(false);
  const [brutosZapTesting, setBrutosZapTesting] = useState(false);
  const [showBrutosZapToken, setShowBrutosZapToken] = useState(false);
  const [showBrutosZapSecret, setShowBrutosZapSecret] = useState(false);
  const [foodySettings, setFoodySettings] = useState({ integrationEnabled: true, apiToken: '', partnerToken: '', webhookToken: '', webhookUrl: '' });
  const [foodyStatus, setFoodyStatus] = useState({ loading: true, error: '', updatedAt: null, lastAttempt: null });
  const [foodySaving, setFoodySaving] = useState(false);
  const [showFoodyToken, setShowFoodyToken] = useState(false);
  const [showFoodyWebhookToken, setShowFoodyWebhookToken] = useState(false);
  const [entregasZapSettings, setEntregasZapSettings] = useState({
    integrationEnabled: true,
    webhookUrl: '',
    apiBaseUrl: '',
    entregasZapToken: '',
    brutosToken: '',
    brutosApiBaseUrl: '',
    callbackBaseUrl: '',
  });
  const [entregasZapStatus, setEntregasZapStatus] = useState({ loading: true, error: '', updatedAt: null });
  const [entregasZapSaving, setEntregasZapSaving] = useState(false);
  const [entregasZapTesting, setEntregasZapTesting] = useState(false);
  const [showEntregasZapToken, setShowEntregasZapToken] = useState(false);
  const [showBrutosToken, setShowBrutosToken] = useState(false);
  const [pedidos10Settings, setPedidos10Settings] = useState({
    integrationEnabled: true,
    apiBaseUrl: '',
    integrationToken: '',
    username: '',
    password: '',
    accessToken: '',
    merchantExternalCode: '',
    webhookSecret: '',
    webhookUrl: '',
    webhookPingUrl: '',
    webhookEnabled: true,
    pollingEnabled: false,
    docsUrl: '',
    apiDocsUrl: '',
  });
  const [pedidos10Status, setPedidos10Status] = useState({ loading: true, error: '', updatedAt: null, lastAuthAt: null, lastWebhookRegistrationAt: null, lastSyncAt: null });
  const [pedidos10Saving, setPedidos10Saving] = useState(false);
  const [pedidos10Testing, setPedidos10Testing] = useState(false);
  const [pedidos10Registering, setPedidos10Registering] = useState(false);
  const [pedidos10Syncing, setPedidos10Syncing] = useState(false);
  const [showPedidos10IntegrationToken, setShowPedidos10IntegrationToken] = useState(false);
  const [showPedidos10Password, setShowPedidos10Password] = useState(false);
  const [showPedidos10AccessToken, setShowPedidos10AccessToken] = useState(false);
  const [showPedidos10WebhookSecret, setShowPedidos10WebhookSecret] = useState(false);
  const hasLocalEntregasUrl = useMemo(() => {
    const rawUrl = String(entregasZapSettings.webhookUrl || '').trim();
    if (!rawUrl) return false;
    try {
      const hostname = new URL(rawUrl).hostname.toLowerCase();
      const currentHost = window.location.hostname.toLowerCase();
      const localTargets = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);
      const currentIsLocal = localTargets.has(currentHost);
      return localTargets.has(hostname) && !currentIsLocal;
    } catch {
      return false;
    }
  }, [entregasZapSettings.webhookUrl]);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [catalogAdminAction, setCatalogAdminAction] = useState('');
  const [catalogAdminSummary, setCatalogAdminSummary] = useState(null);
  const catalogAdminRequestRef = useRef(0);
  const catalogImportMerchantRef = useRef('');
  const [deleteAllProductsDialog, setDeleteAllProductsDialog] = useState(emptyDeleteAllProductsDialog);
  const [catalogImportMerchantId, setCatalogImportMerchantId] = useState('');
  const [catalogImportMerchantTouched, setCatalogImportMerchantTouched] = useState(false);
  const [detectedPrinters, setDetectedPrinters] = useState(() => {
    try {
      const saved = readTenantLocalJson(printerStorageKey, {}, tenantSession);
      const station = resolveCibdStationConnection({ baseUrl: SYSTEM_PRINT_BASE_URL, session: tenantSession, tenantSettings: saved });
      return normalizeCibdPrinterList(station?.printers || []);
    } catch {
      return [];
    }
  });
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [printerError, setPrinterError] = useState('');
  const [printRegistryStatus, setPrintRegistryStatus] = useState({ loading: false, error: '', synced: false });
  const [cibdStatus, setCibdStatus] = useState({ status: 'idle', message: '' });
  const [comandaPreviewOpen, setComandaPreviewOpen] = useState(false);
  const [printPointDialog, setPrintPointDialog] = useState({ open: false, name: '', printerId: '', enabled: true, error: '' });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [userSecuritySummary, setUserSecuritySummary] = useState(null);
  const [userDialog, setUserDialog] = useState({ open: false, mode: 'create', userId: null });
  const [userForm, setUserForm] = useState({ login: '', password: '', role: USER_ROLES.COLABORADOR });
  const [userSaving, setUserSaving] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [planSummary, setPlanSummary] = useState({ loading: true, error: '', data: null });
  const [companySettings, setCompanySettings] = useState(defaultCompanyContractSettings);
  const [companySettingsStatus, setCompanySettingsStatus] = useState({ loading: true, error: '' });
  const [companySettingsSaving, setCompanySettingsSaving] = useState(false);
  const [contractTemplate, setContractTemplate] = useState(defaultContractTemplateSettings);
  const [contractPreview, setContractPreview] = useState('');
  const [contractTemplateStatus, setContractTemplateStatus] = useState({ loading: true, error: '' });
  const [contractTemplateSaving, setContractTemplateSaving] = useState(false);
  const [contractTemplateResetting, setContractTemplateResetting] = useState(false);
  const openingHoursRefreshTimeoutRef = useRef(null);

  const planView = useMemo(() => {
    const data = planSummary.data || {};
    const entitlements = data.entitlements || {};
    const code = String(data.plan || 'FREE').toUpperCase();
    const supportTier = entitlements.supportTier || data.supportTier || 'SELF_HELP';
    const deliveryLimit = entitlements.deliveryDailyLimit ?? data.deliveryDailyLimit;
    const fiscalEnabled = data.fiscalEnabled === true && entitlements.fiscalEnabled !== false;

    return {
      code,
      label: PLAN_LABELS[code] || code,
      monthlyRevenue: formatCurrency(data.monthlyRevenueBrl),
      supportLabel: SUPPORT_TIER_LABELS[supportTier] || supportTier,
      deliveryLimit,
      fiscalEnabled,
      fullIntegrations: Boolean(entitlements.fullIntegrations),
      ifoodAnalysisIncluded: Boolean(entitlements.ifoodAnalysisIncluded),
      onboardingMeetingIncluded: Boolean(entitlements.onboardingMeetingIncluded),
      installationFee: formatCurrency(entitlements.installationFeeBrl),
    };
  }, [planSummary.data]);

  const cibdStatusInfo = useMemo(() => {
    const map = {
      idle: { label: 'CIBD não verificado', color: 'default' },
      checking: { label: 'Verificando CIBD', color: 'info' },
      online: { label: 'CIBD online', color: 'success' },
      offline: { label: 'CIBD offline', color: 'error' },
    };
    return map[cibdStatus.status] || map.idle;
  }, [cibdStatus.status]);

  const groupedOpeningHours = useMemo(
    () => groupOpeningHoursByDay(merchantPanel.openingHours),
    [merchantPanel.openingHours]
  );

  const activeOpeningDaysCount = useMemo(
    () => groupedOpeningHours.filter((day) => day.shifts.length > 0).length,
    [groupedOpeningHours]
  );

  const selectedMerchantSummary = useMemo(
    () => merchantPanel.merchants.find((merchant) => merchant.id === merchantPanel.selectedMerchantId) || merchantPanel.merchantDetails || null,
    [merchantPanel.merchants, merchantPanel.selectedMerchantId, merchantPanel.merchantDetails]
  );

  const hasIfoodCredentials = Boolean(ifoodSettings.credentialsConfigured || (ifoodSettings.clientId && ifoodSettings.clientSecret));
  const hasIfoodStores = Array.isArray(ifoodSettings.merchantIds) && ifoodSettings.merchantIds.length > 0;
  const catalogImportMerchantOptions = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(ifoodSettings.merchantIds) ? ifoodSettings.merchantIds : [])
      .map((merchantId) => {
        const id = normalizeIfoodMerchantId(merchantId);
        if (!id || seen.has(id)) return null;
        seen.add(id);
        const identity = merchantIdentityById.get(id);
        return {
          id,
          displayName: identity?.displayName || 'Loja iFood',
        };
      })
      .filter(Boolean);
  }, [ifoodSettings.merchantIds, merchantIdentityById]);
  const selectedCatalogImportMerchantId = normalizeIfoodMerchantId(catalogImportMerchantId);
  catalogImportMerchantRef.current = selectedCatalogImportMerchantId;
  const selectedCatalogImportMerchant = catalogImportMerchantOptions.find((merchant) => merchant.id === selectedCatalogImportMerchantId) || null;
  const selectedCatalogImportMerchantLabel = selectedCatalogImportMerchant?.displayName || 'Loja iFood';
  const selectedCatalogImportMerchantDescription = selectedCatalogImportMerchantId
    ? `${selectedCatalogImportMerchantLabel} (${selectedCatalogImportMerchantId})`
    : '';
  const canUseCatalogImportActions = hasIfoodCredentials && catalogImportMerchantOptions.length > 0 && Boolean(selectedCatalogImportMerchantId);

  const merchantAddressSummary = useMemo(() => {
    const address = merchantPanel.merchantDetails?.address;
    if (!address || typeof address !== 'object') return '-';
    const streetName = extractMerchantStreetName(address);
    return [
      streetName,
      address.streetNumber || address.number,
      address.neighborhood || address.district,
      address.city,
      address.state,
      address.country,
      address.postalCode,
    ].filter(Boolean).join(', ') || '-';
  }, [merchantPanel.merchantDetails]);

  const merchantStatusOperations = useMemo(() => {
    const statusPayload = merchantPanel.merchantStatus;
    if (Array.isArray(statusPayload)) return statusPayload;
    if (statusPayload && Array.isArray(statusPayload.operations)) return statusPayload.operations;
    return [];
  }, [merchantPanel.merchantStatus]);

  const merchantReadableFields = useMemo(() => {
    const merchant = merchantPanel.merchantDetails;
    if (!merchant || typeof merchant !== 'object') return [];

    const address = merchant.address && typeof merchant.address === 'object' ? merchant.address : {};
    const phoneValue = merchant.phone?.number || merchant.phone || '';
    const streetName = extractMerchantStreetName(address);
    const fields = [
      { label: 'Nome da Loja', value: merchant.name || merchant.displayName || '' },
      { label: 'Razão Social', value: merchant.corporateName || '' },
      { label: 'Telefone', value: phoneValue },
      { label: 'Rua', value: streetName },
      { label: 'Número', value: address.streetNumber || address.number || '' },
      { label: 'Complemento', value: address.complement || '' },
      { label: 'Bairro', value: address.neighborhood || address.district || '' },
      { label: 'Cidade', value: address.city || '' },
      { label: 'Estado', value: address.state || '' },
      { label: 'CEP', value: address.postalCode || '' },
      { label: 'País', value: address.country || '' },
      { label: 'Timezone', value: merchant.timezone || '' },
    ];

    return fields.filter((field) => String(field.value || '').trim());
  }, [merchantPanel.merchantDetails]);

  const availableTabs = useMemo(() => {
    const tabs = [{ id: 'usuarios', label: canAdministerUsers ? 'Usuários & Acesso' : 'Meu Login' }];

    if (canSeeOperationalSettings) {
      tabs.unshift(
        { id: 'plano', label: 'Plano' },
        { id: 'geral', label: 'Geral' },
        { id: 'impressao', label: 'Impressão & Comandas' },
        { id: 'loja', label: 'Loja' },
        { id: 'horario', label: 'Horário' },
        { id: 'interrupcoes', label: 'Interrupções' }
      );
      tabs.push({ id: 'entrega-preparo', label: 'Entrega e Preparo' });
    }

    if (canUseContractSettings) {
      const insertIndex = canSeeOperationalSettings ? 2 : 0;
      tabs.splice(insertIndex, 0, { id: 'contratos', label: 'Contratos' });
    }

    if (canSeeIntegrationConfig || canManageBeeDelivery) {
      tabs.push(
        { id: 'integracoes', label: 'Integrações' }
      );
    }

    if (isAdminLike(currentUser)) tabs.push({ id: 'auditoria-pedidos', label: 'Auditoria de pedidos' });

    return tabs;
  }, [canAdministerUsers, canManageBeeDelivery, canSeeIntegrationConfig, canSeeOperationalSettings, canUseContractSettings, currentUser]);

  useEffect(() => {
    if (!availableTabs.find((item) => item.id === tab)) {
      setTab(availableTabs[0]?.id || 'usuarios');
    }
  }, [availableTabs, tab]);

  useEffect(() => {
    let active = true;

    const hideFiscalFeature = () => {
      setFiscalFeature({ loading: false, available: false, summary: null });
      if (integrationTab === 'fiscal') {
        setIntegrationTab('ifood');
      }
    };

    const loadFiscalFeature = async () => {
      if (!canSeeFiscalIntegration) {
        hideFiscalFeature();
        return;
      }

      setFiscalFeature((prev) => ({ ...prev, loading: true }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/fiscal`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Fiscal indisponível.');
        }
        if (!active) return;
        const available = isFiscalSummaryActive(data);
        setFiscalFeature({ loading: false, available, summary: available ? data : null });
      } catch {
        if (!active) return;
        setFiscalFeature({ loading: false, available: false, summary: null });
      }
    };

    loadFiscalFeature();
    return () => {
      active = false;
    };
  }, [canSeeFiscalIntegration, integrationTab]);

  useEffect(() => {
    let active = true;
    if (!canSeeOperationalSettings) return undefined;
    apiFetch(`${API_URL}/api/delivery-pricing/config`)
      .then(async (response) => ({ response, data: await response.json().catch(() => ({})) }))
      .then(({ response, data }) => {
        if (!active || !response.ok) return;
        setIntegrationManagement(data.integrationManagement || {});
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [canSeeOperationalSettings]);

  const availableIntegrationTabs = useMemo(() => {
    if (!canSeeIntegrationConfig && !canManageBeeDelivery) return [];
    const tabs = canSeeIntegrationConfig ? [
      { id: 'ifood', label: 'iFood' },
      { id: 'pedidos10', label: 'Pedidos10' },
      { id: '99food', label: '99Food' },
      { id: 'foody', label: 'Foody Delivery' },
    ] : [];
    if (canManageBeeDelivery) tabs.push({ id: 'bee', label: 'Bee Delivery' });
    if (!canSeeIntegrationConfig) return tabs;
    if (!integrationManagement['cardapio-digital']?.managedBySuite) {
      tabs.splice(2, 0, { id: 'site', label: 'Site/Cardápio Digital' });
    }
    if (!integrationManagement.zap?.managedBySuite) {
      tabs.push({ id: 'brutos-zap', label: 'Brutos Zap' });
    }
    if (!integrationManagement.entregas?.managedBySuite) {
      tabs.push({ id: 'entregas-zap', label: 'Brutos Entregas' });
    }
    if (canSeeFiscalIntegration && fiscalFeature.available) {
      tabs.push({ id: 'fiscal', label: 'Fiscal' });
    }
    return tabs;
  }, [canManageBeeDelivery, canSeeFiscalIntegration, canSeeIntegrationConfig, fiscalFeature.available, integrationManagement]);

  useEffect(() => {
    if (tab !== 'integracoes') return;
    if (!availableIntegrationTabs.find((item) => item.id === integrationTab)) {
      setIntegrationTab(availableIntegrationTabs[0]?.id || 'ifood');
    }
  }, [availableIntegrationTabs, integrationTab, tab]);

  useEffect(() => {
    setMerchantInterruptionForm((prev) => {
      if (prev.start && prev.end) return prev;
      const windowRange = buildInterruptionWindow(prev.selectedDuration || 15);
      return {
        ...prev,
        description: prev.selectedReason === 'Outro' ? prev.customReason : prev.selectedReason,
        start: prev.start || windowRange.start,
        end: prev.end || windowRange.end,
      };
    });
  }, []);

  const loadMerchantPanel = async (preferredMerchantId = '') => {
    const preferredId = normalizeIfoodMerchantId(preferredMerchantId);
    const configuredMerchantIds = Array.isArray(ifoodSettings.merchantIds)
      ? ifoodSettings.merchantIds.map(normalizeIfoodMerchantId).filter(Boolean)
      : [];
    const availableMerchantIds = configuredMerchantIds.length ? configuredMerchantIds : (preferredId ? [preferredId] : []);
    if (!availableMerchantIds.length) {
      setMerchantPanel({
        loading: false,
        refreshing: false,
        error: '',
        merchants: [],
        selectedMerchantId: '',
        merchantDetails: null,
        merchantStatus: null,
        openingHours: [],
        interruptions: [],
      });
      return;
    }

    const isFirstLoad = !merchantPanel.merchants.length;
    setMerchantPanel((prev) => ({
      ...prev,
      loading: isFirstLoad,
      refreshing: !isFirstLoad,
      error: '',
    }));

    try {
      const merchantsResponse = await apiFetch(`${API_URL}/api/ifood/merchants`);
      const merchantsData = await merchantsResponse.json().catch(() => ({}));
      if (!merchantsResponse.ok || merchantsData.success === false) {
        throw new Error(merchantsData.error || 'Falha ao carregar merchants do iFood.');
      }

      const merchants = normalizeMerchantList(extractMerchantListPayload(merchantsData));
      const selectedId = normalizeIfoodMerchantId(merchantPanel.selectedMerchantId);
      const fallbackMerchantId = preferredId
        || (availableMerchantIds.includes(selectedId) ? selectedId : '')
        || availableMerchantIds[0]
        || merchants[0]?.id
        || '';

      if (!fallbackMerchantId) {
        setMerchantPanel({
          loading: false,
          refreshing: false,
          error: '',
          merchants,
          selectedMerchantId: '',
          merchantDetails: null,
          merchantStatus: null,
          openingHours: [],
          interruptions: [],
        });
        return;
      }

      const configuredMerchant = merchants.find((merchant) => merchant.id === fallbackMerchantId);
      if (configuredMerchant?.authorizationStatus === 'reauthorization-required' || configuredMerchant?.authorizationStatus === 'permission-review') {
        setMerchantPanel({
          loading: false,
          refreshing: false,
          error: '',
          merchants,
          selectedMerchantId: fallbackMerchantId,
          merchantDetails: configuredMerchant,
          merchantStatus: null,
          openingHours: [],
          interruptions: [],
        });
        return;
      }
      if (configuredMerchant?.accessError) {
        setMerchantPanel({
          loading: false,
          refreshing: false,
          error: `Loja ${fallbackMerchantId} cadastrada neste login/CNPJ, mas o iFood retornou: ${configuredMerchant.accessError}`,
          merchants,
          selectedMerchantId: fallbackMerchantId,
          merchantDetails: configuredMerchant,
          merchantStatus: null,
          openingHours: [],
          interruptions: [],
        });
        return;
      }

      const [merchantDetailsResponse, statusResponse, hoursResponse, interruptionsResponse] = await Promise.all([
        apiFetch(`${API_URL}/api/ifood/merchants/${fallbackMerchantId}`),
        apiFetch(`${API_URL}/api/ifood/merchants/${fallbackMerchantId}/status`),
        apiFetch(`${API_URL}/api/ifood/merchants/${fallbackMerchantId}/opening-hours`),
        apiFetch(`${API_URL}/api/ifood/merchants/${fallbackMerchantId}/interruptions`),
      ]);

      const merchantDetailsData = await merchantDetailsResponse.json().catch(() => ({}));
      const statusData = await statusResponse.json().catch(() => ({}));
      const hoursData = await hoursResponse.json().catch(() => ({}));
      const interruptionsData = await interruptionsResponse.json().catch(() => ({}));

      if (!merchantDetailsResponse.ok || merchantDetailsData.success === false) {
        throw new Error(merchantDetailsData.error || 'Falha ao carregar detalhes da loja.');
      }
      if (!statusResponse.ok || statusData.success === false) {
        throw new Error(statusData.error || 'Falha ao carregar status da loja.');
      }
      if (!hoursResponse.ok || hoursData.success === false) {
        throw new Error(hoursData.error || 'Falha ao carregar horário de funcionamento.');
      }
      if (!interruptionsResponse.ok || interruptionsData.success === false) {
        throw new Error(interruptionsData.error || 'Falha ao carregar interrupções da loja.');
      }

      setMerchantPanel({
        loading: false,
        refreshing: false,
        error: '',
        merchants,
        selectedMerchantId: fallbackMerchantId,
        merchantDetails: extractMerchantDetailsPayload(merchantDetailsData),
        merchantStatus: extractMerchantStatusPayload(statusData),
        openingHours: normalizeOpeningHours(extractOpeningHoursPayload(hoursData)),
        interruptions: normalizeInterruptions(extractInterruptionsPayload(interruptionsData)),
      });
    } catch (err) {
      setMerchantPanel((prev) => ({
        ...prev,
        loading: false,
        refreshing: false,
        error: err.message || 'Falha ao carregar módulo Merchant do iFood.',
      }));
    }
  };

  useEffect(() => {
    writeTenantLocalJson(generalStorageKey, generalSettings, tenantSession);
  }, [generalSettings, tenantSession]);

  useEffect(() => {
    writeTenantPrinterSettings(printerSettings, tenantSession);
  }, [printerSettings, tenantSession]);

  useEffect(() => {
    if (!canSeeOperationalSettings) return undefined;
    let active = true;

    const loadPrintPointRegistry = async () => {
      setPrintRegistryStatus({ loading: true, error: '', synced: false });
      try {
        const response = await apiFetch(`${API_URL}/api/salon/print-points`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Falha ao carregar os pontos de impressão compartilhados.');

        if (!active || !isSettingsSessionCurrent()) return;
        let serverPoints = Array.isArray(data.points) ? data.points : [];
        // Only an existing tenant-scoped snapshot can initialize this tenant's
        // registry. Global legacy data has no provable owner and stays inert.
        if (data.initialized === false && hasTenantPrinterSettings) {
          const migrationResponse = await apiFetch(`${API_URL}/api/salon/print-points`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              points: toPrintPointRegistryPayload(printerSettings),
              initializeIfUninitialized: true,
            }),
          });
          const migrationData = await migrationResponse.json().catch(() => ({}));
          if (!migrationResponse.ok) throw new Error(migrationData.error || 'Falha ao migrar os pontos de impressão locais.');
          serverPoints = Array.isArray(migrationData.points) ? migrationData.points : serverPoints;
        }

        if (!active || !isSettingsSessionCurrent()) return;
        setPrinterSettings((prev) => mergeServerPrintPoints(prev, serverPoints));
        setPrintRegistryStatus({ loading: false, error: '', synced: true });
      } catch (err) {
        if (!active) return;
        setPrintRegistryStatus({ loading: false, error: err.message || 'Não foi possível sincronizar os pontos de impressão.', synced: false });
      }
    };

    loadPrintPointRegistry();
    return () => { active = false; };
    // The initial local snapshot is intentionally used only once for migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeOperationalSettings]);

  useEffect(() => {
    const normalizedCurrent = normalizeIfoodMerchantId(catalogImportMerchantId);
    if (!catalogImportMerchantOptions.length) {
      if (normalizedCurrent) setCatalogImportMerchantId('');
      if (catalogImportMerchantTouched) setCatalogImportMerchantTouched(false);
      return;
    }

    if (catalogImportMerchantOptions.length === 1) {
      const onlyMerchantId = catalogImportMerchantOptions[0].id;
      if (normalizedCurrent !== onlyMerchantId) setCatalogImportMerchantId(onlyMerchantId);
      if (catalogImportMerchantTouched) setCatalogImportMerchantTouched(false);
      return;
    }

    const currentIsValid = normalizedCurrent && catalogImportMerchantOptions.some((merchant) => merchant.id === normalizedCurrent);
    if (!catalogImportMerchantTouched || !currentIsValid) {
      if (normalizedCurrent) setCatalogImportMerchantId('');
      if (catalogImportMerchantTouched && !currentIsValid) setCatalogImportMerchantTouched(false);
    }
  }, [catalogImportMerchantId, catalogImportMerchantOptions, catalogImportMerchantTouched]);

  useEffect(() => {
    catalogAdminRequestRef.current += 1;
    setCatalogAdminSummary(null);
    setCatalogAdminAction((current) => (
      ['import-full', 'import-new-only', 'clear-all'].includes(current) ? '' : current
    ));
  }, [selectedCatalogImportMerchantId]);

  useEffect(() => {
    let active = true;

    const loadIfoodSettings = async () => {
      if (!canSeeAdvancedConfig) {
        setIfoodStatus({ loading: false, error: '', updatedAt: null });
        return;
      }
      setIfoodStatus((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/ifood`);
        if (!response.ok) {
          throw new Error('Falha ao carregar integração do iFood.');
        }
        const data = await response.json();
        if (!active) return;
        setIfoodSettings({
          integrationEnabled: data.integrationEnabled !== false,
          clientId: data.clientId || '',
          clientSecret: data.clientSecret || '',
          widgetId: data.widgetId || '',
          merchantIds: Array.isArray(data.merchantIds) ? data.merchantIds : [],
          scheduleMerchantId: data.scheduleMerchantId || '',
          detectedMerchantIds: Array.isArray(data.detectedMerchantIds) ? data.detectedMerchantIds : [],
          pollingEnabled: data.pollingEnabled !== false,
          webhookEnabled: Boolean(data.webhookEnabled),
          autoAcceptEnabled: data.autoAcceptEnabled !== false,
          shippingEnabled: data.shippingEnabled === true,
          shippingMerchantIds: Array.isArray(data.shippingMerchantIds) ? data.shippingMerchantIds : [],
          lastTransportFailure: data.lastTransportFailure || null,
          credentialsConfigured: Boolean(data.credentialsConfigured),
          credentials: data.credentials || {},
        });
        setIfoodStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
        loadMerchantPanel(data.scheduleMerchantId || (Array.isArray(data.merchantIds) ? data.merchantIds[0] : '') || '');
      } catch (err) {
        if (!active) return;
        setIfoodStatus({ loading: false, error: err.message || 'Falha ao carregar integração do iFood.', updatedAt: null });
        setMerchantPanel((prev) => ({
          ...prev,
          loading: false,
          refreshing: false,
          error: err.message || 'Falha ao carregar integração do iFood.',
        }));
      }
    };

    loadIfoodSettings();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeAdvancedConfig]);

  useEffect(() => {
    let active = true;

    const loadPlanSummary = async () => {
      if (!canSeeOperationalSettings) {
        setPlanSummary({ loading: false, error: '', data: null });
        return;
      }

      setPlanSummary((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/system/entitlements`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Falha ao carregar plano.');
        }
        if (!active) return;
        setPlanSummary({ loading: false, error: '', data });
      } catch (err) {
        if (!active) return;
        setPlanSummary({ loading: false, error: err.message || 'Falha ao carregar plano.', data: null });
      }
    };

    loadPlanSummary();
    return () => {
      active = false;
    };
  }, [canSeeOperationalSettings]);

  useEffect(() => {
    let active = true;

    const loadContractSettings = async () => {
      if (!canUseContractSettings) {
        setCompanySettingsStatus({ loading: false, error: '' });
        setContractTemplateStatus({ loading: false, error: '' });
        return;
      }

      setCompanySettingsStatus({ loading: true, error: '' });
      setContractTemplateStatus({ loading: true, error: '' });

      try {
        const [companyResponse, contractsResponse] = await Promise.all([
          apiFetch(`${API_URL}/api/settings/company`),
          apiFetch(`${API_URL}/api/settings/contracts`),
        ]);
        const companyData = await companyResponse.json().catch(() => ({}));
        const contractsData = await contractsResponse.json().catch(() => ({}));

        if (!companyResponse.ok) {
          throw new Error(companyData.error || 'Falha ao carregar dados formais da empresa.');
        }
        if (!contractsResponse.ok) {
          throw new Error(contractsData.error || 'Falha ao carregar modelo de contrato.');
        }
        if (!active) return;
        setCompanySettings({ ...defaultCompanyContractSettings, ...(companyData.company || {}) });
        setContractTemplate({ ...defaultContractTemplateSettings, ...(contractsData.template || {}) });
        setContractPreview(contractsData.preview || '');
        setCompanySettingsStatus({ loading: false, error: '' });
        setContractTemplateStatus({ loading: false, error: '' });
      } catch (err) {
        if (!active) return;
        const message = err.message || 'Falha ao carregar contratos.';
        setCompanySettingsStatus({ loading: false, error: message });
        setContractTemplateStatus({ loading: false, error: message });
      }
    };

    loadContractSettings();
    return () => {
      active = false;
    };
  }, [canUseContractSettings]);

  useEffect(() => {
    let active = true;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError('');

      try {
        const [usersResponse, summaryResponse] = await Promise.all([
          apiFetch(`${API_URL}/api/auth/users`),
          canAdministerUsers ? apiFetch(`${API_URL}/api/auth/security-summary`) : Promise.resolve(null),
        ]);
        const usersData = await usersResponse.json().catch(() => ({}));
        if (!usersResponse.ok) {
          throw new Error(usersData.error || 'Falha ao carregar usuários.');
        }
        if (!active) return;
        setUsers(Array.isArray(usersData.users) ? usersData.users : []);

        if (summaryResponse) {
          const summaryData = await summaryResponse.json().catch(() => ({}));
          if (!summaryResponse.ok) {
            throw new Error(summaryData.error || 'Falha ao carregar o resumo de segurança dos usuários.');
          }
          if (!active) return;
          setUserSecuritySummary(summaryData);
        } else {
          setUserSecuritySummary(null);
        }
      } catch (err) {
        if (!active) return;
        setUsersError(err.message || 'Falha ao carregar usuários.');
      } finally {
        if (active) setUsersLoading(false);
      }
    };

    loadUsers();
    return () => {
      active = false;
    };
  }, [canAdministerUsers]);

  useEffect(() => {
    if (!canSeeOperationalSettings) {
      setMerchantPanel((prev) => ({ ...prev, loading: false, refreshing: false }));
      return;
    }

    loadMerchantPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeOperationalSettings]);

  useEffect(() => {
    let active = true;

    const loadFoodySettings = async () => {
      if (!canSeeAdvancedConfig) {
        setFoodyStatus({ loading: false, error: '', updatedAt: null, lastAttempt: null });
        return;
      }
      setFoodyStatus((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/foody`);
        if (!response.ok) {
          throw new Error('Falha ao carregar integração do Foody Delivery.');
        }
        const data = await response.json();
        if (!active) return;
        setFoodySettings({
          integrationEnabled: data.integrationEnabled !== false,
          apiToken: data.apiToken || '',
          partnerToken: data.partnerToken || '',
          webhookToken: data.webhookToken || '',
          webhookUrl: data.webhookUrl || '',
        });
        setFoodyStatus({ loading: false, error: '', updatedAt: data.updatedAt || null, lastAttempt: data.lastAttempt || null });
      } catch (err) {
        if (!active) return;
        setFoodyStatus({
          loading: false,
          error: err.message || 'Falha ao carregar integração do Foody Delivery.',
          updatedAt: null,
          lastAttempt: null,
        });
      }
    };

    loadFoodySettings();

    return () => {
      active = false;
    };
  }, [canSeeAdvancedConfig]);

  useEffect(() => {
    let active = true;

    const loadEntregasZapSettings = async () => {
      if (!canSeeAdvancedConfig) {
        setEntregasZapStatus({ loading: false, error: '', updatedAt: null });
        return;
      }
      setEntregasZapStatus((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/entregas-zap`);
        if (!response.ok) {
          throw new Error('Falha ao carregar integração com Brutos Entregas.');
        }
        const data = await response.json();
        if (!active) return;
        setEntregasZapSettings({
          integrationEnabled: data.integrationEnabled !== false,
          webhookUrl: data.webhookUrl || '',
          apiBaseUrl: data.apiBaseUrl || '',
          entregasZapToken: data.entregasZapToken || data.integrationToken || '',
          brutosToken: data.brutosToken || '',
          brutosApiBaseUrl: data.brutosApiBaseUrl || '',
          callbackBaseUrl: data.callbackBaseUrl || '',
        });
        setEntregasZapStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
      } catch (err) {
        if (!active) return;
        setEntregasZapStatus({
          loading: false,
          error: err.message || 'Falha ao carregar integração com Brutos Entregas.',
          updatedAt: null,
        });
      }
    };

    loadEntregasZapSettings();

    return () => {
      active = false;
    };
  }, [canSeeAdvancedConfig]);

  useEffect(() => {
    let active = true;

    const loadPedidos10Settings = async () => {
      if (!canSeeAdvancedConfig) {
        setPedidos10Status({ loading: false, error: '', updatedAt: null, lastAuthAt: null, lastWebhookRegistrationAt: null, lastSyncAt: null });
        return;
      }
      setPedidos10Status((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/pedidos10`);
        if (!response.ok) {
          throw new Error('Falha ao carregar integração com Pedidos10.');
        }
        const data = await response.json();
        if (!active) return;
        setPedidos10Settings({
          integrationEnabled: data.integrationEnabled !== false,
          apiBaseUrl: data.apiBaseUrl || '',
          integrationToken: data.integrationToken || '',
          username: data.username || '',
          password: data.password || '',
          accessToken: data.accessToken || '',
          merchantExternalCode: data.merchantExternalCode || '',
          webhookSecret: data.webhookSecret || '',
          webhookUrl: data.webhookUrl || '',
          webhookPingUrl: data.webhookPingUrl || '',
          webhookEnabled: data.webhookEnabled !== false,
          pollingEnabled: Boolean(data.pollingEnabled),
          docsUrl: data.docsUrl || '',
          apiDocsUrl: data.apiDocsUrl || '',
        });
        setPedidos10Status({
          loading: false,
          error: '',
          updatedAt: data.updatedAt || null,
          lastAuthAt: data.lastAuthAt || null,
          lastWebhookRegistrationAt: data.lastWebhookRegistrationAt || null,
          lastSyncAt: data.lastSyncAt || null,
        });
      } catch (err) {
        if (!active) return;
        setPedidos10Status({
          loading: false,
          error: err.message || 'Falha ao carregar integração com Pedidos10.',
          updatedAt: null,
          lastAuthAt: null,
          lastWebhookRegistrationAt: null,
          lastSyncAt: null,
        });
      }
    };

    loadPedidos10Settings();

    return () => {
      active = false;
    };
  }, [canSeeAdvancedConfig]);

  useEffect(() => {
    let active = true;

    const loadSiteWebhook = async () => {
      if (!canSeeAdvancedConfig) {
        setSiteWebhookStatus({ loading: false, error: '', updatedAt: null });
        return;
      }
      setSiteWebhookStatus((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/site-webhook`);
        if (!response.ok) {
          throw new Error('Falha ao carregar integração do site/cardápio.');
        }
        const data = await response.json();
        if (!active) return;
        const fallbackUrl = API_URL
          ? `${API_URL}/api/webhooks/site`
          : `${window.location.origin}/api/webhooks/site`;
        setSiteWebhook({
          siteEnabled: data.siteEnabled !== false,
          webhookUrl: data.webhookUrl || fallbackUrl,
          brutosWebhookUrl: data.brutosWebhookUrl || data.webhookUrl || fallbackUrl,
          tokenBearer: data.tokenBearer || '',
          webhookSecret: data.webhookSecret || '',
        });
        setSiteWebhookStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
      } catch (err) {
        if (!active) return;
        const fallbackUrl = API_URL
          ? `${API_URL}/api/webhooks/site`
          : `${window.location.origin}/api/webhooks/site`;
        setSiteWebhook((prev) => ({
          ...prev,
          webhookUrl: prev.webhookUrl || fallbackUrl,
          brutosWebhookUrl: prev.brutosWebhookUrl || fallbackUrl,
        }));
        setSiteWebhookStatus({
          loading: false,
          error: err.message || 'Falha ao carregar integração do site/cardápio.',
          updatedAt: null,
        });
      }
    };

    loadSiteWebhook();

    return () => {
      active = false;
    };
  }, [canSeeAdvancedConfig]);

  useEffect(() => {
    let active = true;

    const loadBrutosZap = async () => {
      if (!canSeeAdvancedConfig) {
        setBrutosZapStatus({ loading: false, error: '', updatedAt: null });
        return;
      }
      setBrutosZapStatus((prev) => ({ ...prev, loading: true, error: '' }));
      try {
        const response = await apiFetch(`${API_URL}/api/settings/brutos-zap`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'Falha ao carregar integração Brutos Zap.');
        }
        if (!active) return;
        setBrutosZap((prev) => ({
          ...prev,
          enabled: data.enabled !== false,
          webhookUrl: data.webhookUrl || prev.webhookUrl,
          pingUrl: data.pingUrl || prev.pingUrl,
          tokenBearer: '',
          webhookSecret: '',
          tokenPreview: data.tokenPreview || '',
          secretPreview: data.secretPreview || '',
          hasToken: Boolean(data.hasToken),
          hasSecret: Boolean(data.hasSecret),
          statusCallbackUrl: data.statusCallbackUrl || '',
          lastOrderReceivedAt: data.lastOrderReceivedAt || null,
          lastStatusCallbackAt: data.lastStatusCallbackAt || null,
          lastStatusCallbackError: data.lastStatusCallbackError || '',
          updatedAt: data.updatedAt || null,
        }));
        setBrutosZapStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
      } catch (err) {
        if (!active) return;
        setBrutosZapStatus({
          loading: false,
          error: err.message || 'Falha ao carregar integração Brutos Zap.',
          updatedAt: null,
        });
      }
    };

    loadBrutosZap();

    return () => {
      active = false;
    };
  }, [canSeeAdvancedConfig]);

  const handleGeneralToggle = (key) => (event) => {
    setGeneralSettings((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const handleUserFormChange = (key) => (event) => {
    setUserForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const openUserDialog = (mode, user = null) => {
    const baseRole = user?.role && USER_TAB_OPTIONS.some((item) => item.value === user.role)
      ? user.role
      : USER_ROLES.COLABORADOR;

    setUserDialog({ open: true, mode, userId: user?.id || null });
    setUserForm({
      login: user?.login || '',
      password: '',
      role: baseRole,
    });
  };

  const closeUserDialog = () => {
    setUserDialog({ open: false, mode: 'create', userId: null });
    setUserForm({ login: '', password: '', role: USER_ROLES.COLABORADOR });
  };

  const handleGeneralChange = (key) => (event, valueOverride) => {
    const raw = typeof valueOverride !== 'undefined' ? valueOverride : event.target?.value;
    const isNumberInput = event?.target?.type === 'number';
    const value = isNumberInput ? Number(raw) : raw;
    setGeneralSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrinterChange = (key) => (event) => {
    const raw = event?.target?.value;
    const isNumberInput = event?.target?.type === 'number';
    const value = isNumberInput ? Number(raw) : raw;
    setPrinterSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handlePrinterToggle = (key) => (event) => {
    setPrinterSettings((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const handlePrinterSlider = (key) => (_, value) => {
    setPrinterSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSelectDetectedPrinter = (event) => {
    const printerId = event.target.value;
    setPrinterSettings((prev) => ({
      ...prev,
      printerId,
      printPoints: normalizePrintPoints({ ...prev, printerId }).map((point) => (
        point.id === 'caixa' ? { ...point, printerId } : point
      )),
    }));
  };

  const handlePrintPointChange = (pointId, key) => (event) => {
    const value = key === 'enabled' ? event.target.checked : event.target.value;
    setPrinterSettings((prev) => {
      const printPoints = normalizePrintPoints(prev).map((point) => (
        point.id === pointId ? { ...point, [key]: value } : point
      ));
      return {
        ...prev,
        printPoints,
        ...(pointId === 'caixa' && key === 'printerId' ? { printerId: value } : {}),
      };
    });
  };

  const openPrintPointDialog = () => {
    setPrintPointDialog({ open: true, name: '', printerId: '', enabled: true, error: '' });
  };

  const closePrintPointDialog = () => {
    setPrintPointDialog({ open: false, name: '', printerId: '', enabled: true, error: '' });
  };

  const handleCreatePrintPoint = () => {
    const name = String(printPointDialog.name || '').trim();
    const printerId = String(printPointDialog.printerId || '').trim();
    if (!name) {
      setPrintPointDialog((prev) => ({ ...prev, error: 'Informe o nome do ponto de impressão.' }));
      return;
    }
    if (!printerId) {
      setPrintPointDialog((prev) => ({ ...prev, error: 'Selecione a impressora deste ponto.' }));
      return;
    }
    const printPoints = normalizePrintPoints(printerSettings);
    if (printPoints.some((point) => point.name.toLocaleLowerCase('pt-BR') === name.toLocaleLowerCase('pt-BR'))) {
      setPrintPointDialog((prev) => ({ ...prev, error: 'Já existe um ponto de impressão com esse nome.' }));
      return;
    }
    if (printPoints.length >= MAX_PRINT_POINTS) {
      setPrintPointDialog((prev) => ({ ...prev, error: `É possível cadastrar no máximo ${MAX_PRINT_POINTS} pontos de impressão.` }));
      return;
    }
    setPrinterSettings((prev) => ({
      ...prev,
      printPoints: [...normalizePrintPoints(prev), {
        id: createPrintPointId(),
        name,
        printerId,
        enabled: Boolean(printPointDialog.enabled),
        isDefault: false,
      }],
    }));
    closePrintPointDialog();
  };

  const handleRemovePrintPoint = (pointId) => {
    if (pointId === 'caixa') return;
    setPrinterSettings((prev) => ({
      ...prev,
      printPoints: normalizePrintPoints(prev).filter((point) => point.id !== pointId),
    }));
  };

  const handleIfoodTransportToggle = (method) => (event) => {
    const checked = Boolean(event.target.checked);
    setIfoodSettings((prev) => {
      const next = {
        ...prev,
        [method === 'webhook' ? 'webhookEnabled' : 'pollingEnabled']: checked,
      };
      if (!next.pollingEnabled && !next.webhookEnabled) {
        next.pollingEnabled = true;
      }
      if (next.webhookEnabled) {
        next.pollingEnabled = true;
      }
      return next;
    });
  };

  const handleIfoodToggle = (key) => (event) => {
    setIfoodSettings((prev) => ({ ...prev, [key]: event.target.checked }));
  };

  const handleIfoodShippingMerchantsChange = (event) => {
    const value = event.target.value;
    setIfoodSettings((prev) => ({
      ...prev,
      shippingMerchantIds: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleAddIfoodMerchantId = () => {
    const merchantId = normalizeIfoodMerchantId(ifoodMerchantIdDraft);
    if (!merchantId) {
      setToast({ open: true, message: 'Informe o ID da Loja.', severity: 'warning' });
      return;
    }
    if (!IFOOD_MERCHANT_ID_REGEX.test(merchantId)) {
      setToast({ open: true, message: 'ID da Loja inválido. Informe o merchantId completo do iFood.', severity: 'warning' });
      return;
    }
    if ((Array.isArray(ifoodSettings.merchantIds) ? ifoodSettings.merchantIds : []).includes(merchantId)) {
      setToast({ open: true, message: 'Este ID da Loja já está cadastrado.', severity: 'info' });
      setIfoodMerchantIdDraft('');
      return;
    }

    setIfoodSettings((prev) => {
      const currentMerchantIds = Array.isArray(prev.merchantIds) ? prev.merchantIds : [];
      return { ...prev, merchantIds: [...currentMerchantIds, merchantId] };
    });
    setIfoodMerchantIdDraft('');
  };

  const handleIfoodMerchantIdKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleAddIfoodMerchantId();
  };

  const handleRemoveIfoodMerchantId = (merchantId) => {
    const normalizedMerchantId = normalizeIfoodMerchantId(merchantId);
    setIfoodSettings((prev) => ({
      ...prev,
      merchantIds: (Array.isArray(prev.merchantIds) ? prev.merchantIds : [])
        .filter((item) => normalizeIfoodMerchantId(item) !== normalizedMerchantId),
      scheduleMerchantId: normalizeIfoodMerchantId(prev.scheduleMerchantId) === normalizedMerchantId
        ? ''
        : prev.scheduleMerchantId,
      shippingMerchantIds: (Array.isArray(prev.shippingMerchantIds) ? prev.shippingMerchantIds : [])
        .filter((item) => normalizeIfoodMerchantId(item) !== normalizedMerchantId),
    }));
    setMerchantPanel((prev) => (
      prev.selectedMerchantId === normalizedMerchantId
        ? {
          ...prev,
          selectedMerchantId: '',
          merchantDetails: null,
          merchantStatus: null,
          openingHours: [],
          interruptions: [],
        }
        : prev
    ));
  };

  const handleMerchantSelectionChange = (event) => {
    const nextMerchantId = event.target.value;
    setMerchantPanel((prev) => ({ ...prev, selectedMerchantId: nextMerchantId }));
    if (nextMerchantId) {
      loadMerchantPanel(nextMerchantId);
    }
  };

  const handleOpeningHourChange = (shiftId, key) => (event) => {
    const rawValue = event?.target?.value;
    setMerchantPanel((prev) => ({
      ...prev,
      openingHours: prev.openingHours.map((shift) => (
        shift.id === shiftId
          ? {
            ...shift,
            [key]: key === 'duration' ? Number(rawValue) : rawValue,
          }
          : shift
      )),
    }));
  };

  const handleAddOpeningHour = (dayOfWeek = 'MONDAY') => {
    setMerchantPanel((prev) => ({
      ...prev,
      openingHours: [...prev.openingHours, { ...createEmptyShift(), dayOfWeek }],
    }));
  };

  const handleRemoveOpeningHour = (shiftId) => {
    setMerchantPanel((prev) => ({
      ...prev,
      openingHours: prev.openingHours.filter((shift) => shift.id !== shiftId),
    }));
  };

  const handleSaveOpeningHours = async () => {
    if (!merchantPanel.selectedMerchantId) {
      setToast({ open: true, message: 'Selecione uma loja antes de salvar horários.', severity: 'warning' });
      return;
    }

    const visualOpeningHours = sortOpeningHours(
      merchantPanel.openingHours.map((shift) => ({
        ...shift,
        start: String(shift.start || '00:00').slice(0, 5),
        end: String(shift.end || '00:00').slice(0, 5),
      }))
    );

    const payload = merchantPanel.openingHours.map((shift) => ({
      dayOfWeek: shift.dayOfWeek,
      start: `${String(shift.start || '00:00').slice(0, 5)}:00`,
      duration: timeToMinutes(shift.end) - timeToMinutes(shift.start),
    }));

    const hasInvalidShift = payload.some((shift) => (
      !shift.dayOfWeek ||
      !/^\d{2}:\d{2}:\d{2}$/.test(shift.start) ||
      !Number.isFinite(shift.duration) ||
      shift.duration <= 0
    ));

    if (!payload.length || hasInvalidShift) {
      setToast({
        open: true,
        message: 'Revise os turnos: é preciso ter ao menos um horário válido com duração maior que zero.',
        severity: 'warning',
      });
      return;
    }

    setMerchantOpeningHoursSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/merchants/${merchantPanel.selectedMerchantId}/opening-hours`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: merchantPanel.selectedMerchantId,
          shifts: payload,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao salvar horário de funcionamento.');
      }

      setMerchantPanel((prev) => ({
        ...prev,
        openingHours: visualOpeningHours,
      }));
      if (openingHoursRefreshTimeoutRef.current) {
        window.clearTimeout(openingHoursRefreshTimeoutRef.current);
      }
      openingHoursRefreshTimeoutRef.current = window.setTimeout(() => {
        loadMerchantPanel(merchantPanel.selectedMerchantId);
      }, 1200);
      setToast({ open: true, message: 'Horário de funcionamento atualizado no Merchant do iFood.', severity: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao salvar horário de funcionamento.', severity: 'error' });
    } finally {
      setMerchantOpeningHoursSaving(false);
    }
  };

  const handleMerchantInterruptionFormChange = (key) => (event) => {
    const value = event?.target?.value || '';
    setMerchantInterruptionForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'customReason') {
        next.description = prev.selectedReason === 'Outro' ? value : prev.description;
      }
      return next;
    });
  };

  const handleSelectInterruptionDuration = (duration) => {
    const windowRange = buildInterruptionWindow(duration);
    setMerchantInterruptionForm((prev) => ({
      ...prev,
      selectedDuration: duration,
      start: windowRange.start,
      end: windowRange.end,
    }));
  };

  const handleSelectInterruptionReason = (reason) => {
    setMerchantInterruptionForm((prev) => ({
      ...prev,
      selectedReason: reason,
      description: reason === 'Outro' ? prev.customReason : reason,
    }));
  };

  const handleCreateInterruption = async () => {
    if (!merchantPanel.selectedMerchantId) {
      setToast({ open: true, message: 'Selecione uma loja antes de criar uma interrupção.', severity: 'warning' });
      return;
    }

    const resolvedDescription = merchantInterruptionForm.selectedReason === 'Outro'
      ? merchantInterruptionForm.customReason
      : merchantInterruptionForm.selectedReason || merchantInterruptionForm.description;
    const description = String(resolvedDescription || '').trim();
    const start = toIsoStringFromLocalInput(merchantInterruptionForm.start);
    const end = toIsoStringFromLocalInput(merchantInterruptionForm.end);

    if (!description || !start || !end) {
      setToast({ open: true, message: 'Preencha descrição, início e fim da interrupção.', severity: 'warning' });
      return;
    }

    setMerchantInterruptionSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/merchants/${merchantPanel.selectedMerchantId}/interruptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, start, end }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Falha ao criar interrupção.');
      }

      const nextWindow = buildInterruptionWindow(merchantInterruptionForm.selectedDuration || 15);
      setMerchantInterruptionForm((prev) => ({
        ...prev,
        description: prev.selectedReason === 'Outro' ? '' : prev.selectedReason,
        customReason: '',
        start: nextWindow.start,
        end: nextWindow.end,
      }));
      setMerchantPanel((prev) => ({
        ...prev,
        interruptions: mergeInterruption(prev.interruptions, data),
      }));
      setToast({ open: true, message: 'Interrupção criada com sucesso.', severity: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao criar interrupção.', severity: 'error' });
    } finally {
      setMerchantInterruptionSaving(false);
    }
  };

  const handleDeleteInterruption = async (interruptionId) => {
    if (!merchantPanel.selectedMerchantId || !interruptionId) return;

    setMerchantInterruptionDeletingId(interruptionId);
    try {
      const response = await apiFetch(`${API_URL}/api/ifood/merchants/${merchantPanel.selectedMerchantId}/interruptions/${interruptionId}`, {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'Falha ao remover interrupção.');
      }

      setMerchantPanel((prev) => ({
        ...prev,
        interruptions: prev.interruptions.filter((item) => item.id !== interruptionId),
      }));
      setToast({ open: true, message: 'Interrupção removida com sucesso.', severity: 'success' });
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao remover interrupção.', severity: 'error' });
    } finally {
      setMerchantInterruptionDeletingId('');
    }
  };

  const fetchCibdPrinters = async ({ silent = false } = {}) => {
    if (!isSettingsSessionCurrent()) return;
    const tenantSettings = readTenantLocalJson(printerStorageKey, {}, tenantSession);
    const persistedConnection = resolveCibdStationConnection({
      baseUrl: SYSTEM_PRINT_BASE_URL,
      session: tenantSession,
      tenantSettings,
    });
    if (!persistedConnection) return;
    const draftToken = String(printerSettingsRef.current.cibdToken || '').trim();
    const usesDraftToken = draftToken !== persistedConnection.token;
    const connection = usesDraftToken
      ? { ...persistedConnection, token: draftToken, mode: 'draft' }
      : persistedConnection;
    const connectionIsCurrent = () => isSettingsSessionCurrent() && (
      usesDraftToken
        ? String(printerSettingsRef.current.cibdToken || '').trim() === draftToken
          && isCibdStationConnectionCurrent(persistedConnection, {
            baseUrl: SYSTEM_PRINT_BASE_URL,
            session: tenantSession,
            tenantSettings,
          })
        : isCibdStationConnectionCurrent(connection, {
          baseUrl: SYSTEM_PRINT_BASE_URL,
          session: tenantSession,
          tenantSettings,
        })
    );
    let acceptedResponse = false;
    setLoadingPrinters(true);
    try {
      if (!silent) setPrinterError('');
      setCibdStatus({ status: 'checking', message: 'Verificando CIBD...' });
      const response = await fetch(`${connection.baseUrl}/api/system/printers`, {
        headers: buildCibdHeaders({ cibdToken: connection.token }),
        credentials: 'omit',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao buscar impressoras no CIBD.');
      }
      if (!connectionIsCurrent()) return;
      acceptedResponse = true;
      const list = normalizeCibdPrinterList(data.printers || []);
      setDetectedPrinters(list);
      if (usesDraftToken) {
        const currentBindings = Object.fromEntries(normalizePrintPoints(printerSettingsRef.current)
          .filter((point) => point.printerId)
          .map((point) => [point.id, point.printerId]));
        if (!writeCibdStationProfile({
          baseUrl: SYSTEM_PRINT_BASE_URL,
          token: draftToken,
          printers: list,
          defaultPrinter: data.defaultPrinter,
          bindings: currentBindings,
          mode: 'explicit',
          session: tenantSession,
        })) throw new Error('Não foi possível salvar a conexão do CIBD nesta estação.');
      } else {
        updateCibdStationDiscovery({
          connection,
          printers: list,
          defaultPrinter: data.defaultPrinter,
          session: tenantSession,
        });
      }
      const currentStation = resolveCibdStationConnection({
        baseUrl: SYSTEM_PRINT_BASE_URL,
        session: tenantSession,
      });
      setPrinterSettings((prev) => applyCibdStationBindings(prev, currentStation));
      setCibdStatus({ status: 'online', message: 'CIBD ativo.' });
      // Discovery refreshes station defaults; an existing tenant binding still wins.
      if (printerSettings.printerId && list.length && !list.find((p) => p.id === printerSettings.printerId)) {
        setPrinterSettings((prev) => ({ ...prev, printerId: '' }));
      }
      if (data.error && !silent) setPrinterError(data.error);
    } catch (err) {
      if (!connectionIsCurrent()) return;
      if (!silent) setPrinterError(err.message || 'Não foi possível carregar impressoras.');
      setCibdStatus({ status: 'offline', message: err.message || 'CIBD indisponível.' });
    } finally {
      if ((acceptedResponse && isSettingsSessionCurrent()) || connectionIsCurrent()) setLoadingPrinters(false);
    }
  };

  useEffect(() => {
    if (!canSeeOperationalSettings) {
      setLoadingPrinters(false);
      setCibdStatus({ status: 'idle', message: '' });
      return;
    }

    fetchCibdPrinters({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeOperationalSettings]);

  useEffect(() => () => {
    if (openingHoursRefreshTimeoutRef.current) {
      window.clearTimeout(openingHoursRefreshTimeoutRef.current);
    }
  }, []);

  const handleSave = (section) => {
    if (!isSettingsSessionCurrent()) return;
    if (section === 'operação' && !writeTenantLocalJson(generalStorageKey, generalSettings, tenantSession)) {
      setToast({ open: true, message: 'Não foi possível salvar as preferências neste navegador.', severity: 'error' });
      return;
    }
    setToast({
      open: true,
      message: `Preferências de ${section} salvas`,
      severity: 'success',
    });
  };

  const handleSavePrintSettings = async () => {
    if (!isSettingsSessionCurrent()) return;
    const printPoints = normalizePrintPoints(printerSettings);
    const invalidPoint = printPoints.find((point) => !point.name || !point.printerId);
    if (invalidPoint) {
      setToast({
        open: true,
        message: `Informe uma impressora para o ponto ${invalidPoint.name || 'de impressão'}.`,
        severity: 'warning',
      });
      return;
    }
    setPrintRegistryStatus((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/salon/print-points`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ points: toPrintPointRegistryPayload({ printPoints }) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isSettingsSessionCurrent()) return;
      if (!response.ok) throw new Error(data.error || 'Falha ao sincronizar os pontos de impressão.');
      const nextSettings = mergeServerPrintPoints({
        ...printerSettings,
        printPoints,
        printerId: printPoints[0]?.printerId || printerSettings.printerId,
      }, data.points || []);
      if (!writeTenantPrinterSettings(nextSettings, tenantSession)) {
        throw new Error('Não foi possível salvar a impressora desta loja neste navegador.');
      }
      if (!writeCibdStationProfile({
        baseUrl: SYSTEM_PRINT_BASE_URL,
        token: nextSettings.cibdToken,
        printers: detectedPrinters,
        defaultPrinter: nextSettings.printerId,
        bindings: Object.fromEntries(normalizePrintPoints(nextSettings)
          .filter((point) => point.printerId)
          .map((point) => [point.id, point.printerId])),
        mode: 'explicit',
        session: tenantSession,
      })) {
        throw new Error('Não foi possível salvar a conexão do CIBD nesta estação.');
      }
      setPrinterSettings(nextSettings);
      setPrintRegistryStatus({ loading: false, error: '', synced: true });
      handleSave('impressão');
    } catch (err) {
      if (!isSettingsSessionCurrent()) return;
      try {
        const currentResponse = await apiFetch(`${API_URL}/api/salon/print-points`);
        const currentData = await currentResponse.json().catch(() => ({}));
        if (!isSettingsSessionCurrent()) return;
        if (currentResponse.ok && Array.isArray(currentData.points)) {
          setPrinterSettings((prev) => mergeServerPrintPoints(prev, currentData.points));
        }
      } catch {
        // Keep the local printer bindings available when the registry is offline.
      }
      setPrintRegistryStatus({ loading: false, error: err.message || 'Não foi possível sincronizar os pontos de impressão.', synced: false });
      setToast({
        open: true,
        message: err.message || 'Não foi possível sincronizar os pontos de impressão.',
        severity: 'error',
      });
    }
  };

  const handleCompanyContractChange = (key) => (event) => {
    setCompanySettings((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleCompanyAddressChange = (key) => (event) => {
    setCompanySettings((prev) => ({
      ...prev,
      address: {
        ...(prev.address || {}),
        [key]: event.target.value,
      },
    }));
  };

  const handleContractTemplateChange = (key) => (event) => {
    setContractTemplate((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSaveCompanySettings = async () => {
    if (!canManageContractSettings) return;
    setCompanySettingsSaving(true);
    setCompanySettingsStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companySettings),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar dados formais da empresa.');
      }
      setCompanySettings({ ...defaultCompanyContractSettings, ...(data.company || {}) });
      setToast({ open: true, message: 'Dados formais da empresa salvos.', severity: 'success' });
    } catch (err) {
      setCompanySettingsStatus((prev) => ({ ...prev, error: err.message || 'Falha ao salvar dados formais da empresa.' }));
      setToast({ open: true, message: err.message || 'Falha ao salvar dados formais da empresa.', severity: 'error' });
    } finally {
      setCompanySettingsSaving(false);
    }
  };

  const handleSaveContractTemplate = async () => {
    if (!canManageContractSettings) return;
    setContractTemplateSaving(true);
    setContractTemplateStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/contracts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: contractTemplate.name,
          body: contractTemplate.body,
          defaultScale: contractTemplate.defaultScale,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar modelo de contrato.');
      }
      setContractTemplate({ ...defaultContractTemplateSettings, ...(data.template || {}) });
      setContractPreview(data.preview || '');
      setToast({ open: true, message: 'Modelo de contrato salvo.', severity: 'success' });
    } catch (err) {
      setContractTemplateStatus((prev) => ({ ...prev, error: err.message || 'Falha ao salvar modelo de contrato.' }));
      setToast({ open: true, message: err.message || 'Falha ao salvar modelo de contrato.', severity: 'error' });
    } finally {
      setContractTemplateSaving(false);
    }
  };

  const handleResetContractTemplate = async () => {
    if (!canManageContractSettings) return;
    if (!window.confirm('Restaurar o modelo padrão de contrato? O texto atual será substituído.')) {
      return;
    }
    setContractTemplateResetting(true);
    setContractTemplateStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/contracts/reset-default`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ defaultScale: contractTemplate.defaultScale }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao restaurar modelo padrão.');
      }
      setContractTemplate({ ...defaultContractTemplateSettings, ...(data.template || {}) });
      setContractPreview(data.preview || '');
      setToast({ open: true, message: 'Modelo padrão restaurado.', severity: 'success' });
    } catch (err) {
      setContractTemplateStatus((prev) => ({ ...prev, error: err.message || 'Falha ao restaurar modelo padrão.' }));
      setToast({ open: true, message: err.message || 'Falha ao restaurar modelo padrão.', severity: 'error' });
    } finally {
      setContractTemplateResetting(false);
    }
  };

  const handleSaveUser = async () => {
    const login = String(userForm.login || '').trim();
    const password = String(userForm.password || '');
    const editingUser = users.find((item) => item.id === userDialog.userId);
    const isEdit = userDialog.mode === 'edit' && editingUser;
    const isSelfEdit = Number(editingUser?.id) === Number(currentUser?.id);
    const selectedRole = canAdministerUsers && !isSelfEdit
      ? userForm.role
      : (editingUser?.role || currentUser?.role || USER_ROLES.COLABORADOR);

    if (!login) {
      setToast({ open: true, message: 'Informe um login.', severity: 'warning' });
      return;
    }

    if (!isEdit && !password) {
      setToast({ open: true, message: 'Informe uma senha.', severity: 'warning' });
      return;
    }

    setUserSaving(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/auth/users/${editingUser.id}`
        : `${API_URL}/api/auth/users`;
      const method = isEdit ? 'PUT' : 'POST';
      const payload = {
        login,
        role: selectedRole,
      };
      if (password) {
        payload.password = password;
      }
      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar usuário.');
      }

      const savedUser = data.user;
      setUsers((prev) => {
        if (isEdit) {
          return prev.map((item) => (item.id === savedUser.id ? savedUser : item));
        }
        return [...prev, savedUser].sort((a, b) => String(a.login || '').localeCompare(String(b.login || ''), 'pt-BR'));
      });

      if (Number(savedUser.id) === Number(currentUser?.id)) {
        auth.updateUser(savedUser);
      }

      if (canAdministerUsers) {
        const summaryResponse = await apiFetch(`${API_URL}/api/auth/security-summary`);
        const summaryData = await summaryResponse.json().catch(() => ({}));
        if (summaryResponse.ok) {
          setUserSecuritySummary(summaryData);
        }
      }

      closeUserDialog();
      setToast({
        open: true,
        message: isEdit ? 'Usuário atualizado com sucesso.' : 'Usuário criado com sucesso.',
        severity: 'success',
      });
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao salvar usuário.', severity: 'error' });
    } finally {
      setUserSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!user) return;
    if (typeof window !== 'undefined' && !window.confirm(`Excluir o usuário ${user.login}?`)) {
      return;
    }

    setDeleteUserId(user.id);
    try {
      const response = await apiFetch(`${API_URL}/api/auth/users/${user.id}`, {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao excluir usuário.');
      }

      setUsers((prev) => prev.filter((item) => item.id !== user.id));
      if (Number(user.id) === Number(currentUser?.id)) {
        auth.logout();
        return;
      }
      setToast({ open: true, message: 'Usuário excluído com sucesso.', severity: 'success' });
      if (canAdministerUsers) {
        const summaryResponse = await apiFetch(`${API_URL}/api/auth/security-summary`);
        const summaryData = await summaryResponse.json().catch(() => ({}));
        if (summaryResponse.ok) {
          setUserSecuritySummary(summaryData);
        }
      }
    } catch (err) {
      setToast({ open: true, message: err.message || 'Falha ao excluir usuário.', severity: 'error' });
    } finally {
      setDeleteUserId(null);
    }
  };

  const handleSaveIfood = async () => {
    setIfoodSaving(true);
    setIfoodStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/ifood`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationEnabled: Boolean(ifoodSettings.integrationEnabled),
          widgetId: ifoodSettings.widgetId,
          merchantIds: ifoodSettings.merchantIds,
          scheduleMerchantId: ifoodSettings.scheduleMerchantId,
          pollingEnabled: Boolean(ifoodSettings.pollingEnabled),
          webhookEnabled: Boolean(ifoodSettings.webhookEnabled),
          autoAcceptEnabled: ifoodSettings.autoAcceptEnabled !== false,
          shippingEnabled: true,
          shippingMerchantIds: ifoodSettings.shippingMerchantIds,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar integração do iFood.');
      }
      setIfoodSettings({
        integrationEnabled: data.integrationEnabled !== false,
        clientId: data.clientId || '',
        clientSecret: data.clientSecret || '',
        widgetId: data.widgetId || '',
        merchantIds: Array.isArray(data.merchantIds) ? data.merchantIds : [],
        scheduleMerchantId: data.scheduleMerchantId || '',
        detectedMerchantIds: Array.isArray(data.detectedMerchantIds) ? data.detectedMerchantIds : [],
        pollingEnabled: data.pollingEnabled !== false,
        webhookEnabled: Boolean(data.webhookEnabled),
        autoAcceptEnabled: data.autoAcceptEnabled !== false,
        shippingEnabled: data.shippingEnabled === true,
        shippingMerchantIds: Array.isArray(data.shippingMerchantIds) ? data.shippingMerchantIds : [],
        lastTransportFailure: data.lastTransportFailure || null,
        credentialsConfigured: Boolean(data.credentialsConfigured),
        credentials: data.credentials || {},
      });
      setIfoodStatus((prev) => ({ ...prev, updatedAt: data.updatedAt || null }));
      await loadMerchantPanel(data.scheduleMerchantId || (Array.isArray(data.merchantIds) ? data.merchantIds[0] : '') || '');
      setToast({
        open: true,
        message: 'Integração iFood salva.',
        severity: 'success',
      });
    } catch (err) {
      setIfoodStatus((prev) => ({ ...prev, error: err.message || 'Falha ao salvar integração do iFood.' }));
      setToast({
        open: true,
        message: `Erro ao salvar iFood: ${err.message || 'Falha ao salvar integração.'}`,
        severity: 'error',
      });
    } finally {
      setIfoodSaving(false);
    }
  };

  const runCatalogAdminAction = async ({
    endpoint,
    method = 'POST',
    actionKey,
    confirmMessage,
    successMessage,
  }) => {
    if (!isSettingsSessionCurrent()) return;
    setCatalogAdminSummary(null);
    setToast({ open: false, message: '', severity: 'success' });
    const merchantId = selectedCatalogImportMerchantId;
    if (!merchantId) {
      setToast({
        open: true,
        message: 'Selecione uma loja iFood antes de importar ou limpar produtos.',
        severity: 'warning',
      });
      return;
    }

    if (confirmMessage && typeof window !== 'undefined' && !window.confirm(confirmMessage)) {
      return;
    }

    const requestId = ++catalogAdminRequestRef.current;
    const isCurrentRequest = () => isSettingsSessionCurrent()
      && catalogAdminRequestRef.current === requestId
      && catalogImportMerchantRef.current === merchantId;
    if (!isCurrentRequest()) return;
    setCatalogAdminAction(actionKey);
    try {
      let confirmationDigest = '';
      if (actionKey === 'import-full') {
        const previewResponse = await apiFetch(`${API_URL}/api/products/admin/import-ifood/full/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ merchantId }),
        });
        const previewData = await previewResponse.json().catch(() => ({}));
        if (!isCurrentRequest()) return;
        if (!previewResponse.ok || previewData.success !== true) {
          throw new Error(previewData.error || 'Falha ao revisar o impacto da importação.');
        }
        const impact = previewData.preview || {};
        if (impact.requiresConfirmation) {
          const impactMessage = [
            `A importação removerá do catálogo operacional ${Number(impact.wouldDeactivateProductCount || 0)} produto(s)`,
            `e ${Number(impact.wouldDeactivateGroupCount || 0)} grupo(s) desta loja.`,
            impact.highRisk ? 'O impacto é elevado.' : '',
            'Confirma a aplicação desse resultado?',
          ].filter(Boolean).join(' ');
          if (typeof window !== 'undefined' && !window.confirm(impactMessage)) return;
          confirmationDigest = String(impact.digest || '');
        }
      }
      if (!isCurrentRequest()) return;
      const response = await apiFetch(`${API_URL}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchantId, ...(confirmationDigest ? { confirmationDigest } : {}) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isCurrentRequest()) return;
      if (!response.ok || data.success !== true) {
        throw new Error(data.error || `Falha ao executar ${actionKey}.`);
      }

      setCatalogAdminSummary(data);
      setToast({
        open: true,
        message: successMessage,
        severity: 'success',
      });
    } catch (err) {
      if (!isCurrentRequest()) return;
      setCatalogAdminSummary(null);
      setToast({
        open: true,
        message: err.message || `Falha ao executar ${actionKey}.`,
        severity: 'error',
      });
    } finally {
      if (isCurrentRequest()) setCatalogAdminAction('');
    }
  };

  const reviewDeleteAllProducts = async () => {
    setCatalogAdminAction('delete-all-products-preview');
    try {
      const response = await apiFetch(`${API_URL}/api/products/admin/delete-all-products/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Falha ao revisar os produtos do catálogo.');
      }
      if (!isSettingsSessionCurrent()) return;
      const preview = normalizeDeleteAllProductsPreview(data.preview);
      if (!preview.canDelete) {
        setCatalogAdminSummary({
          operation: 'delete-all-products',
          message: 'Não há produtos ativos para remover do catálogo operacional.',
          removedCount: 0,
          deactivatedCount: 0,
        });
        setToast({
          open: true,
          message: 'Não há produtos ativos para deletar.',
          severity: 'info',
        });
        return;
      }
      setDeleteAllProductsDialog({
        open: true,
        applying: false,
        preview,
        confirmation: '',
        error: '',
      });
    } catch (err) {
      if (!isSettingsSessionCurrent()) return;
      setToast({
        open: true,
        message: err.message || 'Falha ao revisar os produtos do catálogo.',
        severity: 'error',
      });
    } finally {
      if (isSettingsSessionCurrent()) setCatalogAdminAction('');
    }
  };

  const closeDeleteAllProductsDialog = () => {
    if (deleteAllProductsDialog.applying) return;
    setDeleteAllProductsDialog(emptyDeleteAllProductsDialog());
  };

  const confirmDeleteAllProducts = async () => {
    const preview = deleteAllProductsDialog.preview;
    if (
      !preview?.canDelete
      || !isDeleteAllProductsConfirmationValid(deleteAllProductsDialog.confirmation)
    ) return;

    setCatalogAdminAction('delete-all-products');
    setDeleteAllProductsDialog((current) => ({ ...current, applying: true, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/products/admin/delete-all-products`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmationPhrase: deleteAllProductsDialog.confirmation,
          confirmationDigest: preview.digest,
          expectedProductCount: preview.activeProductCount,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        const error = new Error(data.error || 'Falha ao deletar todos os produtos.');
        error.preview = data.details?.preview || null;
        throw error;
      }
      if (!isSettingsSessionCurrent()) return;

      invalidateOperationalCatalogLoader({ purge: true });
      invalidatePedidosSnapshots();
      setCatalogAdminSummary(data);
      setDeleteAllProductsDialog(emptyDeleteAllProductsDialog());
      setToast({
        open: true,
        message: data.message || 'Todos os produtos foram removidos do catálogo operacional.',
        severity: 'success',
      });
    } catch (err) {
      if (!isSettingsSessionCurrent()) return;
      setDeleteAllProductsDialog((current) => ({
        ...current,
        applying: false,
        preview: err.preview
          ? normalizeDeleteAllProductsPreview(err.preview)
          : current.preview,
        confirmation: '',
        error: err.message || 'Falha ao deletar todos os produtos.',
      }));
    } finally {
      if (isSettingsSessionCurrent()) setCatalogAdminAction('');
    }
  };

  const copyToClipboard = async (value, label) => {
    if (!value) return;
    let copied = false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        copied = true;
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', 'true');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch {
      copied = false;
    }

    setToast({
      open: true,
      message: copied ? `${label} copiado.` : `Não foi possível copiar ${label}.`,
      severity: copied ? 'success' : 'error',
    });
  };

  const buildCopyAdornment = (value, label, toggle) => (
    <InputAdornment position="end">
      <Stack direction="row" spacing={0.5} alignItems="center">
        {toggle}
        <Tooltip title={`Copiar ${label}`}>
          <span>
            <IconButton size="small" onClick={() => copyToClipboard(value, label)} disabled={!value}>
              <ContentCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </InputAdornment>
  );

  const handleTestPrint = (point = null) => {
    if (!isSettingsSessionCurrent()) return;
    const tenantSettings = readTenantLocalJson(printerStorageKey, {}, tenantSession);
    const connection = resolveCibdStationConnection({
      baseUrl: SYSTEM_PRINT_BASE_URL,
      session: tenantSession,
      tenantSettings,
    });
    if (!connection) return;
    const printerId = point?.printerId || printerSettings.printerId || '';
    if (!printerId) {
      setToast({
        open: true,
        message: 'Selecione uma impressora para testar.',
        severity: 'warning',
      });
      return;
    }
    fetch(`${connection.baseUrl}/api/system/print/test`, {
      method: 'POST',
      headers: buildCibdHeaders({ cibdToken: connection.token }),
      credentials: 'omit',
      body: JSON.stringify({ printerId }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Falha ao testar impressora.');
        }
        return response.json();
      })
      .then(() => {
        if (!isSettingsSessionCurrent()
          || !isCibdStationConnectionCurrent(connection, {
            baseUrl: SYSTEM_PRINT_BASE_URL,
            session: tenantSession,
            tenantSettings,
          })) return;
        setToast({
          open: true,
          message: `Teste enviado para ${point?.name || printerId}.`,
          severity: 'success',
        });
      })
      .catch((err) => {
        if (!isSettingsSessionCurrent()
          || !isCibdStationConnectionCurrent(connection, {
            baseUrl: SYSTEM_PRINT_BASE_URL,
            session: tenantSession,
            tenantSettings,
          })) return;
        setToast({
          open: true,
          message: `Erro no teste de impressão: ${err.message}`,
          severity: 'error',
        });
      });
  };

  const handleCloseToast = (_, reason) => {
    if (reason === 'clickaway') return;
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleFoodyChange = (field) => (event) => {
    const value = event?.target?.type === 'checkbox'
      ? event.target.checked
      : event?.target?.value ?? '';
    setFoodySettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleEntregasZapChange = (field) => (event) => {
    const value = event?.target?.type === 'checkbox'
      ? event.target.checked
      : event?.target?.value ?? '';
    setEntregasZapSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handlePedidos10Change = (field) => (event) => {
    const value = event?.target?.type === 'checkbox'
      ? event.target.checked
      : event?.target?.value ?? '';
    setPedidos10Settings((prev) => ({ ...prev, [field]: value }));
  };

  const handleBrutosZapChange = (field) => (event) => {
    const value = event?.target?.type === 'checkbox'
      ? event.target.checked
      : event?.target?.value ?? '';
    setBrutosZap((prev) => ({ ...prev, [field]: value }));
  };

  const handleSiteWebhookChange = (field) => (event) => {
    const value = event?.target?.type === 'checkbox'
      ? event.target.checked
      : event?.target?.value ?? '';
    setSiteWebhook((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSiteWebhook = async () => {
    setSiteWebhookSaving(true);
    setSiteWebhookStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/site-webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteEnabled: Boolean(siteWebhook.siteEnabled),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar integração do site/cardápio.');
      }
      setSiteWebhook((prev) => ({
        ...prev,
        siteEnabled: data.siteEnabled !== false,
        webhookUrl: data.webhookUrl || prev.webhookUrl,
        brutosWebhookUrl: data.brutosWebhookUrl || data.webhookUrl || prev.brutosWebhookUrl,
        tokenBearer: data.tokenBearer || prev.tokenBearer,
        webhookSecret: data.webhookSecret || prev.webhookSecret,
      }));
      setSiteWebhookStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
      setToast({ open: true, message: 'Integração do site/cardápio salva.', severity: 'success' });
    } catch (err) {
      setSiteWebhookStatus((prev) => ({
        ...prev,
        error: err.message || 'Falha ao salvar integração do site/cardápio.',
      }));
      setToast({ open: true, message: 'Erro ao salvar integração do site/cardápio.', severity: 'error' });
    } finally {
      setSiteWebhookSaving(false);
    }
  };

  const mergeBrutosZapResponse = (data = {}, keepClearSecrets = false) => {
    setBrutosZap((prev) => ({
      ...prev,
      enabled: data.enabled !== false,
      webhookUrl: data.webhookUrl || prev.webhookUrl,
      pingUrl: data.pingUrl || prev.pingUrl,
      tokenBearer: keepClearSecrets ? data.tokenBearer || prev.tokenBearer : '',
      webhookSecret: keepClearSecrets ? data.webhookSecret || prev.webhookSecret : '',
      tokenPreview: data.tokenPreview || prev.tokenPreview,
      secretPreview: data.secretPreview || prev.secretPreview,
      hasToken: data.hasToken !== undefined ? Boolean(data.hasToken) : prev.hasToken,
      hasSecret: data.hasSecret !== undefined ? Boolean(data.hasSecret) : prev.hasSecret,
      statusCallbackUrl: data.statusCallbackUrl ?? prev.statusCallbackUrl,
      lastOrderReceivedAt: data.lastOrderReceivedAt || prev.lastOrderReceivedAt,
      lastStatusCallbackAt: data.lastStatusCallbackAt || prev.lastStatusCallbackAt,
      lastStatusCallbackError: data.lastStatusCallbackError || '',
      updatedAt: data.updatedAt || prev.updatedAt,
    }));
    setBrutosZapStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
  };

  const handleSaveBrutosZap = async () => {
    setBrutosZapSaving(true);
    setBrutosZapStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/brutos-zap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: Boolean(brutosZap.enabled),
          statusCallbackUrl: brutosZap.statusCallbackUrl,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar integração Brutos Zap.');
      }
      mergeBrutosZapResponse(data);
      setToast({ open: true, message: 'Integração Brutos Zap salva.', severity: 'success' });
    } catch (err) {
      setBrutosZapStatus((prev) => ({
        ...prev,
        error: err.message || 'Falha ao salvar integração Brutos Zap.',
      }));
      setToast({ open: true, message: 'Erro ao salvar integração Brutos Zap.', severity: 'error' });
    } finally {
      setBrutosZapSaving(false);
    }
  };

  const handleRegenerateBrutosZapSecrets = async () => {
    if (!window.confirm('Regenerar token e secret do Brutos Zap? A configuração atual no Brutos Zap precisará ser atualizada.')) {
      return;
    }
    setBrutosZapSaving(true);
    setBrutosZapStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/brutos-zap/regenerate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regenerateToken: true, regenerateSecret: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao regenerar credenciais Brutos Zap.');
      }
      mergeBrutosZapResponse(data, true);
      setShowBrutosZapToken(true);
      setShowBrutosZapSecret(true);
      setToast({ open: true, message: 'Credenciais Brutos Zap regeneradas. Copie os valores agora.', severity: 'success' });
    } catch (err) {
      setBrutosZapStatus((prev) => ({
        ...prev,
        error: err.message || 'Falha ao regenerar credenciais Brutos Zap.',
      }));
      setToast({ open: true, message: 'Erro ao regenerar credenciais Brutos Zap.', severity: 'error' });
    } finally {
      setBrutosZapSaving(false);
    }
  };

  const handleTestBrutosZapCallback = async () => {
    setBrutosZapTesting(true);
    setBrutosZapStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/brutos-zap/test-callback`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao testar callback Brutos Zap.');
      }
      setToast({ open: true, message: 'Callback do Brutos Zap respondeu com sucesso.', severity: 'success' });
    } catch (err) {
      setBrutosZapStatus((prev) => ({
        ...prev,
        error: err.message || 'Falha ao testar callback Brutos Zap.',
      }));
      setToast({ open: true, message: 'Erro ao testar callback Brutos Zap.', severity: 'error' });
    } finally {
      setBrutosZapTesting(false);
    }
  };

  const handleSaveFoody = async () => {
    setFoodySaving(true);
    try {
      const response = await apiFetch(`${API_URL}/api/settings/foody`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationEnabled: Boolean(foodySettings.integrationEnabled),
          apiToken: foodySettings.apiToken,
          partnerToken: foodySettings.partnerToken,
          webhookToken: foodySettings.webhookToken,
        }),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Falha ao salvar integração do Foody Delivery.');
      }
      const data = await response.json();
      setFoodySettings((prev) => ({
        ...prev,
        integrationEnabled: data.integrationEnabled !== false,
        apiToken: data.apiToken || prev.apiToken,
        partnerToken: data.partnerToken || prev.partnerToken,
        webhookToken: data.webhookToken || prev.webhookToken,
        webhookUrl: data.webhookUrl || prev.webhookUrl,
      }));
      setFoodyStatus({ loading: false, error: '', updatedAt: data.updatedAt || null, lastAttempt: data.lastAttempt || null });
      setToast({ open: true, message: 'Integração Foody Delivery salva.', severity: 'success' });
    } catch (err) {
      setFoodyStatus((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Falha ao salvar integração do Foody Delivery.',
      }));
      setToast({ open: true, message: 'Erro ao salvar integração Foody Delivery.', severity: 'error' });
    } finally {
      setFoodySaving(false);
    }
  };

  const handleSaveEntregasZap = async () => {
    setEntregasZapSaving(true);
    setEntregasZapStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/entregas-zap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationEnabled: Boolean(entregasZapSettings.integrationEnabled),
          webhookUrl: entregasZapSettings.webhookUrl,
          apiBaseUrl: entregasZapSettings.apiBaseUrl,
          entregasZapToken: entregasZapSettings.entregasZapToken,
          brutosToken: entregasZapSettings.brutosToken,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar integração com Brutos Entregas.');
      }
      setEntregasZapSettings((prev) => ({
        ...prev,
        webhookUrl: data.webhookUrl || prev.webhookUrl,
        integrationEnabled: data.integrationEnabled !== false,
        apiBaseUrl: data.apiBaseUrl || prev.apiBaseUrl,
        entregasZapToken: data.entregasZapToken || data.integrationToken || prev.entregasZapToken,
        brutosToken: data.brutosToken || prev.brutosToken,
        brutosApiBaseUrl: data.brutosApiBaseUrl || prev.brutosApiBaseUrl,
        callbackBaseUrl: data.callbackBaseUrl || prev.callbackBaseUrl,
      }));
      setEntregasZapStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
      setToast({ open: true, message: 'Integração Brutos Entregas salva.', severity: 'success' });
    } catch (err) {
      setEntregasZapStatus((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Falha ao salvar integração com Brutos Entregas.',
      }));
      setToast({ open: true, message: 'Erro ao salvar integração Brutos Entregas.', severity: 'error' });
    } finally {
      setEntregasZapSaving(false);
    }
  };

  const handleRegenerateBrutosToken = async () => {
    setEntregasZapSaving(true);
    setEntregasZapStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/entregas-zap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationEnabled: Boolean(entregasZapSettings.integrationEnabled),
          webhookUrl: entregasZapSettings.webhookUrl,
          apiBaseUrl: entregasZapSettings.apiBaseUrl,
          entregasZapToken: entregasZapSettings.entregasZapToken,
          regenerateBrutosToken: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao gerar chave Brutos Delivery.');
      }
      setEntregasZapSettings((prev) => ({
        ...prev,
        webhookUrl: data.webhookUrl || prev.webhookUrl,
        integrationEnabled: data.integrationEnabled !== false,
        apiBaseUrl: data.apiBaseUrl || prev.apiBaseUrl,
        entregasZapToken: data.entregasZapToken || data.integrationToken || prev.entregasZapToken,
        brutosToken: data.brutosToken || prev.brutosToken,
        brutosApiBaseUrl: data.brutosApiBaseUrl || prev.brutosApiBaseUrl,
        callbackBaseUrl: data.callbackBaseUrl || prev.callbackBaseUrl,
      }));
      setEntregasZapStatus({ loading: false, error: '', updatedAt: data.updatedAt || null });
      setToast({ open: true, message: 'Nova chave Brutos Delivery gerada.', severity: 'success' });
    } catch (err) {
      setEntregasZapStatus((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Falha ao gerar chave Brutos Delivery.',
      }));
      setToast({ open: true, message: 'Erro ao gerar chave Brutos Delivery.', severity: 'error' });
    } finally {
      setEntregasZapSaving(false);
    }
  };

  const handleTestEntregasZapConnection = async () => {
    setEntregasZapTesting(true);
    setEntregasZapStatus((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/entregas-zap/test`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Falha ao testar conexão com o Brutos Entregas.');
      }
      setToast({ open: true, message: 'Conexão com Brutos Entregas validada.', severity: 'success' });
    } catch (err) {
      setEntregasZapStatus((prev) => ({
        ...prev,
        error: err.message || 'Falha ao testar conexão com o Brutos Entregas.',
      }));
      setToast({ open: true, message: 'Falha no teste de conexão Brutos Entregas.', severity: 'error' });
    } finally {
      setEntregasZapTesting(false);
    }
  };

  const refreshPedidos10FromResponse = (data = {}) => {
    setPedidos10Settings((prev) => ({
      ...prev,
      integrationEnabled: data.integrationEnabled !== undefined ? data.integrationEnabled !== false : prev.integrationEnabled,
      apiBaseUrl: data.apiBaseUrl || prev.apiBaseUrl,
      integrationToken: data.integrationToken || prev.integrationToken,
      username: data.username || prev.username,
      password: data.password || prev.password,
      accessToken: data.accessToken || prev.accessToken,
      merchantExternalCode: data.merchantExternalCode || prev.merchantExternalCode,
      webhookSecret: data.webhookSecret || prev.webhookSecret,
      webhookUrl: data.webhookUrl || prev.webhookUrl,
      webhookPingUrl: data.webhookPingUrl || prev.webhookPingUrl,
      webhookEnabled: data.webhookEnabled !== undefined ? data.webhookEnabled !== false : prev.webhookEnabled,
      pollingEnabled: data.pollingEnabled !== undefined ? Boolean(data.pollingEnabled) : prev.pollingEnabled,
      docsUrl: data.docsUrl || prev.docsUrl,
      apiDocsUrl: data.apiDocsUrl || prev.apiDocsUrl,
    }));
    setPedidos10Status((prev) => ({
      ...prev,
      loading: false,
      error: '',
      updatedAt: data.updatedAt || prev.updatedAt,
      lastAuthAt: data.lastAuthAt || prev.lastAuthAt,
      lastWebhookRegistrationAt: data.lastWebhookRegistrationAt || prev.lastWebhookRegistrationAt,
      lastSyncAt: data.lastSyncAt || prev.lastSyncAt,
    }));
  };

  const savePedidos10 = async (extraPayload = {}, successMessage = 'Integração Pedidos10 salva.') => {
    setPedidos10Saving(true);
    setPedidos10Status((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/pedidos10`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          integrationEnabled: Boolean(pedidos10Settings.integrationEnabled),
          apiBaseUrl: pedidos10Settings.apiBaseUrl,
          integrationToken: pedidos10Settings.integrationToken,
          username: pedidos10Settings.username,
          password: pedidos10Settings.password,
          accessToken: pedidos10Settings.accessToken,
          merchantExternalCode: pedidos10Settings.merchantExternalCode,
          webhookSecret: pedidos10Settings.webhookSecret,
          webhookEnabled: Boolean(pedidos10Settings.webhookEnabled),
          pollingEnabled: Boolean(pedidos10Settings.pollingEnabled),
          ...extraPayload,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao salvar integração Pedidos10.');
      }
      refreshPedidos10FromResponse(data);
      setToast({ open: true, message: successMessage, severity: 'success' });
      return data;
    } catch (err) {
      setPedidos10Status((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Falha ao salvar integração Pedidos10.',
      }));
      setToast({ open: true, message: 'Erro ao salvar integração Pedidos10.', severity: 'error' });
      return null;
    } finally {
      setPedidos10Saving(false);
    }
  };

  const handleSavePedidos10 = () => savePedidos10();

  const handleRegeneratePedidos10WebhookSecret = () => savePedidos10(
    { regenerateWebhookSecret: true },
    'Novo segredo do webhook Pedidos10 gerado.'
  );

  const handleRegeneratePedidos10MerchantCode = () => savePedidos10(
    { regenerateMerchantExternalCode: true },
    'Novo código externo do estabelecimento gerado.'
  );

  const handleTestPedidos10Connection = async () => {
    setPedidos10Testing(true);
    setPedidos10Status((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/pedidos10/test`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Falha ao testar conexão com o Pedidos10.');
      }
      setToast({ open: true, message: 'Conexão com Pedidos10 validada.', severity: 'success' });
    } catch (err) {
      setPedidos10Status((prev) => ({
        ...prev,
        error: err.message || 'Falha ao testar conexão com o Pedidos10.',
      }));
      setToast({ open: true, message: 'Falha no teste de conexão Pedidos10.', severity: 'error' });
    } finally {
      setPedidos10Testing(false);
    }
  };

  const handleRegisterPedidos10Webhook = async () => {
    setPedidos10Registering(true);
    setPedidos10Status((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/pedidos10/register-webhook`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Falha ao registrar webhook no Pedidos10.');
      }
      setPedidos10Status((prev) => ({
        ...prev,
        lastWebhookRegistrationAt: new Date().toISOString(),
      }));
      setToast({ open: true, message: 'Webhook do Pedidos10 registrado.', severity: 'success' });
    } catch (err) {
      setPedidos10Status((prev) => ({
        ...prev,
        error: err.message || 'Falha ao registrar webhook no Pedidos10.',
      }));
      setToast({ open: true, message: 'Falha ao registrar webhook Pedidos10.', severity: 'error' });
    } finally {
      setPedidos10Registering(false);
    }
  };

  const handleSyncPedidos10Orders = async () => {
    setPedidos10Syncing(true);
    setPedidos10Status((prev) => ({ ...prev, error: '' }));
    try {
      const response = await apiFetch(`${API_URL}/api/settings/pedidos10/sync`, {
        method: 'POST',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'Falha ao sincronizar pedidos do Pedidos10.');
      }
      setPedidos10Status((prev) => ({
        ...prev,
        lastSyncAt: new Date().toISOString(),
      }));
      setToast({
        open: true,
        message: `Pedidos10 sincronizado: ${data.processed || 0} pedido(s).`,
        severity: 'success',
      });
    } catch (err) {
      setPedidos10Status((prev) => ({
        ...prev,
        error: err.message || 'Falha ao sincronizar pedidos do Pedidos10.',
      }));
      setToast({ open: true, message: 'Falha ao sincronizar Pedidos10.', severity: 'error' });
    } finally {
      setPedidos10Syncing(false);
    }
  };


  const sampleComanda = useMemo(() => ({
    orderNumber: '#1452',
    createdAt: '21/04/2024 19:32',
    salesChannel: 'iFood',
    merchant: 'Brutos Delivery',
    customer: 'Marina Oliveira',
    customerDocument: '123.456.789-00',
    customerPhone: '0800 000 0000',
    localizer: '12345678',
    address: 'Rua das Palmeiras, 112 - Centro',
    deliveryMode: 'Entrega',
    pickupCode: 'A7F2',
    items: [
      { name: 'Burger Brutos', qty: 1, price: '29,90', options: [{ name: 'Borda cheddar', qty: 1, price: '5,00' }] },
      { name: 'Batata cheddar', qty: 1, price: '16,90' },
      { name: 'Refri lata', qty: 2, price: '7,00' },
    ],
    notes: 'Sem cebola no burger. Entregar na portaria.',
    subtotal: '60,80',
    deliveryFee: '5,00',
    discounts: '4,00',
    additionalFees: '2,00',
    total: '65,80',
    payment: 'Online',
    paymentPrepaid: '65,80',
    paymentPending: '0,00',
    paymentDetails: 'Visa - 1x',
    deliveryTime: '20 min',
  }), []);

  const comandaPreviewStyles = useMemo(() => {
    const fontMap = {
      courier: '"Courier New", Courier, monospace',
      helvetica: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      times: '"Times New Roman", Times, serif',
    };
    return {
      fontFamily: fontMap[printerSettings.comandaFontFamily] || fontMap.courier,
      color: '#0f172a',
      lineHeight: 1.35,
    };
  }, [printerSettings.comandaFontFamily]);

  return (
    <Box className="configuracoes-page">
      <Box className="configuracoes-shell">
        <Card className="configuracoes-hero configuracoes-card">
          <CardContent sx={{ position: 'relative', zIndex: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PageMenuButton />
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
                  Central de Configurações
                </Typography>
              </Stack>
              {!canSeeOperationalSettings ? (
                <Typography variant="body2" sx={{ maxWidth: 280, textAlign: 'right', opacity: 0.9 }}>
                  Aqui você pode atualizar somente o seu login e a sua senha.
                </Typography>
              ) : null}
            </Stack>
            <Tabs
              value={tab}
              onChange={(_, newValue) => setTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              className="configuracoes-tabs"
              sx={{
                mt: 1.5,
                maxWidth: '100%',
                '.MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.28 },
              }}
            >
              {availableTabs.map((item) => (
                <Tab key={item.id} value={item.id} label={item.label} />
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {tab === 'integracoes' && (canSeeAdvancedConfig || canManageBeeDelivery) && (
          <Card className="configuracoes-card configuracoes-section">
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Integrações</Typography>
                  <Typography variant="h6">Canais e serviços conectados</Typography>
                </Box>
                <Tabs
                  value={integrationTab}
                  onChange={(_, newValue) => setIntegrationTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ maxWidth: '100%' }}
                >
                  {availableIntegrationTabs.map((item) => (
                    <Tab key={item.id} value={item.id} label={item.label} />
                  ))}
                </Tabs>
              </Stack>
            </CardContent>
          </Card>
        )}

        {tab === 'integracoes' && integrationTab === 'bee' && canManageBeeDelivery && (
          <BeeDeliverySettingsPanel apiUrl={API_URL} sessionToken={auth.token} canManageHomologation={isProgrammer(currentUser)} />
        )}

        {tab === 'integracoes' && integrationTab === 'fiscal' && canSeeFiscalIntegration && fiscalFeature.available && (
          <FiscalSettingsPanel
            apiUrl={API_URL}
            initialSummary={fiscalFeature.summary}
            companySettings={companySettings}
            onSummaryChange={(summary) => {
              const available = isFiscalSummaryActive(summary);
              setFiscalFeature({ loading: false, available, summary: available ? summary : null });
            }}
          />
        )}

        {tab === 'entrega-preparo' && canSeeOperationalSettings && (
          <DeliveryPricingPanel
            apiUrl={API_URL}
            onManagementChange={setIntegrationManagement}
          />
        )}

        {tab === 'auditoria-pedidos' && isAdminLike(currentUser) && (
          <OrderAmendmentAuditPanel apiUrl={API_URL} session={tenantSession} />
        )}

        {tab === 'plano' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Plano atual</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 850 }}>{planView.label}</Typography>
                    </Stack>
                    {planSummary.loading ? (
                      <CircularProgress size={22} />
                    ) : (
                      <Chip label={planView.code} color={planView.code === 'FREE' ? 'default' : 'success'} />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  {planSummary.error ? (
                    <Alert severity="error">{planSummary.error}</Alert>
                  ) : (
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`Faturamento: ${planView.monthlyRevenue}`} variant="outlined" />
                        <Chip
                          label={planView.fiscalEnabled ? 'Fiscal liberado' : 'Fiscal bloqueado'}
                          color={planView.fiscalEnabled ? 'success' : 'warning'}
                          variant="outlined"
                        />
                        <Chip label={`Suporte: ${planView.supportLabel}`} variant="outlined" />
                      </Stack>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          Limite de entregas: {planView.deliveryLimit ? `${planView.deliveryLimit} por dia` : 'sem limite operacional no Brutos Delivery'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Integrações totais: {planView.fullIntegrations ? 'incluídas' : 'não incluídas'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Análise iFood: {planView.ifoodAnalysisIncluded ? '1 análise incluída' : 'não incluída'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Taxa de instalação: {planView.installationFee}
                        </Typography>
                      </Stack>
                      {planView.code === 'FREE' ? (
                        <Alert severity="warning">
                          No plano Free, emissão fiscal fica bloqueada no backend e o suporte é por tutoriais de autoajuda.
                        </Alert>
                      ) : null}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                          variant="contained"
                          href={`mailto:suporte@brutosdelivery.cloud?subject=${encodeURIComponent(`Upgrade Brutos Delivery - ${planView.code}`)}`}
                        >
                          Solicitar upgrade
                        </Button>
                        <Button variant="outlined" disabled={!planView.fiscalEnabled}>
                          Fiscal {planView.fiscalEnabled ? 'disponível' : 'bloqueado'}
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {PLAN_UPGRADE_OPTIONS.map((option) => (
              <Grid key={option.code} size={{ xs: 12, md: 3.5 }}>
                <Card className="configuracoes-card">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h6" sx={{ fontWeight: 850 }}>{option.title}</Typography>
                        <Chip label={option.price} color={option.code === 'ENTERPRISE' ? 'primary' : 'default'} />
                      </Stack>
                      <Stack spacing={1}>
                        {option.highlights.map((highlight) => (
                          <Typography key={highlight} variant="body2" color="text.secondary">
                            {highlight}
                          </Typography>
                        ))}
                      </Stack>
                      <Button
                        variant={option.code === 'ENTERPRISE' ? 'contained' : 'outlined'}
                        href={`mailto:suporte@brutosdelivery.cloud?subject=${encodeURIComponent(`Upgrade para ${option.title}`)}`}
                      >
                        Quero {option.title}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {tab === 'geral' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 8 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Preferências rápidas</Typography>
                      <Typography variant="h6">Notificações e operação</Typography>
                    </Stack>
                    <IconButton color="primary">
                      <SettingsSuggestIcon />
                    </IconButton>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1.5}>
                    <Stack direction="row" flexWrap="wrap" spacing={2}>
                      <FormControlLabel
                        control={<Switch checked={generalSettings.notificacoes} onChange={handleGeneralToggle('notificacoes')} />}
                        label="Notificar novos pedidos"
                      />
                      <FormControlLabel
                        control={<Switch checked={generalSettings.vibrarNovosPedidos} onChange={handleGeneralToggle('vibrarNovosPedidos')} />}
                        label="Vibrar alertas sonoros"
                      />
                      <FormControlLabel
                        control={<Switch checked={generalSettings.somAlerta} onChange={handleGeneralToggle('somAlerta')} />}
                        label="Som de urgência"
                      />
                      <FormControlLabel
                        control={<Switch checked={generalSettings.somNovoPedido !== false} onChange={handleGeneralToggle('somNovoPedido')} />}
                        label="Som de novo pedido"
                      />
                      <FormControlLabel
                        control={<Switch checked={generalSettings.exibirKds} onChange={handleGeneralToggle('exibirKds')} />}
                        label="Exibir pedidos no KDS"
                      />
                      <FormControlLabel
                        control={<Switch checked={generalSettings.modoSilencioso} onChange={handleGeneralToggle('modoSilencioso')} />}
                        label="Silenciar após fechar loja"
                      />
                    </Stack>
                    <Divider />
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 7 }}>
                        <Typography variant="subtitle2" gutterBottom>Tempo médio de preparo</Typography>
                        <Slider
                          value={generalSettings.prepTime}
                          onChange={(_, value) => handleGeneralChange('prepTime')(null, value)}
                          valueLabelDisplay="auto"
                          min={8}
                          max={45}
                          step={1}
                          marks={[{ value: 12, label: '12m' }, { value: 20, label: '20m' }, { value: 35, label: '35m' }]}
                          sx={{ maxWidth: 360 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <Typography variant="subtitle2" gutterBottom>Tolerância de atraso</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={generalSettings.atrasoMinutos}
                          onChange={handleGeneralChange('atrasoMinutos')}
                          InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={() => handleSave('operação')}>
                      Salvar preferências
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <StoreIdentityPanel
                apiUrl={API_URL}
                session={tenantSession}
                canEdit={canSeeOperationalSettings}
                onSaved={(company) => setCompanySettings({ ...defaultCompanyContractSettings, ...company })}
              />
              <CompanyPixPanel apiUrl={API_URL} session={tenantSession} canEdit={canSeeOperationalSettings} />
            </Grid>

          </Grid>
        )}

        {tab === 'contratos' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning">
                Modelo base. Revise com contabilidade/jurídico e convenção coletiva antes de usar oficialmente.
              </Alert>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Empresa</Typography>
                      <Typography variant="h6">Dados formais para contratos</Typography>
                    </Stack>
                    {companySettingsStatus.loading ? <CircularProgress size={20} /> : null}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {companySettingsStatus.error ? <Alert severity="error">{companySettingsStatus.error}</Alert> : null}
                    <TextField
                      label="Razão social"
                      value={companySettings.legalName || ''}
                      onChange={handleCompanyContractChange('legalName')}
                      disabled={!canManageContractSettings}
                      fullWidth
                    />
                    <TextField
                      label="Nome fantasia"
                      value={companySettings.tradeName || ''}
                      onChange={handleCompanyContractChange('tradeName')}
                      disabled={!canManageContractSettings}
                      fullWidth
                    />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="CNPJ"
                          value={companySettings.cnpj || ''}
                          onChange={handleCompanyContractChange('cnpj')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Telefone"
                          value={companySettings.phone || ''}
                          onChange={handleCompanyContractChange('phone')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <TextField
                      label="E-mail"
                      value={companySettings.email || ''}
                      onChange={handleCompanyContractChange('email')}
                      disabled={!canManageContractSettings}
                      fullWidth
                    />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Responsável legal"
                          value={companySettings.legalRepresentativeName || ''}
                          onChange={handleCompanyContractChange('legalRepresentativeName')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="CPF do responsável"
                          value={companySettings.legalRepresentativeCpf || ''}
                          onChange={handleCompanyContractChange('legalRepresentativeCpf')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Divider />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField
                          label="CEP"
                          value={companySettings.address?.cep || ''}
                          onChange={handleCompanyAddressChange('cep')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 7 }}>
                        <TextField
                          label="Rua"
                          value={companySettings.address?.street || ''}
                          onChange={handleCompanyAddressChange('street')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Número"
                          value={companySettings.address?.number || ''}
                          onChange={handleCompanyAddressChange('number')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                          label="Bairro"
                          value={companySettings.address?.neighborhood || ''}
                          onChange={handleCompanyAddressChange('neighborhood')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                          label="Cidade"
                          value={companySettings.address?.city || ''}
                          onChange={handleCompanyAddressChange('city')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="UF"
                          value={companySettings.address?.state || ''}
                          onChange={handleCompanyAddressChange('state')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        onClick={handleSaveCompanySettings}
                        disabled={!canManageContractSettings || companySettingsSaving}
                      >
                        {companySettingsSaving ? 'Salvando...' : 'Salvar empresa'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Modelo editável</Typography>
                      <Typography variant="h6">Template base de contrato</Typography>
                    </Stack>
                    {contractTemplateStatus.loading ? <CircularProgress size={20} /> : (
                      <Chip label={`v${contractTemplate.version || 1}`} variant="outlined" />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {contractTemplateStatus.error ? <Alert severity="error">{contractTemplateStatus.error}</Alert> : null}
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 8 }}>
                        <TextField
                          label="Nome do modelo"
                          value={contractTemplate.name || ''}
                          onChange={handleContractTemplateChange('name')}
                          disabled={!canManageContractSettings}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControl fullWidth disabled={!canManageContractSettings}>
                          <InputLabel>Escala padrão</InputLabel>
                          <Select
                            label="Escala padrão"
                            value={contractTemplate.defaultScale || '5x2'}
                            onChange={handleContractTemplateChange('defaultScale')}
                          >
                            <MenuItem value="5x2">5x2</MenuItem>
                            <MenuItem value="6x1">6x1</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    <TextField
                      label="Corpo do contrato"
                      value={contractTemplate.body || ''}
                      onChange={handleContractTemplateChange('body')}
                      disabled={!canManageContractSettings}
                      fullWidth
                      multiline
                      minRows={16}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" spacing={1.5}>
                      <Button
                        variant="outlined"
                        onClick={handleResetContractTemplate}
                        disabled={!canManageContractSettings || contractTemplateResetting}
                      >
                        {contractTemplateResetting ? 'Restaurando...' : 'Restaurar modelo padrão'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveContractTemplate}
                        disabled={!canManageContractSettings || contractTemplateSaving}
                      >
                        {contractTemplateSaving ? 'Salvando...' : 'Salvar modelo'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card className="configuracoes-card" sx={{ mt: 3 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle2" color="text.secondary">Prévia segura</Typography>
                    <Box sx={{
                      p: 2,
                      border: '1px solid #e5e7eb',
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      whiteSpace: 'pre-wrap',
                      maxHeight: 420,
                      overflow: 'auto',
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: 1.55,
                    }}>
                      {contractPreview || 'Prévia indisponível.'}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'impressao' && (
          <Grid container spacing={3} className="configuracoes-section">
            {!hasTenantPrinterSettings && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  O CIBD e as impressoras desta estação são reconhecidos entre contas. Ajustes de pontos continuam por loja, e a impressão automática só é ativada em Pedidos.
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Impressão</Typography>
                      <Typography variant="h6">CIBD - Comanda</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {loadingPrinters && <CircularProgress size={18} />}
                      <Chip size="small" label={cibdStatusInfo.label} color={cibdStatusInfo.color} variant="outlined" />
                      <Button size="small" variant="outlined" onClick={() => fetchCibdPrinters()} startIcon={<PrintIcon />}>
                        Atualizar impressoras
                      </Button>
                    </Stack>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        CIBD ativo em {CIBD_URL}
                      </Typography>
                      {printerError && (
                        <Alert severity="warning" sx={{ mt: 1 }}>{printerError}</Alert>
                      )}
                      {printRegistryStatus.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {printRegistryStatus.error} As preferências locais foram preservadas, mas as mesas ainda não receberão estas alterações.
                        </Alert>
                      )}
                      {printRegistryStatus.synced && !printRegistryStatus.loading && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          Pontos e modelo permanecem nesta loja. A conexão e a descoberta do CIBD ficam disponíveis para outras contas neste navegador.
                        </Alert>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Token do CIBD desta estação (opcional)"
                        fullWidth
                        value={printerSettings.cibdToken || ''}
                        onChange={handlePrinterChange('cibdToken')}
                        placeholder="SEU_TOKEN"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>Pontos de impressão</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Caixa é o ponto padrão. Pedidos com impressão automática são enviados a todos os pontos ativados.
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={openPrintPointDialog}
                          disabled={normalizePrintPoints(printerSettings).length >= MAX_PRINT_POINTS}
                        >
                          Criar ponto
                        </Button>
                      </Stack>
                      <Stack spacing={1.25}>
                        {normalizePrintPoints(printerSettings).map((point) => (
                          <Box key={point.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                              <Box sx={{ minWidth: { sm: 120 } }}>
                                <Typography fontWeight={700}>{point.name}</Typography>
                                {point.isDefault && <Chip label="Padrão" size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />}
                              </Box>
                              <FormControl fullWidth size="small" required>
                                <InputLabel>{`Impressora do ponto ${point.name}`}</InputLabel>
                                <Select
                                  label={`Impressora do ponto ${point.name}`}
                                  value={point.printerId || ''}
                                  onChange={point.isDefault ? handleSelectDetectedPrinter : handlePrintPointChange(point.id, 'printerId')}
                                >
                                  {detectedPrinters.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                      {printer.name || printer.id}
                                    </MenuItem>
                                  ))}
                                  {detectedPrinters.length === 0 && <MenuItem value=""><em>Nenhuma impressora encontrada no CIBD.</em></MenuItem>}
                                </Select>
                              </FormControl>
                              {point.isDefault ? (
                                <Chip label="Sempre ativado" size="small" color="success" variant="outlined" sx={{ minWidth: 120 }} />
                              ) : (
                                <FormControlLabel
                                  control={<Switch checked={point.enabled} onChange={handlePrintPointChange(point.id, 'enabled')} />}
                                  label={point.enabled ? 'Ativado' : 'Desativado'}
                                  sx={{ minWidth: 120, m: 0 }}
                                />
                              )}
                              <Tooltip title={`Testar ${point.name}`}>
                                <span><IconButton color="primary" onClick={() => handleTestPrint(point)} disabled={!point.printerId}><PrintIcon /></IconButton></span>
                              </Tooltip>
                              {!point.isDefault && (
                                <Tooltip title={`Excluir ${point.name}`}>
                                  <IconButton color="error" onClick={() => handleRemovePrintPoint(point.id)}><DeleteOutlineIcon /></IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {normalizePrintPoints(printerSettings).length} de {MAX_PRINT_POINTS} pontos cadastrados.
                      </Typography>
                    </Grid>
                  </Grid>
                  <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
                    <Button variant="contained" onClick={handleSavePrintSettings} disabled={printRegistryStatus.loading}>
                      {printRegistryStatus.loading ? 'Sincronizando...' : 'Salvar configuração'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card className="configuracoes-card" sx={{ mt: 3 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Comanda personalizada</Typography>
                      <Typography variant="h6">Modelo de impressão</Typography>
                    </Stack>
                    <Button variant="outlined" onClick={() => setComandaPreviewOpen(true)}>
                      Visualizar modelo
                    </Button>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Subtitulo"
                        value={printerSettings.comandaSubtitle}
                        onChange={handlePrinterChange('comandaSubtitle')}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Campos exibidos</Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowOrderNumber}
                              onChange={handlePrinterToggle('comandaShowOrderNumber')}
                            />
                          )}
                          label="Numero do pedido"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaOrderNumberBanner}
                              onChange={handlePrinterToggle('comandaOrderNumberBanner')}
                            />
                          )}
                          label="Numero do pedido em faixa preta"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowCustomer}
                              onChange={handlePrinterToggle('comandaShowCustomer')}
                            />
                          )}
                          label="Cliente"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowCustomerDocument}
                              onChange={handlePrinterToggle('comandaShowCustomerDocument')}
                            />
                          )}
                          label="CPF do cliente"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowCustomerPhone}
                              onChange={handlePrinterToggle('comandaShowCustomerPhone')}
                            />
                          )}
                          label="Telefone do cliente"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowContact}
                              onChange={handlePrinterToggle('comandaShowContact')}
                            />
                          )}
                          label="Localizador"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowAddress}
                              onChange={handlePrinterToggle('comandaShowAddress')}
                            />
                          )}
                          label="Endereço"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowItems}
                              onChange={handlePrinterToggle('comandaShowItems')}
                            />
                          )}
                          label="Itens"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowNotes}
                              onChange={handlePrinterToggle('comandaShowNotes')}
                            />
                          )}
                          label="Observacoes"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowTotals}
                              onChange={handlePrinterToggle('comandaShowTotals')}
                            />
                          )}
                          label="Totais"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowDiscounts}
                              onChange={handlePrinterToggle('comandaShowDiscounts')}
                            />
                          )}
                          label="Descontos"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowAdditionalFees}
                              onChange={handlePrinterToggle('comandaShowAdditionalFees')}
                            />
                          )}
                          label="Taxas adicionais"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowPayment}
                              onChange={handlePrinterToggle('comandaShowPayment')}
                            />
                          )}
                          label="Pagamento"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowPaymentDetails}
                              onChange={handlePrinterToggle('comandaShowPaymentDetails')}
                            />
                          )}
                          label="Detalhes do pagamento"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowDeliveryTime}
                              onChange={handlePrinterToggle('comandaShowDeliveryTime')}
                            />
                          )}
                          label="Prazo"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowSalesChannel}
                              onChange={handlePrinterToggle('comandaShowSalesChannel')}
                            />
                          )}
                          label="Canal de vendas"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowCreatedAt}
                              onChange={handlePrinterToggle('comandaShowCreatedAt')}
                            />
                          )}
                          label="Data/hora do pedido"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowMerchant}
                              onChange={handlePrinterToggle('comandaShowMerchant')}
                            />
                          )}
                          label="Restaurante"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowDeliveryMode}
                              onChange={handlePrinterToggle('comandaShowDeliveryMode')}
                            />
                          )}
                          label="Tipo de entrega"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaShowPickupCode}
                              onChange={handlePrinterToggle('comandaShowPickupCode')}
                            />
                          )}
                          label="Código de retirada"
                        />
                      </FormGroup>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Negrito nos campos</Typography>
                      <FormGroup row>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldTitle}
                              onChange={handlePrinterToggle('comandaBoldTitle')}
                            />
                          )}
                          label="Titulo"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldOrderNumber}
                              onChange={handlePrinterToggle('comandaBoldOrderNumber')}
                            />
                          )}
                          label="Numero do pedido"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldCustomer}
                              onChange={handlePrinterToggle('comandaBoldCustomer')}
                            />
                          )}
                          label="Cliente"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldAddress}
                              onChange={handlePrinterToggle('comandaBoldAddress')}
                            />
                          )}
                          label="Endereço"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldPhone}
                              onChange={handlePrinterToggle('comandaBoldPhone')}
                            />
                          )}
                          label="Telefone"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldOptions}
                              onChange={handlePrinterToggle('comandaBoldOptions')}
                            />
                          )}
                          label="Complementos"
                        />
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={printerSettings.comandaBoldTotal}
                              onChange={handlePrinterToggle('comandaBoldTotal')}
                            />
                          )}
                          label="Total"
                        />
                      </FormGroup>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Fonte e qualidade</Typography>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel>Fonte da comanda</InputLabel>
                            <Select
                              label="Fonte da comanda"
                              value={printerSettings.comandaFontFamily}
                              onChange={handlePrinterChange('comandaFontFamily')}
                            >
                              <MenuItem value="courier">Courier</MenuItem>
                              <MenuItem value="helvetica">Helvetica</MenuItem>
                              <MenuItem value="times">Times</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={printerSettings.comandaStrongPrint}
                                onChange={handlePrinterToggle('comandaStrongPrint')}
                              />
                            )}
                            label="Impressão mais forte"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Tamanho de fonte</Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Faixa do numero do pedido</Typography>
                          <Slider
                            value={printerSettings.comandaSizeOrderNumberBanner}
                            onChange={handlePrinterSlider('comandaSizeOrderNumberBanner')}
                            valueLabelDisplay="auto"
                            min={12}
                            max={24}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Titulo</Typography>
                          <Slider
                            value={printerSettings.comandaSizeTitle}
                            onChange={handlePrinterSlider('comandaSizeTitle')}
                            valueLabelDisplay="auto"
                            min={14}
                            max={26}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Numero do pedido</Typography>
                          <Slider
                            value={printerSettings.comandaSizeOrderNumber}
                            onChange={handlePrinterSlider('comandaSizeOrderNumber')}
                            valueLabelDisplay="auto"
                            min={12}
                            max={22}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Itens</Typography>
                          <Slider
                            value={printerSettings.comandaSizeItem}
                            onChange={handlePrinterSlider('comandaSizeItem')}
                            valueLabelDisplay="auto"
                            min={10}
                            max={18}
                          />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                          <Slider
                            value={printerSettings.comandaSizeTotal}
                            onChange={handlePrinterSlider('comandaSizeTotal')}
                            valueLabelDisplay="auto"
                            min={12}
                            max={22}
                          />
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <BoltIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Checklist do CIBD</Typography>
                  </Stack>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">1. Abra o CIBD no Windows.</Typography>
                    <Typography variant="body2">2. Selecione a impressora da comanda.</Typography>
                    <Typography variant="body2">3. Envie um teste de impressão.</Typography>
                  </Stack>
                </CardContent>
              </Card>

            </Grid>
          </Grid>
        )}

        {tab === 'integracoes' && integrationTab === 'ifood' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Integração</Typography>
                      <Typography variant="h6">Loja vinculada ao iFood</Typography>
                    </Stack>
                    {ifoodStatus.loading ? (
                      <Chip label="Carregando" color="default" variant="outlined" />
                    ) : (
                      <Chip
                        label={!ifoodSettings.integrationEnabled ? 'Desativada' : hasIfoodCredentials ? 'Credenciais salvas' : 'Não configurado'}
                        color={!ifoodSettings.integrationEnabled ? 'default' : hasIfoodCredentials ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={Boolean(ifoodSettings.integrationEnabled)}
                          onChange={handleIfoodToggle('integrationEnabled')}
                        />
                      )}
                      label="Integração ativa"
                    />
                    <TextField
                      label="Client ID"
                      value={ifoodSettings.credentials?.clientIdPreview || 'Não configurado'}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText={ifoodSettings.credentials?.testTenant
                        ? 'Credencial exclusiva do tenant técnico de testes — bloqueada.'
                        : 'Credencial padrão da plataforma, gerenciada no servidor.'}
                    />
                    <TextField
                      label="Client Secret"
                      value={ifoodSettings.credentials?.hasClientSecret ? 'Configurado com segurança' : 'Não configurado'}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="O segredo nunca é exibido nem alterado por esta tela."
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                      <TextField
                        label="ID da Loja"
                        value={ifoodMerchantIdDraft}
                        onChange={(event) => setIfoodMerchantIdDraft(event.target.value)}
                        onKeyDown={handleIfoodMerchantIdKeyDown}
                        fullWidth
                        placeholder="merchantId do iFood"
                        helperText="Adicione os merchantIds permitidos para este login/CNPJ."
                      />
                      <Button
                        variant="outlined"
                        onClick={handleAddIfoodMerchantId}
                        startIcon={<AddCircleOutlineIcon />}
                        sx={{ minWidth: 140, mt: { sm: 1 } }}
                      >
                        Adicionar
                      </Button>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {hasIfoodStores ? (
                        ifoodSettings.merchantIds.map((merchantId) => {
                          const merchantIdentity = getIfoodMerchantIdentity(merchantId);
                          return (
                            <Tooltip
                              key={merchantIdentity.id}
                              title={merchantIdentity.displayName ? `${merchantIdentity.displayName} - ${merchantIdentity.id}` : merchantIdentity.id}
                            >
                              <Chip
                                label={(
                                  <Box component="span" sx={{ display: 'grid', gap: 0.25, minWidth: 0, py: 0.25 }}>
                                    {merchantIdentity.displayName && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{
                                          color: 'primary.main',
                                          fontWeight: 700,
                                          lineHeight: 1.15,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {merchantIdentity.displayName}
                                      </Typography>
                                    )}
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{
                                        color: merchantIdentity.displayName ? 'text.secondary' : 'primary.main',
                                        fontFamily: 'monospace',
                                        lineHeight: 1.15,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {merchantIdentity.id}
                                    </Typography>
                                  </Box>
                                )}
                                variant="outlined"
                                color="primary"
                                onDelete={() => handleRemoveIfoodMerchantId(merchantId)}
                                deleteIcon={<DeleteOutlineIcon />}
                                sx={{
                                  height: 'auto',
                                  maxWidth: { xs: '100%', sm: 420 },
                                  '& .MuiChip-label': {
                                    display: 'block',
                                    minWidth: 0,
                                    maxWidth: { xs: 'calc(100vw - 120px)', sm: 360 },
                                    py: 0.5,
                                  },
                                }}
                              />
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Nenhum ID da Loja cadastrado para este login/CNPJ.
                        </Typography>
                      )}
                    </Stack>
                    {hasIfoodStores && (
                      <TextField
                        select
                        label="Loja que envia os horários ao Cardápio"
                        value={ifoodSettings.scheduleMerchantId || (ifoodSettings.merchantIds.length === 1 ? ifoodSettings.merchantIds[0] : '')}
                        onChange={(event) => setIfoodSettings((prev) => ({
                          ...prev,
                          scheduleMerchantId: event.target.value,
                        }))}
                        fullWidth
                        required={ifoodSettings.merchantIds.length > 1}
                        helperText={ifoodSettings.merchantIds.length === 1
                          ? 'Esta é a única loja cadastrada e será usada automaticamente.'
                          : 'Selecione explicitamente a loja cujos horários serão sincronizados.'}
                      >
                        {ifoodSettings.merchantIds.map((merchantId) => {
                          const merchantIdentity = getIfoodMerchantIdentity(merchantId);
                          return (
                            <MenuItem key={merchantId} value={merchantId}>
                              {merchantIdentity.displayName
                                ? `${merchantIdentity.displayName} — ${merchantId}`
                                : merchantId}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      As credenciais autenticam o token do iFood; os IDs da Loja limitam pedidos, catálogo e Merchant somente às lojas cadastradas neste login/CNPJ.
                    </Typography>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Recebimento de eventos
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(ifoodSettings.pollingEnabled)}
                              onChange={handleIfoodTransportToggle('polling')}
                            />
                          )}
                          label="Eventos por polling"
                        />
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={Boolean(ifoodSettings.webhookEnabled)}
                              onChange={handleIfoodTransportToggle('webhook')}
                            />
                          )}
                          label="Eventos por webhook"
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Mantenha o polling ativo para ACK e reconciliação; o webhook pode rodar junto para acelerar a chegada dos eventos.
                      </Typography>
                    </Box>
                    <Box>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={ifoodSettings.autoAcceptEnabled !== false}
                            onChange={handleIfoodToggle('autoAcceptEnabled')}
                          />
                        )}
                        label="Aceitar pedidos automaticamente"
                      />
                    </Box>
                    {canManageIfoodShippingConfig && (
                      <Box sx={{ border: '1px solid #ececec', borderRadius: 2, p: 2, backgroundColor: '#fafafa' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CloudDoneIcon color="success" fontSize="small" />
                          <Typography variant="subtitle2">
                            Entrega iFood ativa em produção
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Mostra o botão de chamar entregador iFood nos pedidos elegíveis dos tenants com iFood configurado.
                        </Typography>
                        <FormControl
                          fullWidth
                          size="small"
                          sx={{ mt: 2 }}
                          disabled={!shippingMerchantOptions.length}
                        >
                          <InputLabel id="ifood-shipping-merchants-label" shrink>Lojas habilitadas</InputLabel>
                          <Select
                            labelId="ifood-shipping-merchants-label"
                            label="Lojas habilitadas"
                            multiple
                            displayEmpty
                            value={ifoodSettings.shippingMerchantIds}
                            onChange={handleIfoodShippingMerchantsChange}
                            renderValue={(selected) => {
                              if (!selected.length) return 'Todas as lojas deste tenant';
                              return selected
                                .map((id) => shippingMerchantOptions.find((merchant) => merchant.id === id)?.name || 'Loja iFood')
                                .join(', ');
                            }}
                          >
                            {shippingMerchantOptions.map((merchant) => (
                              <MenuItem key={merchant.id} value={merchant.id}>
                                <Checkbox checked={ifoodSettings.shippingMerchantIds.includes(merchant.id)} />
                                <ListItemText primary={merchant.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Sem seleção, o recurso fica disponível para todas as lojas iFood deste tenant.
                        </Typography>
                      </Box>
                    )}
                    {ifoodSettings.lastTransportFailure?.failedMethod && (
                      <Alert severity="warning">
                        Último fallback automático: {ifoodSettings.lastTransportFailure.failedMethod === 'webhook' ? 'polling reforçado após falha no webhook' : 'webhook habilitado após falha no polling'}
                        {ifoodSettings.lastTransportFailure.reason ? ` (${ifoodSettings.lastTransportFailure.reason})` : ''}.
                      </Alert>
                    )}
                    {ifoodStatus.error && (
                      <Alert severity="error">{ifoodStatus.error}</Alert>
                    )}
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveIfood}
                      disabled={ifoodSaving || ifoodStatus.loading}
                      startIcon={ifoodSaving ? <CircularProgress size={18} /> : <CloudDoneIcon />}
                    >
                      {ifoodSaving ? 'Salvando...' : 'Salvar integração'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              {showProgrammerCatalogTools && (
                <Card className="configuracoes-card" sx={{ mt: 3 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                      <Stack spacing={0.5}>
                        <Typography variant="subtitle2" color="text.secondary">Catálogo iFood</Typography>
                        <Typography variant="h6">Importar cardápio</Typography>
                      </Stack>
                      <Chip label="Programador" color="primary" variant="outlined" />
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Escolha a loja iFood antes de importar ou limpar produtos. Cada ação vale somente para o merchantId selecionado; produtos de outras lojas permanecem preservados.
                      </Typography>
                      {hasIfoodStores ? (
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, backgroundColor: '#fafafa' }}>
                          <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                              <Box>
                                <Typography variant="subtitle2">Loja para importação</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Importar, atualizar e limpar produtos usa apenas esta loja.
                                </Typography>
                              </Box>
                              {selectedCatalogImportMerchantId ? (
                                <Chip
                                  label={catalogImportMerchantOptions.length === 1 ? 'Selecionada automaticamente' : 'Loja selecionada'}
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="Selecione uma loja" color="warning" variant="outlined" />
                              )}
                            </Stack>
                            <FormControl fullWidth size="small" disabled={catalogAdminAction !== '' || catalogImportMerchantOptions.length === 1}>
                              <InputLabel id="catalog-import-merchant-label" shrink>Loja iFood</InputLabel>
                              <Select
                                labelId="catalog-import-merchant-label"
                                label="Loja iFood"
                                value={selectedCatalogImportMerchantId}
                                onChange={(event) => {
                                  setCatalogImportMerchantTouched(Boolean(event.target.value));
                                  setCatalogImportMerchantId(event.target.value);
                                }}
                                displayEmpty
                                renderValue={(value) => {
                                  const selected = catalogImportMerchantOptions.find((merchant) => merchant.id === value);
                                  if (!selected) return 'Selecione uma loja iFood';
                                  return `${selected.displayName} - ${selected.id}`;
                                }}
                              >
                                {catalogImportMerchantOptions.length > 1 && (
                                  <MenuItem value="">
                                    <em>Selecione uma loja iFood</em>
                                  </MenuItem>
                                )}
                                {catalogImportMerchantOptions.map((merchant) => (
                                  <MenuItem key={merchant.id} value={merchant.id}>
                                    <ListItemText
                                      primary={merchant.displayName}
                                      secondary={merchant.id}
                                      secondaryTypographyProps={{ sx: { fontFamily: 'monospace' } }}
                                    />
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {selectedCatalogImportMerchantId ? (
                              <Alert severity="info">
                                A importação e a limpeza serão aplicadas somente em {selectedCatalogImportMerchantDescription}. Produtos de outras lojas não serão alterados.
                              </Alert>
                            ) : (
                              <Alert severity="warning">
                                Selecione uma loja iFood antes de importar ou limpar produtos.
                              </Alert>
                            )}
                          </Stack>
                        </Box>
                      ) : null}
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={catalogAdminAction !== '' || !canUseCatalogImportActions}
                          onClick={() => runCatalogAdminAction({
                            endpoint: '/api/products/admin/import-ifood/full',
                            actionKey: 'import-full',
                            confirmMessage: `Importar o cardápio completo de ${selectedCatalogImportMerchantDescription} para o estoque compartilhado? Isso atualizará apenas os produtos iFood desta loja.`,
                            successMessage: 'Importação completa do cardápio concluída.',
                          })}
                        >
                          {catalogAdminAction === 'import-full' ? 'Importando completo...' : 'Importar cardápio completo'}
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={catalogAdminAction !== '' || !canUseCatalogImportActions}
                          onClick={() => runCatalogAdminAction({
                            endpoint: '/api/products/admin/import-ifood/new-only',
                            actionKey: 'import-new-only',
                            confirmMessage: `Importar somente os produtos ativos de ${selectedCatalogImportMerchantDescription} que ainda não constam no estoque compartilhado desta loja?`,
                            successMessage: 'Importação apenas de novos produtos concluída.',
                          })}
                        >
                          {catalogAdminAction === 'import-new-only' ? 'Importando novos...' : 'Atualizar e importar só novos'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={catalogAdminAction !== '' || !canUseCatalogImportActions}
                          onClick={() => runCatalogAdminAction({
                            endpoint: '/api/products/admin/clear-all',
                            method: 'DELETE',
                            actionKey: 'clear-all',
                            confirmMessage: `Isso limpará TODOS os produtos importados da loja ${selectedCatalogImportMerchantLabel} (${selectedCatalogImportMerchantId}). Produtos de outras lojas não serão alterados. Deseja continuar?`,
                            successMessage: 'Produtos iFood da loja selecionada foram limpos do estoque.',
                          })}
                        >
                          {catalogAdminAction === 'clear-all' ? 'Limpando loja...' : 'Limpar produtos da loja'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteSweepOutlinedIcon />}
                          disabled={catalogAdminAction !== ''}
                          onClick={reviewDeleteAllProducts}
                        >
                          {catalogAdminAction === 'delete-all-products-preview'
                            ? 'Revisando produtos...'
                            : catalogAdminAction === 'delete-all-products'
                              ? 'Deletando produtos...'
                              : 'Deletar todos os produtos'}
                        </Button>
                      </Stack>
                      {!hasIfoodCredentials ? (
                        <Alert severity="info">
                          Salve o Client ID e o Client Secret do iFood antes de usar os botões de importação.
                        </Alert>
                      ) : null}
                      {hasIfoodCredentials && !hasIfoodStores ? (
                        <Alert severity="info">
                          Adicione e salve pelo menos um ID da Loja antes de usar os botões de importação.
                        </Alert>
                      ) : null}
                      {hasIfoodCredentials && hasIfoodStores && !selectedCatalogImportMerchantId ? (
                        <Alert severity="info">
                          Selecione uma loja iFood antes de importar ou limpar produtos.
                        </Alert>
                      ) : null}
                      {catalogAdminSummary ? (
                        <Alert severity="success">
                          {catalogAdminSummary.operation === 'delete-all-products' ? (
                            <>
                              {catalogAdminSummary.message || 'Operação concluída.'}
                              {' '}Removidos do catálogo operacional: {catalogAdminSummary.deactivatedCount ?? 0}.
                              {' '}Manuais: {catalogAdminSummary.manualProductCount ?? 0}.
                              {' '}iFood: {catalogAdminSummary.ifoodProductCount ?? 0}.
                            </>
                          ) : (
                            <>
                              {catalogAdminSummary.message || 'Operação concluída.'}
                              {' '}Recebidos: {catalogAdminSummary.totalReceived ?? 0}.
                              {' '}Processados: {catalogAdminSummary.processedCount ?? 0}.
                              {' '}Inseridos: {catalogAdminSummary.insertedCount ?? 0}.
                              {' '}Atualizados: {catalogAdminSummary.updatedCount ?? 0}.
                              {' '}Ignorados: {catalogAdminSummary.skippedCount ?? 0}.
                              {' '}Códigos gerados: {catalogAdminSummary.autoGeneratedCount ?? 0}.
                              {' '}Removidos: {catalogAdminSummary.removedCount ?? 0}.
                            </>
                          )}
                        </Alert>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        )}

        {tab === 'integracoes' && integrationTab === '99food' && canSeeIntegrationConfig && (
          <NinetyNineFoodSettingsPanel apiUrl={API_URL} sessionToken={tenantSession.token} />
        )}

        {tab === 'integracoes' && integrationTab === 'pedidos10' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Integração Pedidos10</Typography>
                      <Typography variant="h6">Pedidos do marketplace</Typography>
                    </Stack>
                    {pedidos10Status.loading ? (
                      <Chip label="Carregando" color="default" variant="outlined" />
                    ) : (
                      <Chip
                        label={!pedidos10Settings.integrationEnabled ? 'Desativada' : pedidos10Settings.integrationToken || pedidos10Settings.accessToken ? 'Credenciais informadas' : 'Aguardando suporte'}
                        color={!pedidos10Settings.integrationEnabled ? 'default' : pedidos10Settings.integrationToken || pedidos10Settings.accessToken ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={<Switch checked={Boolean(pedidos10Settings.integrationEnabled)} onChange={handlePedidos10Change('integrationEnabled')} />}
                      label="Integração ativa"
                    />
                    <TextField
                      label="URL base da API Pedidos10"
                      value={pedidos10Settings.apiBaseUrl}
                      onChange={handlePedidos10Change('apiBaseUrl')}
                      fullWidth
                      placeholder="https://api-integracao.pedidos10.com.br"
                    />
                    <TextField
                      label="Integration token"
                      value={pedidos10Settings.integrationToken}
                      onChange={handlePedidos10Change('integrationToken')}
                      fullWidth
                      type={showPedidos10IntegrationToken ? 'text' : 'password'}
                      placeholder="Token liberado pelo suporte Pedidos10"
                      InputProps={{
                        endAdornment: buildCopyAdornment(
                          pedidos10Settings.integrationToken,
                          'integration token Pedidos10',
                          <IconButton size="small" onClick={() => setShowPedidos10IntegrationToken((prev) => !prev)} edge="end">
                            {showPedidos10IntegrationToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Usuário do estabelecimento"
                          value={pedidos10Settings.username}
                          onChange={handlePedidos10Change('username')}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Senha do estabelecimento"
                          value={pedidos10Settings.password}
                          onChange={handlePedidos10Change('password')}
                          fullWidth
                          type={showPedidos10Password ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPedidos10Password((prev) => !prev)} edge="end">
                                  {showPedidos10Password ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                    <TextField
                      label="Access token"
                      value={pedidos10Settings.accessToken}
                      onChange={handlePedidos10Change('accessToken')}
                      fullWidth
                      type={showPedidos10AccessToken ? 'text' : 'password'}
                      helperText="Preenchido automaticamente após autenticar, ou manualmente se o suporte fornecer."
                      InputProps={{
                        endAdornment: buildCopyAdornment(
                          pedidos10Settings.accessToken,
                          'access token Pedidos10',
                          <IconButton size="small" onClick={() => setShowPedidos10AccessToken((prev) => !prev)} edge="end">
                            {showPedidos10AccessToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <Divider />
                    <TextField
                      label="URL do webhook do Brutos"
                      value={pedidos10Settings.webhookUrl}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(pedidos10Settings.webhookUrl, 'URL do webhook Pedidos10'),
                      }}
                    />
                    <TextField
                      label="Código externo do estabelecimento"
                      value={pedidos10Settings.merchantExternalCode}
                      onChange={handlePedidos10Change('merchantExternalCode')}
                      fullWidth
                      InputProps={{
                        endAdornment: buildCopyAdornment(pedidos10Settings.merchantExternalCode, 'código externo Pedidos10'),
                      }}
                    />
                    <TextField
                      label="Segredo do webhook"
                      value={pedidos10Settings.webhookSecret}
                      onChange={handlePedidos10Change('webhookSecret')}
                      fullWidth
                      type={showPedidos10WebhookSecret ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: buildCopyAdornment(
                          pedidos10Settings.webhookSecret,
                          'segredo do webhook Pedidos10',
                          <IconButton size="small" onClick={() => setShowPedidos10WebhookSecret((prev) => !prev)} edge="end">
                            {showPedidos10WebhookSecret ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <Stack direction="row" flexWrap="wrap" spacing={2}>
                      <FormControlLabel
                        control={<Switch checked={Boolean(pedidos10Settings.webhookEnabled)} onChange={handlePedidos10Change('webhookEnabled')} />}
                        label="Receber webhook"
                      />
                      <FormControlLabel
                        control={<Switch checked={Boolean(pedidos10Settings.pollingEnabled)} onChange={handlePedidos10Change('pollingEnabled')} />}
                        label="Polling a cada 45s"
                      />
                    </Stack>
                    {pedidos10Status.error && (
                      <Alert severity="error">{pedidos10Status.error}</Alert>
                    )}
                    {pedidos10Status.lastWebhookRegistrationAt && (
                      <Alert severity="info">
                        Webhook registrado em {new Date(pedidos10Status.lastWebhookRegistrationAt).toLocaleString('pt-BR')}.
                      </Alert>
                    )}
                    {pedidos10Status.lastSyncAt && (
                      <Alert severity="info">
                        Última sincronização em {new Date(pedidos10Status.lastSyncAt).toLocaleString('pt-BR')}.
                      </Alert>
                    )}
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={handleRegeneratePedidos10MerchantCode}
                      disabled={pedidos10Saving}
                    >
                      Gerar código
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRegeneratePedidos10WebhookSecret}
                      disabled={pedidos10Saving}
                    >
                      Gerar segredo
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleTestPedidos10Connection}
                      disabled={pedidos10Testing || pedidos10Saving || pedidos10Status.loading}
                    >
                      {pedidos10Testing ? 'Testando...' : 'Testar conexão'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRegisterPedidos10Webhook}
                      disabled={pedidos10Registering || pedidos10Saving || pedidos10Status.loading}
                    >
                      {pedidos10Registering ? 'Registrando...' : 'Registrar webhook'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleSyncPedidos10Orders}
                      disabled={pedidos10Syncing || pedidos10Saving || pedidos10Status.loading}
                    >
                      {pedidos10Syncing ? 'Sincronizando...' : 'Sincronizar pedidos'}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSavePedidos10}
                      disabled={pedidos10Saving || pedidos10Status.loading}
                      startIcon={pedidos10Saving ? <CircularProgress size={18} /> : <CloudDoneIcon />}
                    >
                      {pedidos10Saving ? 'Salvando...' : 'Salvar integração'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Checklist Pedidos10</Typography>
                  </Stack>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">1. Solicite ao suporte o integration_token e a liberação da API própria.</Typography>
                    <Typography variant="body2">2. Salve o token, usuário e senha do estabelecimento nesta aba.</Typography>
                    <Typography variant="body2">3. Registre o webhook ou envie ao suporte a URL do webhook e o código externo do estabelecimento.</Typography>
                    <Typography variant="body2">4. Use Sincronizar pedidos para validar o polling após a liberação.</Typography>
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Stack spacing={1}>
                    {pedidos10Settings.apiDocsUrl && (
                      <Button variant="outlined" href={pedidos10Settings.apiDocsUrl} target="_blank" rel="noreferrer">
                        Abrir API própria
                      </Button>
                    )}
                    {pedidos10Settings.docsUrl && (
                      <Button variant="outlined" href={pedidos10Settings.docsUrl} target="_blank" rel="noreferrer">
                        Abrir portal developer
                      </Button>
                    )}
                  </Stack>
                  {pedidos10Status.updatedAt && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Última atualização em {new Date(pedidos10Status.updatedAt).toLocaleString('pt-BR')}.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'loja' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Stack spacing={2}>
                        <TextField
                          select
                          label="Loja iFood"
                          value={merchantPanel.selectedMerchantId}
                          onChange={handleMerchantSelectionChange}
                          fullWidth
                          helperText={merchantPanel.merchants.length ? 'Selecione a loja para exibir os dados completos do merchant.' : 'Nenhum merchant carregado automaticamente.'}
                        >
                          {merchantPanel.merchants.map((merchant) => (
                            <MenuItem key={merchant.id} value={merchant.id}>
                              {merchant.displayName}
                              {merchant.authorizationStatus === 'reauthorization-required' ? ' (nova autorização necessária)' : ''}
                              {merchant.authorizationStatus === 'permission-review' ? ' (acesso a revisar)' : ''}
                              {merchant.authorizationStatus === 'unknown' ? ' (autorização não confirmada)' : ''}
                            </MenuItem>
                          ))}
                        </TextField>

                        {!merchantPanel.selectedMerchantId ? (
                          <Alert severity="warning">
                            Selecione uma loja para listar e demonstrar os dados completos do merchant.
                          </Alert>
                        ) : null}

                        {merchantPanel.error ? (
                          <Alert severity="error">{merchantPanel.error}</Alert>
                        ) : null}

                        {merchantPanel.merchants.find((merchant) => merchant.id === merchantPanel.selectedMerchantId)?.authorizationStatus === 'reauthorization-required' ? (
                          <Alert severity="warning">
                            O iFood não incluiu esta loja na lista completa vinculada ao token. Faça nova autorização pelo fluxo oficial da sua conta iFood e atualize a tela para confirmar. A configuração e o histórico foram preservados; nenhuma loja foi desligada.
                          </Alert>
                        ) : null}
                        {merchantPanel.merchants.find((merchant) => merchant.id === merchantPanel.selectedMerchantId)?.authorizationStatus === 'permission-review' ? (
                          <Alert severity="warning">
                            O iFood negou o acesso desta loja. A autorização não pôde ser confirmada; revise as permissões no fluxo oficial antes de retomar.
                          </Alert>
                        ) : null}
                        {merchantPanel.merchants.find((merchant) => merchant.id === merchantPanel.selectedMerchantId)?.authorizationStatus === 'unknown'
                          || merchantPanel.merchants.find((merchant) => merchant.id === merchantPanel.selectedMerchantId)?.authorizationListComplete === false ? (
                          <Alert severity="info">
                            Não foi possível confirmar a lista completa de autorizações agora. O último estado foi preservado; tente novamente mais tarde.
                          </Alert>
                        ) : null}

                        <Card variant="outlined">
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Stack spacing={0.5}>
                                <Typography variant="overline" color="text.secondary">
                                  Merchant
                                </Typography>
                                <Typography variant="h6">
                                  Resumo da loja
                                </Typography>
                              </Stack>

                              <Stack spacing={1}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">ID da Loja</Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    {selectedMerchantSummary?.id || selectedMerchantSummary?.merchantId || merchantPanel.selectedMerchantId || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Nome</Typography>
                                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                    {selectedMerchantSummary?.name || selectedMerchantSummary?.displayName || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Razão social</Typography>
                                  <Typography variant="body2">
                                    {merchantPanel.merchantDetails?.corporateName || '-'}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Endereço</Typography>
                                  <Typography variant="body2">
                                    {merchantAddressSummary}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>

                        <Card variant="outlined">
                          <CardContent>
                            <Stack spacing={1.25}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Disponibilidade da loja
                              </Typography>
                              {merchantStatusOperations.length ? merchantStatusOperations.map((operation, index) => (
                                <Box key={`${operation.name || operation.operation || 'status'}-${index}`} className="merchant-inline-card">
                                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {operation.name || operation.operation || `Operação ${index + 1}`}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={operation.state || operation.status || 'N/A'}
                                      color={operation.available ? 'success' : 'default'}
                                      variant={operation.available ? 'filled' : 'outlined'}
                                    />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    {formatMerchantStatusMessage(operation.message)
                                      || operation.message?.status
                                      || (operation.available ? 'Loja disponível.' : 'Loja indisponível.')}
                                  </Typography>
                                </Box>
                              )) : (
                                <Alert severity="info">
                                  Nenhuma operação retornada no status do merchant.
                                </Alert>
                              )}
                            </Stack>
                          </CardContent>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid container spacing={2.5}>
                        <Grid size={{ xs: 12 }}>
                          <Card variant="outlined">
                            <CardContent>
                              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Dados da loja
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Informações relevantes do merchant organizadas para leitura humana e para gravação do checklist de homologação.
                                  </Typography>
                                </Box>
                                <Button
                                  variant="outlined"
                                  onClick={() => loadMerchantPanel(merchantPanel.selectedMerchantId)}
                                  disabled={merchantPanel.refreshing || !merchantPanel.selectedMerchantId}
                                  startIcon={merchantPanel.refreshing ? <CircularProgress size={18} /> : <CloudDoneIcon />}
                                >
                                  {merchantPanel.refreshing ? 'Atualizando...' : 'Atualizar merchant'}
                                </Button>
                              </Stack>

                              <Grid container spacing={2}>
                                {merchantReadableFields.map((field) => (
                                  <Grid key={field.label} size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                      label={field.label}
                                      value={field.value}
                                      fullWidth
                                      InputProps={{ readOnly: true }}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'horario' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Stack spacing={2}>
                        <TextField
                          select
                          label="Loja iFood"
                          value={merchantPanel.selectedMerchantId}
                          onChange={handleMerchantSelectionChange}
                          fullWidth
                          helperText={merchantPanel.merchants.length ? 'Selecione a loja para consultar status e horários.' : 'Nenhum merchant carregado automaticamente.'}
                        >
                          {merchantPanel.merchants.map((merchant) => (
                            <MenuItem key={merchant.id} value={merchant.id}>
                              {merchant.displayName}
                              {merchant.authorizationStatus === 'reauthorization-required' ? ' (nova autorização necessária)' : ''}
                              {merchant.authorizationStatus === 'permission-review' ? ' (acesso a revisar)' : ''}
                              {merchant.authorizationStatus === 'unknown' ? ' (autorização não confirmada)' : ''}
                            </MenuItem>
                          ))}
                        </TextField>

                        {!merchantPanel.selectedMerchantId ? (
                          <Alert severity="warning">
                            Salve as credenciais e confirme ao menos um merchant válido para liberar a homologação.
                          </Alert>
                        ) : null}

                        {merchantPanel.error ? (
                          <Alert severity="error">{merchantPanel.error}</Alert>
                        ) : null}

                        <Card variant="outlined" className="horario-resumo-card">
                          <CardContent>
                            <Stack spacing={2}>
                              <Stack spacing={0.75}>
                                <Typography variant="overline" className="horario-resumo-eyebrow">
                                  Resumo visual
                                </Typography>
                                <Typography variant="h6">
                                  Horários salvos
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Visualização rápida da grade atual enviada e mantida na interface.
                                </Typography>
                              </Stack>

                              <Box className="horario-resumo-stats">
                                <Box className="horario-resumo-stat">
                                  <Typography variant="caption" color="text.secondary">Dias ativos</Typography>
                                  <Typography variant="h5">{activeOpeningDaysCount}</Typography>
                                </Box>
                                <Box className="horario-resumo-stat">
                                  <Typography variant="caption" color="text.secondary">Turnos</Typography>
                                  <Typography variant="h5">{merchantPanel.openingHours.length}</Typography>
                                </Box>
                              </Box>

                              <Stack spacing={1.1}>
                                {groupedOpeningHours.map((day) => (
                                  <Box key={`summary-${day.value}`} className={`horario-resumo-dia${day.shifts.length ? ' is-active' : ''}`}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {day.label}
                                      </Typography>
                                      <Chip
                                        size="small"
                                        label={day.shifts.length ? `${day.shifts.length} faixa${day.shifts.length > 1 ? 's' : ''}` : 'Fechado'}
                                        color={day.shifts.length ? 'success' : 'default'}
                                        variant={day.shifts.length ? 'filled' : 'outlined'}
                                      />
                                    </Stack>

                                    {day.shifts.length ? (
                                      <Stack spacing={0.75} sx={{ mt: 1.1 }}>
                                        {day.shifts.map((shift) => (
                                          <Box key={`summary-shift-${shift.id}`} className="horario-resumo-faixa">
                                            <AccessTimeIcon sx={{ fontSize: 16 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {shift.start} - {shift.end}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Sem horário configurado.
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Grid container spacing={2.5}>
                        <Grid size={{ xs: 12 }}>
                          <Card variant="outlined">
                            <CardContent>
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Horário de funcionamento</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Configure abertura e fechamento, com mais de uma faixa por dia quando necessário.
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  onClick={handleSaveOpeningHours}
                                  disabled={merchantOpeningHoursSaving || !merchantPanel.selectedMerchantId}
                                  startIcon={merchantOpeningHoursSaving ? <CircularProgress size={18} /> : <AccessTimeIcon />}
                                >
                                  {merchantOpeningHoursSaving ? 'Salvando...' : 'Salvar horários'}
                                </Button>
                              </Stack>

                              <Stack spacing={1.5}>
                                {groupedOpeningHours.map((day) => (
                                  <Box key={day.value} className="merchant-inline-card">
                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: day.shifts.length ? 1.5 : 0 }}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{day.label}</Typography>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleAddOpeningHour(day.value)}
                                        disabled={!merchantPanel.selectedMerchantId}
                                        aria-label={`Adicionar horário em ${day.label}`}
                                      >
                                        <AddCircleOutlineIcon />
                                      </IconButton>
                                    </Stack>

                                    <Stack spacing={1.25}>
                                      {day.shifts.map((shift) => (
                                        <Grid key={shift.id} container spacing={2} alignItems="center">
                                          <Grid size={{ xs: 12, md: 5 }}>
                                            <TextField
                                              label="Abertura"
                                              type="time"
                                              value={shift.start}
                                              onChange={handleOpeningHourChange(shift.id, 'start')}
                                              fullWidth
                                              InputLabelProps={{ shrink: true }}
                                            />
                                          </Grid>
                                          <Grid size={{ xs: 12, md: 5 }}>
                                            <TextField
                                              label="Fechamento"
                                              type="time"
                                              value={shift.end}
                                              onChange={handleOpeningHourChange(shift.id, 'end')}
                                              fullWidth
                                              InputLabelProps={{ shrink: true }}
                                            />
                                          </Grid>
                                          <Grid size={{ xs: 12, md: 2 }}>
                                            <Button
                                              color="error"
                                              variant="outlined"
                                              fullWidth
                                              startIcon={<DeleteOutlineIcon />}
                                              onClick={() => handleRemoveOpeningHour(shift.id)}
                                            >
                                              Remover
                                            </Button>
                                          </Grid>
                                        </Grid>
                                      ))}
                                      {!day.shifts.length ? (
                                        <Typography variant="body2" color="text.secondary">
                                          Nenhum horário configurado para {day.label.toLowerCase()}.
                                        </Typography>
                                      ) : null}
                                    </Stack>
                                  </Box>
                                ))}
                                {!merchantPanel.openingHours.length ? (
                                  <Alert severity="warning">
                                    Nenhum turno retornado pela API. Use o ícone de + no dia desejado para adicionar a primeira faixa.
                                  </Alert>
                                ) : null}
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'interrupcoes' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 4 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Merchant iFood</Typography>
                      <Typography variant="h6">Pausas da loja</Typography>
                    </Box>
                    <TextField
                      select
                      label="Loja iFood"
                      value={merchantPanel.selectedMerchantId}
                      onChange={handleMerchantSelectionChange}
                      fullWidth
                      helperText={merchantPanel.merchants.length ? 'Selecione a loja para consultar e gerenciar pausas.' : 'Nenhum merchant carregado automaticamente.'}
                    >
                      {merchantPanel.merchants.map((merchant) => (
                        <MenuItem key={merchant.id} value={merchant.id}>
                          {merchant.displayName}
                          {merchant.authorizationStatus === 'reauthorization-required' ? ' (nova autorização necessária)' : ''}
                              {merchant.authorizationStatus === 'permission-review' ? ' (acesso a revisar)' : ''}
                              {merchant.authorizationStatus === 'unknown' ? ' (autorização não confirmada)' : ''}
                        </MenuItem>
                      ))}
                    </TextField>

                    {!merchantPanel.selectedMerchantId ? (
                      <Alert severity="warning">
                        Selecione uma loja para criar ou remover interrupções.
                      </Alert>
                    ) : null}

                    {merchantPanel.error ? (
                      <Alert severity="error">{merchantPanel.error}</Alert>
                    ) : null}

                    <Card variant="outlined" className="interruption-quick-card">
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack spacing={0.75}>
                            <Typography variant="h6" className="interruption-title">
                              Fechar sua loja
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Feche temporariamente a loja e reabra automaticamente quando o tempo acabar, no mesmo padrão operacional do iFood.
                            </Typography>
                          </Stack>

                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                              Duração
                            </Typography>
                            <Box className="interruption-chip-group">
                              {interruptionDurationOptions.map((option) => (
                                <Button
                                  key={option.value}
                                  type="button"
                                  variant="outlined"
                                  className={`interruption-chip${merchantInterruptionForm.selectedDuration === option.value ? ' is-selected' : ''}`}
                                  onClick={() => handleSelectInterruptionDuration(option.value)}
                                >
                                  {option.label}
                                </Button>
                              ))}
                            </Box>
                          </Box>

                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                              Motivo
                            </Typography>
                            <Box className="interruption-chip-group">
                              {interruptionReasonOptions.map((reason) => (
                                <Button
                                  key={reason}
                                  type="button"
                                  variant="outlined"
                                  className={`interruption-chip${merchantInterruptionForm.selectedReason === reason ? ' is-selected' : ''}`}
                                  onClick={() => handleSelectInterruptionReason(reason)}
                                >
                                  {reason}
                                </Button>
                              ))}
                            </Box>
                          </Box>

                          {merchantInterruptionForm.selectedReason === 'Outro' ? (
                            <TextField
                              label="Digite o motivo"
                              value={merchantInterruptionForm.customReason}
                              onChange={handleMerchantInterruptionFormChange('customReason')}
                              fullWidth
                              multiline
                              minRows={2}
                              placeholder="Descreva o motivo da interrupção"
                            />
                          ) : null}

                          <Grid container spacing={1.5}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                label="Início"
                                type="datetime-local"
                                value={merchantInterruptionForm.start}
                                onChange={handleMerchantInterruptionFormChange('start')}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                label="Fim"
                                type="datetime-local"
                                value={merchantInterruptionForm.end}
                                onChange={handleMerchantInterruptionFormChange('end')}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>

                          <Typography variant="body2" color="text.secondary">
                            Você pode ajustar manualmente os horários quando precisar fugir dos presets.
                          </Typography>
                          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
                            <Button
                              variant="outlined"
                              color="inherit"
                              onClick={() => setMerchantInterruptionForm((prev) => ({
                                ...prev,
                                selectedDuration: 15,
                                selectedReason: 'Falta de entregador',
                                description: 'Falta de entregador',
                                customReason: '',
                                ...buildInterruptionWindow(15),
                              }))}
                              disabled={merchantInterruptionSaving}
                            >
                              Voltar
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={handleCreateInterruption}
                              disabled={merchantInterruptionSaving || !merchantPanel.selectedMerchantId}
                              startIcon={merchantInterruptionSaving ? <CircularProgress size={18} /> : <BoltIcon />}
                            >
                              {merchantInterruptionSaving ? 'Confirmando...' : 'Confirmar'}
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} mb={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Interrupções cadastradas</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lista de pausas ativas ou agendadas para o merchant selecionado.
                      </Typography>
                    </Box>
                    <Chip
                      label={`${merchantPanel.interruptions.length} interrupção${merchantPanel.interruptions.length === 1 ? '' : 'ões'}`}
                      color={merchantPanel.interruptions.length ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Stack>

                  <Stack spacing={1.5}>
                    {!merchantPanel.interruptions.length ? (
                      <Alert severity="success">
                        Nenhuma interrupção cadastrada no momento para esta loja.
                      </Alert>
                    ) : null}

                    {merchantPanel.interruptions.map((interruption) => (
                      <Card key={interruption.id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                  {interruption.description || 'Interrupção sem descrição'}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                {isInterruptionActive(interruption) ? (
                                  <Chip
                                    label="Loja pausada"
                                    color="warning"
                                    variant="filled"
                                  />
                                ) : (
                                  <Chip
                                    label="Agendada"
                                    color="default"
                                    variant="outlined"
                                  />
                                )}
                                <Button
                                  color="error"
                                  variant="outlined"
                                  startIcon={merchantInterruptionDeletingId === interruption.id ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
                                  onClick={() => handleDeleteInterruption(interruption.id)}
                                  disabled={merchantInterruptionDeletingId === interruption.id}
                                >
                                  {merchantInterruptionDeletingId === interruption.id
                                    ? 'Processando...'
                                    : 'Cancelar pausa'}
                                </Button>
                              </Stack>
                            </Stack>

                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  label="Início"
                                  value={formatInterruptionDisplayDate(interruption.start)}
                                  fullWidth
                                  InputProps={{ readOnly: true }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                  label="Fim"
                                  value={formatInterruptionDisplayDate(interruption.end)}
                                  fullWidth
                                  InputProps={{ readOnly: true }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            </Grid>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'integracoes' && integrationTab === 'site' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Integração Site/Cardápio Digital</Typography>
                      <Typography variant="h6">Recebimento de pedidos do site</Typography>
                    </Stack>
                    {siteWebhookStatus.loading ? (
                      <Chip label="Carregando" color="default" variant="outlined" />
                    ) : (
                      <Chip
                        label={!siteWebhook.siteEnabled ? 'Desativada' : siteWebhook.webhookUrl && (siteWebhook.tokenBearer || siteWebhook.webhookSecret) ? 'Pronta para receber' : 'Aguardando credenciais'}
                        color={!siteWebhook.siteEnabled ? 'default' : siteWebhook.webhookUrl && (siteWebhook.tokenBearer || siteWebhook.webhookSecret) ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={<Switch checked={Boolean(siteWebhook.siteEnabled)} onChange={handleSiteWebhookChange('siteEnabled')} />}
                      label="Integração ativa"
                    />
                    <TextField
                      label="URL do webhook do Brutos"
                      value={siteWebhook.webhookUrl || siteWebhook.brutosWebhookUrl}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(siteWebhook.webhookUrl || siteWebhook.brutosWebhookUrl, 'URL do webhook do Brutos'),
                      }}
                    />
                    <TextField
                      label="Token Bearer para o site"
                      value={siteWebhook.tokenBearer}
                      fullWidth
                      type={showBrutosZapToken ? 'text' : 'password'}
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(
                          siteWebhook.tokenBearer,
                          'Token Bearer do site',
                          <IconButton size="small" onClick={() => setShowSiteToken((prev) => !prev)} edge="end">
                            {showSiteToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <TextField
                      label="Webhook Secret para o site"
                      value={siteWebhook.webhookSecret}
                      fullWidth
                      type={showSiteSecret ? 'text' : 'password'}
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(
                          siteWebhook.webhookSecret,
                          'Webhook Secret do site',
                          <IconButton size="small" onClick={() => setShowSiteSecret((prev) => !prev)} edge="end">
                            {showSiteSecret ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <Alert severity="info">
                      Esta integração é exclusiva do cardápio digital Site-FFBC. Cole estes valores no painel do site em Integração com Sistema.
                      O site envia pedidos para esta URL e o Brutos autentica pelo header Authorization ou x-webhook-secret.
                    </Alert>
                    {siteWebhookStatus.error && (
                      <Alert severity="error">{siteWebhookStatus.error}</Alert>
                    )}
                    <Box>
                      <Button variant="contained" onClick={handleSaveSiteWebhook} disabled={siteWebhookSaving}>
                        {siteWebhookSaving ? 'Salvando...' : 'Salvar integração Site'}
                      </Button>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Checklist Site/Cardápio Digital</Typography>
                  </Stack>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">1. Abra no site a aba Integração com Sistema.</Typography>
                    <Typography variant="body2">2. Cole a URL do webhook do Brutos no campo URL do webhook do seu sistema.</Typography>
                    <Typography variant="body2">3. Cole o token no campo Token Bearer, sem escrever Bearer antes do valor.</Typography>
                    <Typography variant="body2">4. Cole o Webhook Secret no campo SITE_WEBHOOK_SECRET e deixe o envio automático ativo.</Typography>
                  </Stack>
                  {siteWebhookStatus.updatedAt && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Última atualização em {new Date(siteWebhookStatus.updatedAt).toLocaleString('pt-BR')}.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'integracoes' && integrationTab === 'brutos-zap' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Integração WhatsApp</Typography>
                      <Typography variant="h6">Brutos Zap</Typography>
                    </Stack>
                    {brutosZapStatus.loading ? (
                      <Chip label="Carregando" color="default" variant="outlined" />
                    ) : (
                      <Chip
                        label={!brutosZap.enabled ? 'Desativada' : brutosZap.hasToken && brutosZap.hasSecret ? 'Pronta para receber' : 'Aguardando credenciais'}
                        color={!brutosZap.enabled ? 'default' : brutosZap.hasToken && brutosZap.hasSecret ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={<Switch checked={Boolean(brutosZap.enabled)} onChange={handleBrutosZapChange('enabled')} />}
                      label="Integração ativa"
                    />
                    <TextField
                      label="URL do webhook do Brutos Delivery para o Brutos Zap"
                      value={brutosZap.webhookUrl}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(brutosZap.webhookUrl, 'URL do webhook Brutos Zap'),
                      }}
                    />
                    <TextField
                      label="URL de teste da integração"
                      value={brutosZap.pingUrl}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(brutosZap.pingUrl, 'URL de teste Brutos Zap'),
                      }}
                    />
                    <TextField
                      label="Token Bearer para o Brutos Zap"
                      value={brutosZap.tokenBearer || brutosZap.tokenPreview || (brutosZap.hasToken ? 'Token salvo mascarado' : '')}
                      fullWidth
                      type={showSiteToken ? 'text' : 'password'}
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(
                          brutosZap.tokenBearer,
                          'Token Bearer Brutos Zap',
                          <IconButton size="small" onClick={() => setShowBrutosZapToken((prev) => !prev)} edge="end">
                            {showBrutosZapToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <TextField
                      label="Webhook Secret para o Brutos Zap"
                      value={brutosZap.webhookSecret || brutosZap.secretPreview || (brutosZap.hasSecret ? 'Secret salvo mascarado' : '')}
                      fullWidth
                      type={showBrutosZapSecret ? 'text' : 'password'}
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(
                          brutosZap.webhookSecret,
                          'Webhook Secret Brutos Zap',
                          <IconButton size="small" onClick={() => setShowBrutosZapSecret((prev) => !prev)} edge="end">
                            {showBrutosZapSecret ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <TextField
                      label="URL de callback de status do Brutos Zap"
                      value={brutosZap.statusCallbackUrl}
                      onChange={handleBrutosZapChange('statusCallbackUrl')}
                      fullWidth
                      placeholder="Cole a URL exibida no Brutos Zap"
                    />
                    <Alert severity="info">
                      As credenciais salvas aparecem mascaradas. Ao regenerar, copie o Token Bearer e o Webhook Secret agora e cole no Brutos Zap.
                    </Alert>
                    {brutosZapStatus.error && (
                      <Alert severity="error">{brutosZapStatus.error}</Alert>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant="contained" onClick={handleSaveBrutosZap} disabled={brutosZapSaving}>
                        {brutosZapSaving ? 'Salvando...' : 'Salvar integração'}
                      </Button>
                      <Button variant="outlined" onClick={handleRegenerateBrutosZapSecrets} disabled={brutosZapSaving}>
                        Regenerar credenciais
                      </Button>
                      <Button variant="outlined" onClick={handleTestBrutosZapCallback} disabled={brutosZapTesting || !brutosZap.statusCallbackUrl}>
                        {brutosZapTesting ? 'Testando...' : 'Testar callback'}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Checklist Brutos Zap</Typography>
                  </Stack>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">1. Abra o Brutos Zap em Configurações &gt; Integrações &gt; Brutos Delivery.</Typography>
                    <Typography variant="body2">2. Cole a URL do webhook exibida aqui.</Typography>
                    <Typography variant="body2">3. Cole o Token Bearer e o Webhook Secret nos campos correspondentes.</Typography>
                    <Typography variant="body2">4. Copie a URL de callback do Brutos Zap para esta tela se o pedido não enviar callbackUrl.</Typography>
                    <Typography variant="body2">5. Ative o envio automático e faça um pedido de teste no WhatsApp.</Typography>
                  </Stack>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {brutosZap.lastOrderReceivedAt && (
                      <Alert severity="success">
                        Último pedido recebido em {new Date(brutosZap.lastOrderReceivedAt).toLocaleString('pt-BR')}.
                      </Alert>
                    )}
                    {brutosZap.lastStatusCallbackAt && (
                      <Alert severity="success">
                        Último callback enviado em {new Date(brutosZap.lastStatusCallbackAt).toLocaleString('pt-BR')}.
                      </Alert>
                    )}
                    {brutosZap.lastStatusCallbackError && (
                      <Alert severity="warning">
                        Último erro de callback: {brutosZap.lastStatusCallbackError}
                      </Alert>
                    )}
                  </Stack>
                  {(brutosZap.updatedAt || brutosZapStatus.updatedAt) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Última atualização em {new Date(brutosZap.updatedAt || brutosZapStatus.updatedAt).toLocaleString('pt-BR')}.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'integracoes' && integrationTab === 'entregas-zap' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Integração logística</Typography>
                      <Typography variant="h6">Brutos Entregas</Typography>
                    </Stack>
                    {entregasZapStatus.loading ? (
                      <Chip label="Carregando" color="default" variant="outlined" />
                    ) : (
                      <Chip
                        label={!entregasZapSettings.integrationEnabled ? 'Desativada' : entregasZapSettings.webhookUrl && entregasZapSettings.entregasZapToken && entregasZapSettings.brutosToken ? 'Conexão configurada' : 'Aguardando troca de chaves'}
                        color={!entregasZapSettings.integrationEnabled ? 'default' : entregasZapSettings.webhookUrl && entregasZapSettings.entregasZapToken && entregasZapSettings.brutosToken ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={<Switch checked={Boolean(entregasZapSettings.integrationEnabled)} onChange={handleEntregasZapChange('integrationEnabled')} />}
                      label="Integração ativa"
                    />
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.primary">
                        Cole no Brutos os dados do Brutos Entregas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Copie estes dois valores na aba Brutos Delivery do Brutos Entregas e salve aqui.
                      </Typography>
                    </Stack>
                    <TextField
                      label="URL de pedidos do Brutos Entregas"
                      value={entregasZapSettings.webhookUrl}
                      onChange={handleEntregasZapChange('webhookUrl')}
                      fullWidth
                      placeholder="Cole a URL exibida no Brutos Entregas"
                      helperText="O Brutos usa esta URL para enviar pedidos ao Brutos Entregas."
                      InputProps={{
                        endAdornment: buildCopyAdornment(entregasZapSettings.webhookUrl, 'URL do Brutos Entregas'),
                      }}
                    />
                    {hasLocalEntregasUrl && (
                      <Alert severity="warning">
                        Esta URL aponta para localhost. Em produção, o Brutos Delivery tentará chamar o próprio servidor da VPS, não o computador onde o Brutos Entregas está aberto.
                      </Alert>
                    )}
                    <TextField
                      label="Chave do Brutos Entregas"
                      value={entregasZapSettings.entregasZapToken}
                      onChange={handleEntregasZapChange('entregasZapToken')}
                      fullWidth
                      type={showEntregasZapToken ? 'text' : 'password'}
                      placeholder="Cole a chave exibida no Brutos Entregas"
                      helperText="O Brutos usa esta chave para autenticar o envio de pedidos."
                      InputProps={{
                        endAdornment: buildCopyAdornment(
                          entregasZapSettings.entregasZapToken,
                          'Chave Brutos Entregas',
                          <IconButton size="small" onClick={() => setShowEntregasZapToken((prev) => !prev)} edge="end">
                            {showEntregasZapToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <Divider />
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.primary">
                        Copie do Brutos para o Brutos Entregas
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Estes dois valores pertencem ao Brutos e devem ser colados no Brutos Entregas.
                      </Typography>
                    </Stack>
                    <TextField
                      label="URL da API do Brutos Delivery"
                      value={entregasZapSettings.brutosApiBaseUrl}
                      fullWidth
                      helperText="Copie esta URL para o campo URL da API do Brutos no Brutos Entregas."
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(entregasZapSettings.brutosApiBaseUrl, 'URL da API do Brutos'),
                      }}
                    />
                    <TextField
                      label="Chave do Brutos Delivery"
                      value={entregasZapSettings.brutosToken}
                      fullWidth
                      type={showBrutosToken ? 'text' : 'password'}
                      helperText="Copie esta chave para o campo Chave do Brutos Delivery no Brutos Entregas."
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(
                          entregasZapSettings.brutosToken,
                          'Chave Brutos Delivery',
                          <IconButton size="small" onClick={() => setShowBrutosToken((prev) => !prev)} edge="end">
                            {showBrutosToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    {entregasZapStatus.error && (
                      <Alert severity="error">{entregasZapStatus.error}</Alert>
                    )}
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={handleTestEntregasZapConnection}
                      disabled={entregasZapTesting || entregasZapSaving || entregasZapStatus.loading}
                    >
                      {entregasZapTesting ? 'Testando...' : 'Testar conexão'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRegenerateBrutosToken}
                      disabled={entregasZapTesting || entregasZapSaving || entregasZapStatus.loading}
                    >
                      Gerar nova chave do Brutos
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveEntregasZap}
                      disabled={entregasZapTesting || entregasZapSaving || entregasZapStatus.loading}
                      startIcon={entregasZapSaving ? <CircularProgress size={18} /> : <CloudDoneIcon />}
                    >
                      {entregasZapSaving ? 'Salvando...' : 'Salvar integração'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Checklist Brutos Entregas</Typography>
                  </Stack>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">1. Abra Configurações &gt; Brutos Delivery no Brutos Entregas.</Typography>
                    <Typography variant="body2">2. Cole aqui a URL de pedidos do Brutos Entregas e a chave do Brutos Entregas.</Typography>
                    <Typography variant="body2">3. Copie daqui a URL da API do Brutos Delivery e a chave do Brutos Delivery.</Typography>
                    <Typography variant="body2">4. Cole esses dois dados do Brutos nos campos equivalentes do Brutos Entregas.</Typography>
                  </Stack>
                  {entregasZapStatus.updatedAt && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Última atualização em {new Date(entregasZapStatus.updatedAt).toLocaleString('pt-BR')}.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'integracoes' && integrationTab === 'foody' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12, md: 7 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">Integração</Typography>
                      <Typography variant="h6">Foody Delivery</Typography>
                    </Stack>
                    {foodyStatus.loading ? (
                      <Chip label="Carregando" color="default" variant="outlined" />
                    ) : (
                      <Chip
                        label={!foodySettings.integrationEnabled ? 'Desativada' : foodySettings.apiToken ? 'Credenciais salvas' : 'Não configurado'}
                        color={!foodySettings.integrationEnabled ? 'default' : foodySettings.apiToken ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={<Switch checked={Boolean(foodySettings.integrationEnabled)} onChange={handleFoodyChange('integrationEnabled')} />}
                      label="Integração ativa"
                    />
                    <TextField
                      label="URL do webhook do Foody"
                      value={foodySettings.webhookUrl}
                      fullWidth
                      helperText="Esta URL já identifica o login/CNPJ atual."
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(foodySettings.webhookUrl, 'URL do webhook'),
                      }}
                    />
                    <TextField
                      label="Token de API da Foody (Authorization)"
                      value={foodySettings.apiToken}
                      onChange={handleFoodyChange('apiToken')}
                      fullWidth
                      type={showFoodyToken ? 'text' : 'password'}
                      placeholder="Cole o token da credencial API do Foody"
                      helperText="Use o token de Credenciais API do Foody para o Brutos enviar entregas."
                      InputProps={{
                        endAdornment: buildCopyAdornment(
                          foodySettings.apiToken,
                          'Token de API',
                          <IconButton size="small" onClick={() => setShowFoodyToken((prev) => !prev)} edge="end">
                            {showFoodyToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <TextField
                      label="Token do parceiro (SoftwareCompanyPartnerToken)"
                      value={foodySettings.partnerToken}
                      onChange={handleFoodyChange('partnerToken')}
                      fullWidth
                      placeholder="Cole se o Foody fornecer"
                      helperText="Preencha somente quando existir token de parceiro na conta Foody."
                    />
                    <TextField
                      label="Token do webhook do Brutos"
                      value={foodySettings.webhookToken}
                      fullWidth
                      type={showFoodyWebhookToken ? 'text' : 'password'}
                      placeholder="Token gerado pelo BD"
                      helperText="Copie este token para o campo Token de autorização do gatilho no Foody."
                      InputProps={{
                        readOnly: true,
                        endAdornment: buildCopyAdornment(
                          foodySettings.webhookToken,
                          'Token do webhook',
                          <IconButton size="small" onClick={() => setShowFoodyWebhookToken((prev) => !prev)} edge="end">
                            {showFoodyWebhookToken ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        ),
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      O token de API envia pedidos para o Foody. O token do webhook recebe eventos do Foody no CNPJ atual.
                    </Typography>
                    {!foodySettings.apiToken && !foodyStatus.loading && (
                      <Alert severity="warning">
                        Informe o Token de API da Foody para o Brutos enviar entregas automaticamente.
                      </Alert>
                    )}
                    {foodyStatus.lastAttempt?.ok === false && (
                      <Alert severity="warning">
                        Última tentativa de envio ao Foody falhou
                        {foodyStatus.lastAttempt.at ? ` em ${new Date(foodyStatus.lastAttempt.at).toLocaleString('pt-BR')}` : ''}.
                        {foodyStatus.lastAttempt.status ? ` Status ${foodyStatus.lastAttempt.status}.` : ''}
                        {foodyStatus.lastAttempt.message ? ` ${foodyStatus.lastAttempt.message}` : ''}
                      </Alert>
                    )}
                    {foodyStatus.error && (
                      <Alert severity="error">{foodyStatus.error}</Alert>
                    )}
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveFoody}
                      disabled={foodySaving || foodyStatus.loading}
                      startIcon={foodySaving ? <CircularProgress size={18} /> : <CloudDoneIcon />}
                    >
                      {foodySaving ? 'Salvando...' : 'Salvar integração'}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">Checklist Foody</Typography>
                  </Stack>
                  <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                    <Typography variant="body2">1. Copie a URL do webhook e cadastre no gatilho do Foody.</Typography>
                    <Typography variant="body2">2. Cole o Token do webhook do Brutos no campo Token de autorização do gatilho.</Typography>
                    <Typography variant="body2">3. Cole o Token de API da Foody para o Brutos enviar entregas.</Typography>
                  </Stack>
                  {foodyStatus.updatedAt && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Última atualização em {new Date(foodyStatus.updatedAt).toLocaleString('pt-BR')}.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {tab === 'usuarios' && (
          <Grid container spacing={3} className="configuracoes-section">
            <Grid size={{ xs: 12 }}>
              <Card className="configuracoes-card">
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={2}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Segurança de acesso
                      </Typography>
                      <Typography variant="h6">
                        {canAdministerUsers ? 'Logins, senhas e permissões' : 'Meu login e senha'}
                      </Typography>
                    </Stack>
                    {canAdministerUsers ? (
                      <Button startIcon={<AddCircleOutlineIcon />} variant="contained" onClick={() => openUserDialog('create')}>
                        Novo usuário
                      </Button>
                    ) : null}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {usersError ? <Alert severity="error">{usersError}</Alert> : null}
                    {!usersError && canAdministerUsers && userSecuritySummary?.hasDefaultAdminPassword ? (
                      <Alert severity={userSecuritySummary?.safeToDisableDefaultAdmin ? 'warning' : 'error'}>
                        O login padrão `Admin` ainda aceita a senha `1234`.
                        {userSecuritySummary?.safeToDisableDefaultAdmin
                          ? ' Já existe outro usuário administrativo; a recomendação é trocar a senha ou desativar esse login em janela controlada.'
                          : ' Antes de desativar esse login, garanta outro usuário com acesso administrativo funcional.'}
                      </Alert>
                    ) : null}
                    {!usersError && canAdministerUsers && !userSecuritySummary?.hasDefaultAdminPassword && userSecuritySummary?.hasDefaultAdminLogin ? (
                      <Alert severity="info">
                        O login `Admin` ainda existe, mas não foi detectada a senha padrão `1234`. Mesmo assim, trate esse usuário como conta sensível e mantenha controle estrito sobre ele.
                      </Alert>
                    ) : null}
                    {usersLoading ? (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <CircularProgress size={20} />
                        <Typography variant="body2">Carregando usuários...</Typography>
                      </Stack>
                    ) : null}
                    {!usersLoading && !users.length ? (
                      <Alert severity="info">Nenhum usuário encontrado.</Alert>
                    ) : null}
                    {users.map((user) => {
                      const isSelf = Number(user.id) === Number(currentUser?.id);
                      const canEdit = canAdministerUsers || isSelf;
                      const canDelete = canAdministerUsers;
                      return (
                        <Card key={user.id} variant="outlined" sx={{ borderRadius: 3 }}>
                          <CardContent>
                            <Stack spacing={2}>
                              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <Avatar sx={{ bgcolor: user.role === USER_ROLES.ADMINISTRADOR ? '#111827' : '#f59e0b' }}>
                                    {String(user.login || '?').slice(0, 1).toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                      {user.login}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Chip
                                        size="small"
                                        color={user.role === USER_ROLES.ADMINISTRADOR ? 'primary' : 'default'}
                                        label={getUserRoleLabel(user.role)}
                                      />
                                      {isSelf ? <Chip size="small" variant="outlined" label="Você" /> : null}
                                    </Stack>
                                  </Box>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  {canEdit ? (
                                    <Button variant="outlined" onClick={() => openUserDialog('edit', user)}>
                                      Editar
                                    </Button>
                                  ) : null}
                                  {canDelete ? (
                                    <Button
                                      color="error"
                                      variant="outlined"
                                      disabled={deleteUserId === user.id || Number(user.id) === Number(currentUser?.id)}
                                      onClick={() => handleDeleteUser(user)}
                                    >
                                      {deleteUserId === user.id ? 'Excluindo...' : 'Excluir'}
                                    </Button>
                                  ) : null}
                                </Stack>
                              </Stack>
                              <TextField
                                label="Senha"
                                value="Não exibida por segurança"
                                fullWidth
                                helperText="Para alterar a senha, use a opção Editar e informe uma nova."
                                InputProps={{
                                  readOnly: true,
                                }}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                    <Alert severity={canAdministerUsers ? 'info' : 'warning'}>
                      {canAdministerUsers
                        ? 'Administradores podem criar, editar e excluir usuários administrador, gerente, colaborador, garçom e caixa. O perfil Programador permanece restrito ao backend.'
                        : 'Como colaborador, você só pode alterar o seu próprio login e a sua própria senha.'}
                    </Alert>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      <Dialog open={userDialog.open} onClose={closeUserDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {userDialog.mode === 'edit' ? 'Editar usuário' : 'Novo usuário'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Login"
              value={userForm.login}
              onChange={handleUserFormChange('login')}
              fullWidth
            />
            <TextField
              label="Senha"
              value={userForm.password}
              onChange={handleUserFormChange('password')}
              type="password"
              fullWidth
              helperText={userDialog.mode === 'edit' ? 'Deixe em branco para manter a senha atual.' : 'Informe a senha inicial do usuário.'}
            />
            <FormControl fullWidth disabled={!canAdministerUsers || users.find((item) => item.id === userDialog.userId)?.role === USER_ROLES.PROGRAMADOR}>
              <InputLabel id="user-role-label">Perfil</InputLabel>
              <Select
                labelId="user-role-label"
                label="Perfil"
                value={userForm.role}
                onChange={handleUserFormChange('role')}
              >
                {USER_TAB_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeUserDialog}>Cancelar</Button>
          <Button onClick={handleSaveUser} variant="contained" disabled={userSaving}>
            {userSaving ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteAllProductsDialog.open}
        onClose={closeDeleteAllProductsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Deletar todos os produtos?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Alert severity="error">
              Esta ação removerá todos os produtos ativos do catálogo operacional desta empresa,
              incluindo itens manuais e importados de todas as lojas iFood.
            </Alert>
            {deleteAllProductsDialog.error ? (
              <Alert severity="warning">{deleteAllProductsDialog.error}</Alert>
            ) : null}
            <Box>
              <Typography variant="subtitle2">Produtos que serão removidos</Typography>
              <Typography variant="body2" color="text.secondary">
                Total: {deleteAllProductsDialog.preview?.activeProductCount ?? 0}
                {' · '}Manuais: {deleteAllProductsDialog.preview?.manualProductCount ?? 0}
                {' · '}iFood: {deleteAllProductsDialog.preview?.ifoodProductCount ?? 0}
                {(deleteAllProductsDialog.preview?.otherProductCount ?? 0) > 0
                  ? ` · Outras origens: ${deleteAllProductsDialog.preview.otherProductCount}`
                  : ''}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Produtos, códigos PDV, fichas técnicas, mídias e históricos serão preservados de forma
              inativa para segurança. Nada será excluído no iFood. Uma importação completa poderá
              reativar produtos iFood; a opção “Atualizar e importar só novos” não os reativa.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pedidos já realizados permanecem preservados. Carrinhos ainda não confirmados precisarão
              ser refeitos após a remoção.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label={`Digite ${DELETE_ALL_PRODUCTS_CONFIRMATION} para confirmar`}
              value={deleteAllProductsDialog.confirmation}
              onChange={(event) => setDeleteAllProductsDialog((current) => ({
                ...current,
                confirmation: event.target.value,
                error: '',
              }))}
              disabled={deleteAllProductsDialog.applying}
              error={Boolean(deleteAllProductsDialog.confirmation)
                && !isDeleteAllProductsConfirmationValid(deleteAllProductsDialog.confirmation)}
              inputProps={{ autoComplete: 'off' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteAllProductsDialog} disabled={deleteAllProductsDialog.applying}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteSweepOutlinedIcon />}
            onClick={confirmDeleteAllProducts}
            disabled={deleteAllProductsDialog.applying
              || !deleteAllProductsDialog.preview?.canDelete
              || !isDeleteAllProductsConfirmationValid(deleteAllProductsDialog.confirmation)}
          >
            {deleteAllProductsDialog.applying ? 'Deletando produtos...' : 'Deletar todos os produtos'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3200} onClose={handleCloseToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog open={printPointDialog.open} onClose={closePrintPointDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Criar ponto de impressão</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Dê um nome ao ponto e escolha a impressora que receberá as comandas.
          </Typography>
          {printPointDialog.error && <Alert severity="warning" sx={{ mb: 2 }}>{printPointDialog.error}</Alert>}
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              autoFocus
              required
              label="Nome do ponto"
              value={printPointDialog.name}
              onChange={(event) => setPrintPointDialog((prev) => ({ ...prev, name: event.target.value, error: '' }))}
              placeholder="Ex.: Cozinha"
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel>Impressora</InputLabel>
              <Select
                label="Impressora"
                value={printPointDialog.printerId}
                onChange={(event) => setPrintPointDialog((prev) => ({ ...prev, printerId: event.target.value, error: '' }))}
              >
                {detectedPrinters.map((printer) => <MenuItem key={printer.id} value={printer.id}>{printer.name || printer.id}</MenuItem>)}
                {detectedPrinters.length === 0 && <MenuItem value="" disabled><em>Nenhuma impressora encontrada no CIBD.</em></MenuItem>}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={printPointDialog.enabled} onChange={(event) => setPrintPointDialog((prev) => ({ ...prev, enabled: event.target.checked }))} />}
              label={printPointDialog.enabled ? 'Ponto ativado' : 'Ponto desativado'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePrintPointDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreatePrintPoint}>Criar ponto</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={comandaPreviewOpen} onClose={() => setComandaPreviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Preview da comanda</DialogTitle>
        <DialogContent>
          <Box sx={{ border: '1px dashed #d4a71c', borderRadius: 2, p: 2, background: '#fff3b0' }}>
            <Box sx={comandaPreviewStyles}>
              {printerSettings.comandaShowOrderNumber && printerSettings.comandaOrderNumberBanner && (
                <Box
                  sx={{
                    background: '#000',
                    color: '#fff',
                    px: 1,
                    py: 1,
                    minHeight: printerSettings.comandaSizeOrderNumberBanner * 1.4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: printerSettings.comandaSizeOrderNumberBanner,
                    letterSpacing: 0.5,
                  }}
                >
                  {sampleComanda.orderNumber}
                </Box>
              )}
                <Typography
                  sx={{
                    fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldTitle ? 700 : 500),
                    fontSize: printerSettings.comandaSizeTitle + (printerSettings.comandaStrongPrint ? 1 : 0),
                    mt: printerSettings.comandaShowOrderNumber && printerSettings.comandaOrderNumberBanner ? 1 : 0,
                  }}
                >
                  {printerSettings.comandaTitle || 'COMANDA'}
                </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
                {printerSettings.comandaSubtitle || 'Pedido'}
                {printerSettings.comandaShowCreatedAt ? ` - ${sampleComanda.createdAt}` : ''}
              </Typography>
              <Divider sx={{ my: 1 }} />

              {printerSettings.comandaShowOrderNumber && !printerSettings.comandaOrderNumberBanner && (
                <Typography
                  sx={{
                    fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldOrderNumber ? 700 : 400),
                    fontSize: printerSettings.comandaSizeOrderNumber + (printerSettings.comandaStrongPrint ? 1 : 0),
                  }}
                >
                  Pedido {sampleComanda.orderNumber}
                </Typography>
              )}

              {printerSettings.comandaShowSalesChannel && (
                <Typography>Canal: {sampleComanda.salesChannel}</Typography>
              )}
              {printerSettings.comandaShowMerchant && (
                <Typography>Restaurante: {sampleComanda.merchant}</Typography>
              )}
              {printerSettings.comandaShowDeliveryMode && (
                <Typography>Tipo: {sampleComanda.deliveryMode}</Typography>
              )}
              {printerSettings.comandaShowCustomer && (
                <Typography sx={{ fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldCustomer ? 700 : 400) }}>
                  Cliente: {sampleComanda.customer}
                </Typography>
              )}
              {printerSettings.comandaShowCustomerDocument && (
                <Typography>CPF: {sampleComanda.customerDocument}</Typography>
              )}
              {printerSettings.comandaShowCustomerPhone && (
                <Typography sx={{ fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldPhone ? 700 : 400) }}>
                  Telefone: {sampleComanda.customerPhone}
                </Typography>
              )}
              {printerSettings.comandaShowContact && (
                <Typography>Localizador: {sampleComanda.localizer}</Typography>
              )}
              {printerSettings.comandaShowAddress && (
                <Typography sx={{ fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldAddress ? 700 : 400) }}>
                  Endereço: {sampleComanda.address}
                </Typography>
              )}
              {printerSettings.comandaShowPickupCode && (
                <Typography>Código de retirada: {sampleComanda.pickupCode}</Typography>
              )}

              {printerSettings.comandaShowItems && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>Itens</Typography>
                  {sampleComanda.items.map((item) => (
                    <Box key={item.name} sx={{ mb: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: printerSettings.comandaSizeItem + (printerSettings.comandaStrongPrint ? 1 : 0) }}>
                        <span>{item.qty}x {item.name}</span>
                        <span>{item.price}</span>
                      </Box>
                      {item.options?.map((opt) => (
                        <Box
                          key={opt.name}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            fontSize: 11,
                            fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldOptions ? 700 : 400),
                            pl: 1.5,
                          }}
                        >
                          <span>+ {opt.qty}x {opt.name}</span>
                          <span>{opt.price}</span>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              )}

              {printerSettings.comandaShowNotes && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>Observacoes</Typography>
                  <Typography sx={{ fontSize: 12 }}>{sampleComanda.notes}</Typography>
                </Box>
              )}

              {printerSettings.comandaShowTotals && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Subtotal</span>
                    <span>{sampleComanda.subtotal}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Entrega</span>
                    <span>{sampleComanda.deliveryFee}</span>
                  </Box>
                  {printerSettings.comandaShowDiscounts && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Descontos</span>
                      <span>{sampleComanda.discounts}</span>
                    </Box>
                  )}
                  {printerSettings.comandaShowAdditionalFees && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Taxas adicionais</span>
                      <span>{sampleComanda.additionalFees}</span>
                    </Box>
                  )}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: printerSettings.comandaStrongPrint ? 700 : (printerSettings.comandaBoldTotal ? 700 : 500),
                    fontSize: printerSettings.comandaSizeTotal + (printerSettings.comandaStrongPrint ? 1 : 0),
                  }}>
                    <span>Total</span>
                    <span>{sampleComanda.total}</span>
                  </Box>
                </Box>
              )}

              {printerSettings.comandaShowPayment && (
                <Box sx={{ mt: 1 }}>
                  <Typography>Pagamento: {sampleComanda.payment}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Pago:</span>
                    <span>{sampleComanda.paymentPrepaid}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Pendente:</span>
                    <span>{sampleComanda.paymentPending}</span>
                  </Box>
                </Box>
              )}
              {printerSettings.comandaShowPaymentDetails && (
                <Typography>Detalhes: {sampleComanda.paymentDetails}</Typography>
              )}
              {printerSettings.comandaShowDeliveryTime && (
                <Typography>Prazo: {sampleComanda.deliveryTime}</Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setComandaPreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Configuracoes = () => {
  const auth = useAuth();
  if (!auth.token || !auth.user?.companyId) return null;
  return <TenantConfiguracoes key={`${auth.user.companyId}:${auth.token}`} auth={auth} />;
};

export default Configuracoes;
